import React, { useState, useEffect, useRef } from "react";
import { ethers } from "ethers";
import init, {
  generate_transfer_proof,
  generate_withdraw_proof,
  decrypt_ciphertext,
} from "../crypto/confidentialCrypto.js";
import { useDeterministicElGamalKeypair } from "../crypto/useDeterministicElGamalKeypair.ts";
import History from "./History";
import { RequestService } from "../services/requestService";
import { safeLog, createSecureKeypair } from "../utils/secureMemory.js";
import { getCleanErrorMessage, isFeeTooLowError, isUserRejectedError } from "../utils/errorParser.js";
import TokenSelector from "./TokenSelector";
import TokenSelectorPill from "./TokenSelectorPill";
import KeyDerivationConsentModal from "./KeyDerivationConsentModal";
import EditorialForm, { StatusPanel } from "./EditorialForm";
import MobileShell, { Sheet, SheetRow, MobileClaimBanner, MobilePending, MobileComplete, ConfirmSheet } from "./MobileShell";
import TransferRequestModal from "./TransferRequestModal";
import RecipientInviteNote from "./RecipientInviteNote";
import Onboarding from "./Onboarding";
import { CHAINS, getPopularChains, isChainEnabledForNetwork, isTokenEnabledForNetwork, resolveChainEntry, getDefaultChainIdForNetwork, getNetworkModeForChain, getChainDisplayName, tokenIdFromCompositeId, toConfidentialSymbol, toConfidentialName } from "../config/chains";
import { getSavedChainId, getSavedTokenId, setSavedChainId, setSavedTokenId } from "../utils/chainPreference";
import { isOnboardingSkipped, setOnboardingSkipped } from "../utils/onboardingPreference";
import {
  getReadyConfidentialSession,
  applyPendingWithClient,
  SDK_UX_OPTIONS,
} from "../services/stabletrustService";
import {
  PENDING_KIND_CREATE,
  PENDING_KIND_DEPOSIT,
  PENDING_KIND_TRANSFER,
  PENDING_KIND_WITHDRAW,
  PENDING_KIND_APPLY,
  getPendingActionTypeName,
  getPendingProgressMessage,
  isPendingKind,
  parseAccountCore,
  parsePendingKind,
  shouldUseOffchainZKP,
  CONTRACT_ABI,
} from "../config/contractAbi";
import { secureKeyCache } from "../services/secureKeyCache.js";
import { encryptRandomness } from "../utils/ibeEncryption.js";
import {
  checkTempoFeeTokenBalance,
  getTempoFeeTokenSymbols,
} from "../utils/tempoFeeTokens";
import { uploadBytesToIpfs } from "../utils/ipfs";
import { withRateLimitRetry } from "../utils/rateLimitRetry";
import { getExplorerTxUrl } from "../utils/explorerUtils";
import {
  executeContractTxWithGasFallback,
  estimateTxFees,
} from "../utils/gasEstimation";

/** Resolve a transaction hash from an ethers v5/v6 receipt. */
function receiptTxHash(receipt) {
  return receipt?.hash || receipt?.transactionHash || null;
}

const accountCreationSessions = new Set();

const makeAccountCreationKey = (addr, chainId, verifyingContract) => {
  const a = addr ? addr.toLowerCase() : "null";
  const c = chainId ?? "null";
  const vc = verifyingContract ? verifyingContract.toLowerCase() : "null";
  return `${a}::${c}::${vc}`;
};

// Helper function to detect SSL/certificate errors
const isSSLError = (error) => {
  if (!error) return false;
  const errorMessage = String(error.message || error).toLowerCase();
  const errorCode = String(error.code || "").toLowerCase();
  return (
    errorMessage.includes("ssl") ||
    errorMessage.includes("certificate") ||
    errorMessage.includes("err_ssl") ||
    errorCode.includes("ssl") ||
    errorMessage.includes("unrecognized_name_alert") ||
    errorMessage.includes("cert_") ||
    errorMessage.includes("net::err_ssl")
  );
};

// Helper function to get user-friendly error message for network errors
const getNetworkErrorMessage = (error, chainName = "this network") => {
  if (isSSLError(error)) {
    return `Cannot connect securely to ${chainName}. Try again later.`;
  }
  if (
    error?.message?.includes("network") ||
    error?.message?.includes("connection") ||
    error?.message?.includes("timeout")
  ) {
    return `Cannot reach ${chainName}. Check your connection and try again.`;
  }
  if (
    error?.code === "CALL_EXCEPTION" ||
    error?.message?.includes("CALL_EXCEPTION")
  ) {
    return `Could not reach ${chainName}. Try again later.`;
  }
  return `Could not connect to ${chainName}. Try again later.`;
};

// ethers CALL_EXCEPTION with empty revert data usually means either
// "account/contract doesn't exist on this chain" or "provider is on the
// wrong chain for this address". Callers that expect the former should
// treat this as a soft miss, not a hard failure.
const isCallException = (error) =>
  error?.code === "CALL_EXCEPTION" ||
  String(error?.message || "").includes("CALL_EXCEPTION");

const faucetLinkStyle = {
  color: "var(--st-link, #3E8FCC)",
  textDecoration: "none",
};

/** Network-aware insufficient-funds copy. Mainnet never says "testnet" or links to faucets. */
function insufficientFundsMessage({
  isMainnet,
  kind,
  nativeSymbol = "ETH",
  tokenSymbol = "USDC",
  feeTokenSymbols = "",
  chainName = "this network",
}) {
  const label = (s) => (isMainnet ? s : `testnet ${s}`);
  const fundCta = (text) =>
    isMainnet ? (
      <>fund your wallet on {chainName}</>
    ) : (
      <a
        href="/faucets.html"
        target="_blank"
        rel="noopener noreferrer"
        style={faucetLinkStyle}
      >
        {text}
      </a>
    );

  if (kind === "both-tempo") {
    return (
      <>
        You'll need a {isMainnet ? "" : "testnet "}fee token (
        {feeTokenSymbols})
        {tokenSymbol ? (
          <>
            {" "}
            and {label(tokenSymbol)}
          </>
        ) : null}{" "}
        to get started.{" "}
        {fundCta(
          `Get ${isMainnet ? "" : "testnet "}fee tokens${tokenSymbol ? ` and ${label(tokenSymbol)}` : ""}`,
        )}
        , or switch network.
      </>
    );
  }
  if (kind === "gas-tempo") {
    return (
      <>
        Transaction fees on this network use {feeTokenSymbols}.{" "}
        {fundCta(`Get ${isMainnet ? "" : "testnet "}fee tokens`)}, or switch
        network.
      </>
    );
  }
  if (kind === "arc") {
    return (
      <>
        You'll need {label(tokenSymbol)} for fees and to use this app.{" "}
        {fundCta(`Get ${label(tokenSymbol)}`)}, or switch network.
      </>
    );
  }
  if (kind === "both") {
    return (
      <>
        You'll need {label(nativeSymbol)} for fees and {label(tokenSymbol)} to
        use this app.{" "}
        {fundCta(`Get ${label(nativeSymbol)} and ${label(tokenSymbol)}`)}, or
        switch network.
      </>
    );
  }
  if (kind === "gas") {
    return (
      <>
        You'll need a little {label(nativeSymbol)} for transaction fees.{" "}
        {fundCta(`Get ${label(nativeSymbol)}`)}, or switch network.
      </>
    );
  }
  if (kind === "token-warn") {
    return (
      <>
        You can create an account with your current gas balance. Add{" "}
        {label(tokenSymbol)} when you're ready to deposit or transfer
        {isMainnet ? (
          <>.</>
        ) : (
          <>
            {" "}
            — {fundCta(`get ${label(tokenSymbol)}`)}.
          </>
        )}
      </>
    );
  }
  // token (hard block)
  return (
    <>
      You'll need {label(tokenSymbol)} to use this app.{" "}
      {fundCta(`Get ${isMainnet ? label(tokenSymbol) : "testnet tokens"}`)}, or
      switch network.
    </>
  );
}

/**
 * Resolve protocol fee display for "View Breakdown": different networks use different
 * fee currencies (e.g. wei on Ethereum, USDC on Arc, PathUSD on Tempo/Ontempo).
 * @param {object} chainConfig - CHAINS[chainId]
 * @param {string} feeTokenAddr - contract.feeToken() (zero address = native)
 * @param {string} feeAmountRaw - contract.nonAnonymousTransferFee().toString()
 * @returns {string|null} Display string e.g. "1 wei", "0.000001 USDC", "1 PathUSD", or null
 */
function getProtocolFeeDisplayText(chainConfig, feeTokenAddr, feeAmountRaw) {
  if (!chainConfig || !feeAmountRaw) return null;
  const addr = (feeTokenAddr || "").toLowerCase();
  const zero = "0x0000000000000000000000000000000000000000";
  if (addr === zero) {
    const native = chainConfig.nativeCurrency || {};
    // Arc (and similar) use USDC as native but the contract stores fee in 18-decimal units; use 18 for display.
    const isArcStyleNativeToken =
      chainConfig.id === "arc" || chainConfig.id === "stable";
    const decimals = isArcStyleNativeToken
      ? 18
      : native.decimals != null
        ? native.decimals
        : 18;
    const symbol = native.symbol || "ETH";
    if (decimals === 18 && !isArcStyleNativeToken) {
      return `${feeAmountRaw} wei`;
    }
    const divisor = Math.pow(10, decimals);
    const amount = Number(feeAmountRaw) / divisor;
    const formatted =
      amount < 1 && amount > 0
        ? amount.toFixed(Math.min(decimals, 6))
        : amount.toFixed(amount >= 1 ? 2 : 6);
    return `${formatted.replace(/\.?0+$/, "") || "0"} ${symbol}`;
  }
  let decimals = 18;
  let symbol = "units";
  const tokens = chainConfig.tokens ? Object.values(chainConfig.tokens) : [];
  const feeTokens = chainConfig.feeTokens || [];
  for (const t of [...tokens, ...feeTokens]) {
    if ((t.address || "").toLowerCase() === addr) {
      decimals = t.decimals != null ? t.decimals : 18;
      symbol = t.symbol || symbol;
      break;
    }
  }
  // Tempo (PathUSD) and similar: contract stores fee in 18-decimal units; use 18 for display.
  const isTempoStyleFeeToken = chainConfig.id === "tempo";
  if (isTempoStyleFeeToken) decimals = 18;
  if (decimals === 0) return `${feeAmountRaw} ${symbol}`;
  const divisor = Math.pow(10, decimals);
  const amount = Number(feeAmountRaw) / divisor;
  const formatted =
    amount < 1 && amount > 0
      ? amount.toFixed(Math.min(decimals, 6))
      : amount.toFixed(amount >= 1 ? 2 : 6);
  return `${formatted.replace(/\.?0+$/, "") || "0"} ${symbol}`;
}

const TabbedOperations = ({
  contract,
  userAccount,
  wasmModule,
  provider: providerProp,
  showNotification,
  onOperationComplete,
  onKeypairUpdate,
  accountData,
  hasActiveAccount,
  accountExists,
  onConnect,
  onDisconnect,
  privyIdentityLabel,
  onSwitchAccount,
  switchingAccount,
  confidentialBalance,
  setConfidentialBalance,
  activeTab,
  setActiveTab,
  onTokenDataChange,
  onPendingBalanceChange,
  onSecureClaimRegister,
  onSecureClaimInFlightChange,
  onSelectTokenRegister, // App.js BalancesModal "Deposit" → switch token then open deposit
  onRequestModalRegister, // App.js topbar "Request a Payment" → open the builder modal
  switchToChain,
  incomingRequest, // { to, chainKey, tokenId, amount } from a shared "request a transfer" link
  onIncomingConsumed, // called once an incoming request has been applied to the form
  activeWalletOrigin,
  // Balances-and-gas (see handoff/BALANCES.md) — from App.js's useBalances(),
  // not refetched here, so the header chip/modal and this gate share one poll.
  gasState, // 'ok' | 'low' | 'empty' | null
  gasRuns,
  nativeSymbol,
  nativeBalance, // number|null — so "No ETH" vs "Not enough ETH" copy is accurate
  // Per-token wallet ERC-20 balances for the selected chain (keyed by token id).
  // Deposit must use this map — a single shared `balance` string raced when
  // switching USDC↔USDT and could show the other token's amount (or 0).
  walletBalances,
  refreshWalletBalances,
}) => {
  // Use WASM-based deterministic key generation
  const provider = providerProp || contract?.signer?.provider || null;

  // --- Mobile viewport check (renders MobileShell instead of the desktop
  // tree below this width) --- matchMedia + a change listener, not a
  // user-agent sniff, so this tracks actual viewport width live (resizing
  // a desktop browser window, rotating a device, etc.) rather than a
  // one-time guess about the device class.
  const [isMobileViewport, setIsMobileViewport] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 767px)").matches,
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(max-width: 767px)");
    const handleChange = (e) => setIsMobileViewport(e.matches);
    mql.addEventListener("change", handleChange);
    return () => mql.removeEventListener("change", handleChange);
  }, []);

  // MobileShell's header address button now opens an account menu (copy
  // address, disconnect) instead of disconnecting immediately on tap —
  // same truncation format App.js already uses for its own header
  // (formatAddress there).
  const truncatedAddress = userAccount
    ? `${userAccount.slice(0, 6)}...${userAccount.slice(-4)}`
    : "";

  // Mobile replaces the immediate handleTransfer call with a confirm sheet
  // (step 5) — this just tracks whether it's open; deposit/withdraw keep
  // submitting directly, same as desktop.
  const [showTransferConfirm, setShowTransferConfirm] = useState(false);

  useEffect(() => {
    if (activeTab !== "transfer" || !isMobileViewport || !userAccount) {
      setShowTransferConfirm(false);
    }
  }, [activeTab, isMobileViewport, userAccount]);

  // --- Gas breakdown state (for "View Breakdown" UI) ---
  const [showGasBreakdown, setShowGasBreakdown] = useState(false);
  const [gasBreakdownOperation, setGasBreakdownOperation] = useState(null); // 'transfer' | 'deposit' | 'withdraw'
  const [gasBreakdown, setGasBreakdown] = useState(null);
  const [gasBreakdownError, setGasBreakdownError] = useState(null);
  const [isGasBreakdownLoading, setIsGasBreakdownLoading] = useState(false);
  const transferProofPreviewCacheRef = useRef(new Map());
  const withdrawProofPreviewCacheRef = useRef(new Map());

  const buildTransferProofDataForPreview = async (
    tokenAddress,
    recipientAddress,
    amountForContractBn,
  ) => {
    try {
      if (
        !contract ||
        !userAccount ||
        !keypair?.private_key ||
        confidentialBalance === null
      )
        return null;
      if (
        !tokenAddress ||
        !recipientAddress ||
        !amountForContractBn ||
        amountForContractBn.lte(0)
      )
        return null;

      const cacheKey = [
        selectedChainId || "",
        String(tokenAddress).toLowerCase(),
        String(recipientAddress).toLowerCase(),
        amountForContractBn.toString(),
        String(confidentialBalance),
        String(userAccount).toLowerCase(),
      ].join("::");
      const cached = transferProofPreviewCacheRef.current.get(cacheKey);
      if (cached) return cached;

      const currentBalanceCiphertext =
        await fetchCurrentBalanceCiphertext(tokenAddress);
      const recipientPubkey = await getRecipientPublicKey(recipientAddress);
      const encodedRecipientPubkey = encodeRecipientPubkey(recipientPubkey);

      const secureKeypair = createSecureKeypair(keypair);
      let privateKey;
      try {
        privateKey = secureKeypair.getPrivateKey();
        if (!privateKey) return null;

        const transferInput = {
          current_balance_ciphertext: currentBalanceCiphertext,
          current_balance: Math.floor(confidentialBalance),
          transfer_amount: parseInt(amountForContractBn.toString(), 10),
          source_keypair: privateKey,
          destination_pubkey: encodedRecipientPubkey,
        };

        const proofResult = generate_transfer_proof(
          JSON.stringify(transferInput),
        );
        const proof = JSON.parse(proofResult);
        if (
          !proof?.success ||
          !proof?.data?.equality_proof ||
          !proof?.data?.ciphertext_validity_proof ||
          !proof?.data?.range_proof
        ) {
          return null;
        }

        const encodedProofData = ethers.utils.defaultAbiCoder.encode(
          ["string", "string", "string"],
          [
            proof.data.equality_proof,
            proof.data.ciphertext_validity_proof,
            proof.data.range_proof,
          ],
        );
        transferProofPreviewCacheRef.current.set(cacheKey, encodedProofData);
        return encodedProofData;
      } finally {
        secureKeypair.clear();
      }
    } catch (error) {
      console.warn(
        "Failed to build transfer proof for preview estimation:",
        error,
      );
      return null;
    }
  };

  const buildWithdrawProofDataForPreview = async (
    tokenAddress,
    amountForContractBn,
  ) => {
    try {
      if (
        !contract ||
        !userAccount ||
        !keypair?.private_key ||
        confidentialBalance === null
      )
        return null;
      if (!tokenAddress || !amountForContractBn || amountForContractBn.lte(0))
        return null;

      const userTxIdNonce = await getUserTxIdNonce();

      const currentBalanceCiphertext =
        await fetchCurrentBalanceCiphertext(tokenAddress);
      const secureKeypair = createSecureKeypair(keypair);
      let privateKey;
      try {
        privateKey = secureKeypair.getPrivateKey();
        if (!privateKey) return null;

        const currentBalanceFromCiphertext =
          await decryptBalanceFromCiphertext(
            currentBalanceCiphertext,
            privateKey,
          );

        const cacheKey = [
          selectedChainId || "",
          String(tokenAddress).toLowerCase(),
          amountForContractBn.toString(),
          String(currentBalanceFromCiphertext),
          String(userAccount).toLowerCase(),
          userTxIdNonce,
        ].join("::");
        const cached = withdrawProofPreviewCacheRef.current.get(cacheKey);
        if (cached) return cached;

        const withdrawAmountForProof = parseInt(
          amountForContractBn.toString(),
          10,
        );

        const withdrawInput = {
          current_balance_ciphertext: currentBalanceCiphertext,
          current_balance: currentBalanceFromCiphertext,
          withdraw_amount: withdrawAmountForProof,
          keypair: privateKey,
          nonce: userTxIdNonce,
        };
        const proofResult = generate_withdraw_proof(
          JSON.stringify(withdrawInput),
        );
        const proof = JSON.parse(proofResult);

        if (
          !proof?.success ||
          !proof?.data?.equality_proof ||
          !proof?.data?.range_proof
        ) {
          return null;
        }

        const encodedProofData = ethers.utils.defaultAbiCoder.encode(
          ["string", "string"],
          [proof.data.equality_proof, proof.data.range_proof],
        );
        withdrawProofPreviewCacheRef.current.set(cacheKey, encodedProofData);
        return encodedProofData;
      } finally {
        secureKeypair.clear();
      }
    } catch (error) {
      console.warn(
        "Failed to build withdraw proof for preview estimation:",
        error,
      );
      return null;
    }
  };

  const fetchGasBreakdown = async (operation, opts = {}) => {
    // Always record which operation the user is trying to view,
    // so the panel can render even when we're only showing an error message
    setGasBreakdownOperation(operation);

    // Require wallet, contract, and (for actual values) an explicitly selected chain+token
    if (!provider || !userAccount || !contract) {
      setGasBreakdownError("Connect your wallet to view fee breakdown.");
      setGasBreakdown(null);
      return;
    }

    // If no chain/token is selected yet, guide the user instead of showing default-chain values
    if (!selectedChainId || !selectedTokenId) {
      setGasBreakdownError("Select a chain first to view fee breakdown.");
      setGasBreakdown(null);
      return;
    }

    try {
      setIsGasBreakdownLoading(true);
      setGasBreakdownError(null);
      setGasBreakdownOperation(operation);

      let manualGasLimit = 500000;
      if (operation === "transfer") manualGasLimit = 800000;
      else if (operation === "withdraw") manualGasLimit = 1000000;

      let breakdown;
      if (
        operation === "transfer" &&
        opts.tokenAddress &&
        opts.recipient &&
        contract
      ) {
        const amountForContract =
          opts.amountForContract != null
            ? ethers.BigNumber.from(opts.amountForContract)
            : ethers.BigNumber.from(0);
        let transferProofData = await buildTransferProofDataForPreview(
          opts.tokenAddress,
          opts.recipient,
          amountForContract,
        );
        if (!transferProofData) {
          // Fallback to representative payload sizes if preview proof generation is unavailable.
          transferProofData = ethers.utils.defaultAbiCoder.encode(
            ["string", "string", "string"],
            ["A".repeat(2600), "B".repeat(2600), "C".repeat(1200)],
          );
        }

        // Mirror transfer submit path: include native fee value override when fee token is native.
        let txOverrides = {};
        try {
          const feeTokenAddr = await contract.feeToken();
          if (feeTokenAddr === ethers.constants.AddressZero) {
            const feeAmt = await contract.nonAnonymousTransferFee();
            txOverrides = { value: feeAmt };
          }
        } catch (_) {}

        const chainConfigForPreview = CHAINS[selectedChainId];
        const offchainZKPPreview = shouldUseOffchainZKP(
          chainConfigForPreview?.chainIdDecimal,
        );

        breakdown = await estimateTxFees(
          contract,
          "transferConfidential",
          [
            opts.recipient,
            opts.tokenAddress,
            transferProofData,
            offchainZKPPreview,
          ],
          provider,
          { manualGasLimit, txOverrides },
        );
      } else if (operation === "deposit" && opts.tokenAddress && contract) {
        const amountForContract =
          opts.amountForContract != null
            ? ethers.BigNumber.from(opts.amountForContract)
            : ethers.BigNumber.from(0);
        breakdown = await estimateTxFees(
          contract,
          "deposit",
          [opts.tokenAddress, amountForContract],
          provider,
          { manualGasLimit },
        );
      } else if (operation === "withdraw" && opts.tokenAddress && contract) {
        const amountForContract =
          opts.amountForContract != null
            ? ethers.BigNumber.from(opts.amountForContract)
            : ethers.BigNumber.from(0);
        let withdrawProofData = await buildWithdrawProofDataForPreview(
          opts.tokenAddress,
          amountForContract,
        );
        if (!withdrawProofData) {
          // Fallback to representative payload sizes if preview proof generation is unavailable.
          withdrawProofData = ethers.utils.defaultAbiCoder.encode(
            ["string", "string"],
            ["A".repeat(2600), "B".repeat(1200)],
          );
        }
        const chainConfigForWithdrawPreview = CHAINS[selectedChainId];
        const offchainZKPWithdrawPreview = shouldUseOffchainZKP(
          chainConfigForWithdrawPreview?.chainIdDecimal,
        );
        breakdown = await estimateTxFees(
          contract,
          "withdraw",
          [
            opts.tokenAddress,
            amountForContract.toString(),
            withdrawProofData,
            offchainZKPWithdrawPreview,
          ],
          provider,
          { manualGasLimit },
        );
      } else {
        breakdown = await estimateTxFees(null, null, [], provider, {
          manualGasLimit,
        });
      }

      // Attach protocol fee for transfer only; currency varies by network (wei, USDC, PathUSD, etc.).
      let protocolFeeDisplay = null;
      if (operation === "transfer" && contract && breakdown) {
        try {
          const feeTokenAddr = await contract.feeToken();
          const feeAmt = await contract.nonAnonymousTransferFee();
          const chainConfig = CHAINS[selectedChainId];
          protocolFeeDisplay = getProtocolFeeDisplayText(
            chainConfig,
            feeTokenAddr,
            feeAmt.toString(),
          );
        } catch (_) {
          protocolFeeDisplay = null;
        }
      }

      setGasBreakdown(
        protocolFeeDisplay != null
          ? { ...breakdown, protocolFeeDisplay }
          : breakdown,
      );
    } catch (error) {
      console.error("Error estimating gas breakdown:", error);
      setGasBreakdownError(
        getCleanErrorMessage(error) || "Failed to estimate network fees.",
      );
      setGasBreakdown(null);
    } finally {
      setIsGasBreakdownLoading(false);
    }
  };

  const refreshGasBreakdownForExactTx = async (
    operation,
    methodName,
    methodArgs,
    options = {},
  ) => {
    if (
      !showGasBreakdown ||
      gasBreakdownOperation !== operation ||
      !provider ||
      !contract
    ) {
      return;
    }
    try {
      setIsGasBreakdownLoading(true);
      setGasBreakdownError(null);
      const breakdown = await estimateTxFees(
        contract,
        methodName,
        methodArgs,
        provider,
        options,
      );
      setGasBreakdownOperation(operation);
      setGasBreakdown(breakdown);
    } catch (error) {
      console.warn(
        `Failed to refresh ${operation} gas breakdown with exact tx args:`,
        error,
      );
    } finally {
      setIsGasBreakdownLoading(false);
    }
  };

  // Helper function to get operation type name
  const getOperationTypeName = (operationKind) => {
    const operationKinds = [
      "create",
      "deposit",
      "transfer",
      "secure claim",
      "withdraw",
    ];
    return operationKinds[operationKind] || "unknown";
  };

  // Check if user has a pending action
  const hasPendingAction = () => {
    const hasPending = accountData && accountData.pendingAction;
    return hasPending;
  };

  // Get pending action type message from contract
  const getPendingActionMessage = async () => {
    if (!contract || !userAccount) {
      return "A request is in progress. Please wait for it to complete before submitting a new request.";
    }

    try {
      const pendingActionTuple = await contract.getPendingAction(userAccount);
      const kind = parsePendingKind(pendingActionTuple[0]);
      const actionTypeName = getPendingActionTypeName(kind);

      if (actionTypeName) {
        return `You have a pending ${actionTypeName} request. Please wait for it to complete before submitting a new request.`;
      }
    } catch (error) {
      console.error("Error fetching pending action type:", error);
    }

    // Fallback message if we can't fetch the type
    return "A request is in progress. Please wait for it to complete before submitting a new request.";
  };

  // Check for pending action and update state
  const checkPendingAction = async () => {
    if (!contract || !userAccount) {
      setPendingActionKind(null);
      return;
    }

    try {
      // Check contract directly for pending action
      const pendingActionTuple = await contract.getPendingAction(userAccount);
      const kind = parsePendingKind(pendingActionTuple[0]);
      setPendingActionKind(kind);
    } catch (error) {
      // No account / wrong-chain lag: treat as no pending action rather than
      // spamming the console every poll tick.
      if (!isCallException(error)) {
        console.error("Error checking pending action:", error);
      }
      // On error, check accountData as fallback
      if (accountData && accountData.pendingAction) {
        // Keep current state or try to infer from accountData if possible
        // For now, just set to null on error to avoid showing stale state
        setPendingActionKind(null);
      } else {
        setPendingActionKind(null);
      }
    }
  };

  // Check if user can submit new operations
  const canSubmitOperation = () => {
    if (!accountData) return false;
    return (
      accountData.exists && accountData.finalized && !accountData.pendingAction
    );
  };

  // Check if user can submit secure claim operations (less strict than regular operations)
  const canSubmitSecureClaim = () => {
    // If we have a user account and can see balances, allow secure claim
    // This is more permissive since secure claim is about claiming already received funds
    if (!userAccount) {
      return false;
    }

    // If we have account data, use it for validation
    if (accountData) {
      const canSubmit = accountData.exists && !accountData.pendingAction;
      return canSubmit;
    }

    // If no account data but we have a user account, allow it
    // This handles cases where account data hasn't loaded yet
    return true;
  };

  // Request tracking functions
  const addPendingRequest = async (
    requestId,
    operationKind,
    details,
    transactionHash,
    amount = null,
    randomness = null,
  ) => {
    if (!userAccount) return;

    try {
      // Get current chainId from selected token data - convert to numeric chainId for consistency
      const chainKey = getTokenData().chainId; // This is the chain key like 'arbitrum'
      const chainConfig = CHAINS[chainKey];
      // Use numeric chainId (chainIdDecimal) for document IDs to match contractListener format
      const numericChainId =
        chainConfig?.chainIdDecimal || chainConfig?.chainId
          ? typeof chainConfig.chainId === "string" &&
            chainConfig.chainId.startsWith("0x")
            ? parseInt(chainConfig.chainId, 16)
            : chainConfig.chainIdDecimal || Number(chainConfig.chainId)
          : null;


      // Encrypt sender randomness using IBE if this is a transfer and randomness is provided
      // NOTE: sender_randomness corresponds to transfer_amount_source_ciphertext_lo (transfer amount encrypted for sender)
      // This randomness can be used by auditors to decrypt the transfer amount ciphertext for the sender
      let encryptedSenderRandomness = null;
      let queuedEncryptedSenderRandomness = null;
      let encryptedSenderRandomnessId = null;
      let queuedEncryptedSenderRandomnessId = null;
      if (operationKind === 2 && randomness && randomness.sender) {
        // 2 = Transfer
        try {
          const senderEncryption = await encryptRandomness(
            userAccount,
            chainKey,
            randomness.sender,
          );
          encryptedSenderRandomness = senderEncryption.active;
          queuedEncryptedSenderRandomness = senderEncryption.queued;
          encryptedSenderRandomnessId = senderEncryption.activeId;
          queuedEncryptedSenderRandomnessId = senderEncryption.queuedId;
        } catch (error) {
          console.error("Error encrypting sender randomness:", error);
          // Don't fail the whole operation if encryption fails
        }
      }

      // Encrypt receiver randomness (when available) so backend can mirror
      // the received record with the correct ciphertext even if client-side
      // receiver write fails.
      let encryptedReceiverRandomness = null;
      let queuedEncryptedReceiverRandomness = null;
      let encryptedReceiverRandomnessId = null;
      let queuedEncryptedReceiverRandomnessId = null;
      if (operationKind === 2 && details.recipient && randomness && randomness.receiver) {
        try {
          const receiverEncryption = await encryptRandomness(
            details.recipient,
            chainKey,
            randomness.receiver,
          );
          encryptedReceiverRandomness = receiverEncryption.active;
          queuedEncryptedReceiverRandomness = receiverEncryption.queued;
          encryptedReceiverRandomnessId = receiverEncryption.activeId;
          queuedEncryptedReceiverRandomnessId = receiverEncryption.queuedId;
        } catch (error) {
          console.error("Error encrypting receiver randomness:", error);
          // Don't fail the whole operation if encryption fails
        }
      }

      // Create sender's record
      const requestData = {
        requestId,
        userAddress: userAccount.toLowerCase(),
        operationKind,
        type: getOperationTypeName(operationKind),
        details,
        transactionHash,
        chainId: numericChainId, // Store numeric chainId for document ID consistency
        chainKey: chainKey, // Also store chain key for display/filtering
        status: "pending",
      };

      // Add encrypted sender randomness if available
      if (encryptedSenderRandomness) {
        requestData.encryptedSenderRandomness = encryptedSenderRandomness;
      }
      if (encryptedSenderRandomnessId) {
        requestData.encryptedSenderRandomnessId = encryptedSenderRandomnessId;
      }
      if (queuedEncryptedSenderRandomness) {
        requestData.queuedEncryptedSenderRandomness = queuedEncryptedSenderRandomness;
      }
      if (queuedEncryptedSenderRandomnessId) {
        requestData.queuedEncryptedSenderRandomnessId = queuedEncryptedSenderRandomnessId;
      }
      if (encryptedReceiverRandomness) {
        requestData.encryptedReceiverRandomness = encryptedReceiverRandomness;
      }
      if (encryptedReceiverRandomnessId) {
        requestData.encryptedReceiverRandomnessId = encryptedReceiverRandomnessId;
      }
      if (queuedEncryptedReceiverRandomness) {
        requestData.queuedEncryptedReceiverRandomness = queuedEncryptedReceiverRandomness;
      }
      if (queuedEncryptedReceiverRandomnessId) {
        requestData.queuedEncryptedReceiverRandomnessId = queuedEncryptedReceiverRandomnessId;
      }

      try {
        await RequestService.addRequest(requestData);
      } catch (error) {
        // RequestService.addRequest already retries, so if we get here, all retries failed
        console.error(
          "Failed to add request to Firebase after retries:",
          error,
        );
        showNotification(
          "Warning: Transaction was submitted but could not be saved to history. It may still appear after refresh.",
          "warning",
        );
        // Don't throw - the transaction was successful, just the history tracking failed
      }

      // If this is a transfer, also create a receiver record
      if (operationKind === 2 && details.recipient) {
        // 2 = Transfer
        try {
          const receiverRequestId = `received-${requestId}-${details.recipient.toLowerCase()}`;

          const receiverRequestData = {
            requestId: receiverRequestId,
            userAddress: details.recipient.toLowerCase(),
            operationKind: 2,
            type: "received",
            details: {
              sender: userAccount.toLowerCase(),
              recipient: details.recipient.toLowerCase(),
              token: details.token,
              tokenAddress: details.tokenAddress || details.token,
            },
            transactionHash,
            chainId: numericChainId, // Use numeric chainId for consistency
            chainKey: chainKey, // Also store chain key for display/filtering
            status: "pending",
          };

          // Add encrypted receiver randomness if available
          if (encryptedReceiverRandomness) {
            receiverRequestData.encryptedReceiverRandomness =
              encryptedReceiverRandomness;
          }
          if (encryptedReceiverRandomnessId) {
            receiverRequestData.encryptedReceiverRandomnessId =
              encryptedReceiverRandomnessId;
          }
          if (queuedEncryptedReceiverRandomness) {
            receiverRequestData.queuedEncryptedReceiverRandomness =
              queuedEncryptedReceiverRandomness;
          }
          if (queuedEncryptedReceiverRandomnessId) {
            receiverRequestData.queuedEncryptedReceiverRandomnessId =
              queuedEncryptedReceiverRandomnessId;
          }

          try {
            await RequestService.addRequest(receiverRequestData);
          } catch (error) {
            console.error("Error adding receiver record after retries:", error);
            // Don't show notification for receiver record failures - it's less critical
            // The receiver can still see the transaction from contract events
          }
        } catch (error) {
          console.error("Error creating receiver record:", error);
          // Don't fail the whole operation if receiver record fails
        }
      }
    } catch (error) {
      console.error("Unexpected error in addPendingRequest:", error);
      // This shouldn't happen, but if it does, log it
    }
  };

  // Arbitrum One (mainnet) is the intended landing chain; Sepolia counterpart
  // is `arbitrum`. Default before connect, after disconnect, and when nothing
  // else has been explicitly chosen.
  // Default landing chain: Arbitrum One (mainnet). Testnet counterpart is
  // `arbitrum` (Arbitrum Sepolia) — never interchange the two config keys.
  const DEFAULT_CHAIN_ID = getDefaultChainIdForNetwork("mainnet");
  const DEFAULT_TOKEN_ID = "usdc";

  const buildDefaultTokenData = () => {
    const chainConfig = CHAINS[DEFAULT_CHAIN_ID];
    const tokenConfig = chainConfig?.tokens?.[DEFAULT_TOKEN_ID];
    if (!chainConfig || !tokenConfig) return null;
    return {
      ...tokenConfig,
      id: `${DEFAULT_CHAIN_ID}_${DEFAULT_TOKEN_ID}`,
      chainId: DEFAULT_CHAIN_ID,
      chainName: chainConfig.name,
      chainIcon: chainConfig.icon,
      chainColor: chainConfig.color,
      chainActive: chainConfig.active,
    };
  };

  // Restore the last chain the user picked. localStorage survives refresh;
  // sessionStorage is a fallback for older sessions.
  const getInitialChainId = () => {
    try {
      const saved = getSavedChainId();
      const savedChain = saved ? CHAINS[saved] : null;
      if (
        saved &&
        savedChain &&
        isChainEnabledForNetwork(saved, savedChain.network)
      ) {
        return saved;
      }
    } catch (e) {
      console.warn("Error reading saved chain preference:", e);
    }
    return DEFAULT_CHAIN_ID;
  };

  const getInitialTokenId = () => {
    try {
      const savedChain = getSavedChainId();
      const savedToken = getSavedTokenId();
      const chainConfig = savedChain ? CHAINS[savedChain] : null;
      if (
        savedChain &&
        savedToken &&
        chainConfig &&
        isChainEnabledForNetwork(savedChain, chainConfig.network) &&
        chainConfig.tokens?.[savedToken]
      ) {
        return savedToken;
      }
    } catch (e) {
      console.warn("Error reading saved token preference:", e);
    }
    return DEFAULT_TOKEN_ID;
  };

  const getInitialTokenData = () => {
    try {
      const savedChain = getSavedChainId();
      const savedToken = getSavedTokenId();
      if (
        savedChain &&
        savedToken &&
        CHAINS[savedChain] &&
        isChainEnabledForNetwork(savedChain, CHAINS[savedChain].network) &&
        CHAINS[savedChain]?.tokens?.[savedToken]
      ) {
        const chainConfig = CHAINS[savedChain];
        const tokenConfig = chainConfig.tokens[savedToken];
        return {
          ...tokenConfig,
          id: `${savedChain}_${savedToken}`,
          chainId: savedChain,
          chainName: chainConfig.name,
          chainIcon: chainConfig.icon,
          chainColor: chainConfig.color,
          chainActive: true,
        };
      }
    } catch (e) {
      console.warn("Error reconstructing token data from saved preference:", e);
    }
    return buildDefaultTokenData();
  };

  // Common state
  const [transferAmount, setTransferAmount] = useState("");
  const [showRequestModal, setShowRequestModal] = useState(false); // "Request a Payment" builder
  // Expose an opener so App.js's topbar "Request a Payment" button can open this modal.
  useEffect(() => {
    if (!onRequestModalRegister) return undefined;
    onRequestModalRegister(() => () => setShowRequestModal(true));
    return () => onRequestModalRegister(null);
  }, [onRequestModalRegister]);
  const transferAmountRef = useRef("");
  transferAmountRef.current = transferAmount;
  const [depositAmount, setDepositAmount] = useState("");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [transferAmountError, setTransferAmountError] = useState("");
  const [depositAmountError, setDepositAmountError] = useState("");
  const [withdrawAmountError, setWithdrawAmountError] = useState("");
  const depositAmountRef = useRef("");
  depositAmountRef.current = depositAmount;
  const withdrawAmountRef = useRef("");
  withdrawAmountRef.current = withdrawAmount;
  const [selectedChainId, setSelectedChainId] = useState(getInitialChainId);
  const [selectedTokenId, setSelectedTokenId] = useState(getInitialTokenId);
  const [selectedTokenData, setSelectedTokenData] =
    useState(getInitialTokenData);
  const [showTokenSelector, setShowTokenSelector] = useState(false);
  // Mobile replaces the desktop TokenSelector (chains + tokens side by side
  // in one modal) with two sequential Sheets: chain list, then that
  // chain's token list. null = showing the chain-list sheet; a chain id =
  // showing that chain's token-list sheet.
  const [mobilePickerChainId, setMobilePickerChainId] = useState(null);
  const closeMobilePicker = () => {
    setShowTokenSelector(false);
    setMobilePickerChainId(null);
  };
  // Governs both the chain list and (through selectedChainId) the token
  // list — one flip switches which universe of chains/tokens is being
  // browsed, on both the desktop TokenSelector and the mobile Sheets
  // below. Match the restored/default chain so a saved testnet selection
  // doesn't open under the Mainnet tab (and vice versa).
  const [networkMode, setNetworkMode] = useState(() =>
    getNetworkModeForChain(getInitialChainId()),
  );
  // Same enablement rules as desktop TokenSelector (incl. USDG/USDe + CUSD).
  const isMobileTokenEnabled = (chainId, symbol) =>
    isTokenEnabledForNetwork(chainId, symbol, networkMode);
  // The Mainnet/Testnet toggle is a browse filter for the selector's chain and
  // token lists ONLY - it must NOT switch the active network. The actual switch
  // happens when the user picks a chain + token from the list
  // (handleChainSelect / handleTokenSelect). This used to auto-jump to the
  // mode's default chain whenever the current chain wasn't enabled in the new
  // mode, so merely tapping "Testnet" switched the whole app to Arbitrum
  // Sepolia + USDC without the user selecting anything.
  const handleNetworkModeChange = (mode) => {
    setNetworkMode(mode);
  };

  useEffect(() => {
    if (showTokenSelector) {
      setNetworkMode(getNetworkModeForChain(selectedChainId));
    }
  }, [showTokenSelector]);
  // Post-submit status screen (StableTrust Pay - Editorial design: 'idle' shows
  // the normal form, 'pending' shows a spinner while the proof/tx is
  // generated, 'done' shows a checkmark + summary + View receipt/View in
  // history). One per tab since a user could plausibly have more than one
  // in flight (submit a transfer, switch to deposit while it confirms).
  const [transferStatus, setTransferStatus] = useState("idle");
  const [depositStatus, setDepositStatus] = useState("idle");
  const [withdrawStatus, setWithdrawStatus] = useState("idle");
  // Snapshot of the submitted amount/recipient/tx hash/etc, captured right
  // before the form fields are cleared — both the status meta line and the
  // receipt modal need this after the inputs themselves have gone blank.
  const [transferReceiptData, setTransferReceiptData] = useState(null);
  const [depositReceiptData, setDepositReceiptData] = useState(null);
  const [withdrawReceiptData, setWithdrawReceiptData] = useState(null);
  const [receiptTab, setReceiptTab] = useState(null); // null | 'transfer' | 'deposit' | 'withdraw'
  const [receiptShared, setReceiptShared] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isApplyingPending, setIsApplyingPending] = useState(false);
  const [isFaucetProcessing, setIsFaucetProcessing] = useState(false);
  const [balance, setBalance] = useState("Loading...");
  const [pendingBalance, setPendingBalance] = useState(null);
  const [showPendingNotification, setShowPendingNotification] = useState(false);
  const [pendingNotificationDismissed, setPendingNotificationDismissed] =
    useState(false);
  const pendingNotificationRef = useRef(null);
  const [hasPendingDeposit, setHasPendingDeposit] = useState(false);
  const [pendingActionKind, setPendingActionKind] = useState(null); // bytes32 pending kind from contract

  // Key derivation consent state
  const [keyDerivationConsent, setKeyDerivationConsent] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [keysDerived, setKeysDerived] = useState(false);
  const [userCancelledKeyDerivation, setUserCancelledKeyDerivation] =
    useState(false);
  const [accountCreationFailed, setAccountCreationFailed] = useState(false);
  const [isFirstTimeConnection, setIsFirstTimeConnection] = useState(true);

  // State for account creation during key derivation
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [accountCreationStep, setAccountCreationStep] = useState("");
  // Why the automatic account-creation step stopped or warned (insufficient
  // gas / token). Reflected on the CTA / onboarding error — no inline banner.
  // { node, severity, cta, hideNotice }.
  const [accountSetupIssue, setAccountSetupIssue] = useState(null);

  // --- First-run onboarding (Onboarding.js) ---------------------------------
  // 'checking' reading whether this wallet has an account on the selected
  //            chain; the automatic consent modal / account creation wait.
  // 'open'     no account yet: onboarding owns setup until it finishes.
  // 'skipped'  dismissed; form CTAs read "Set up your account" and reopen it.
  // 'off'      account already existed (or the read failed): the regular
  //            consent-modal flow below, unchanged.
  const [onboardingPhase, setOnboardingPhase] = useState(() =>
    userAccount ? "checking" : "off",
  );
  // null (unknown) | 'missing' | 'pending' (created, not finalized) | 'active'
  const [onboardingAccountState, setOnboardingAccountState] = useState(null);
  // "Create account" was clicked — the only thing that lets the automatic
  // sign → create flow run while onboarding is open.
  const [onboardingCreateRequested, setOnboardingCreateRequested] =
    useState(false);
  // Any create attempt this session, so an account turning active reads as
  // "done" rather than "this network already had one".
  const [onboardingAttempted, setOnboardingAttempted] = useState(false);
  const [onboardingSetupError, setOnboardingSetupError] = useState(null);
  const onboardingBlocksAutoSetup =
    onboardingPhase === "checking" ||
    onboardingPhase === "skipped" ||
    (onboardingPhase === "open" && !onboardingCreateRequested);
  // Read inside the long-lived auto-create closure without re-running it.
  const onboardingOpenRef = useRef(false);
  onboardingOpenRef.current = onboardingPhase === "open";
  const accountCreationAttemptedRef = useRef(false);
  const accountCreationCancelledRef = useRef(false);
  const accountCreationInProgressRef = useRef(false);
  const lastBalanceErrorShownRef = useRef(null); // Track last balance error to prevent duplicates
  const onOperationCompleteRef = useRef(onOperationComplete);
  const contractRef = useRef(contract);
  const secureClaimHandlerRef = useRef(null);
  const loadingChainRef = useRef(null); // Track which chain we're loading for
  const loadingTokenRef = useRef(null); // Track which token we're loading for
  // Drop stale wallet ERC-20 balance reads when the user switches USDC↔USDT
  // (or chain) mid-flight — otherwise Deposit can show the previous token's
  // amount under the newly selected symbol.
  const walletBalanceRequestIdRef = useRef(0);
  const loadingPendingBalanceRef = useRef(false); // Track if we're currently loading pending balance
  const recipientTransferTimeoutRef = useRef(null); // Track pending timeout for recipient transfer listener
  // Cache balances per chain+token combination to avoid unnecessary reloads
  const balanceCacheRef = useRef(new Map()); // key: `${chainId}_${tokenId}`, value: { available: number, pending: number }
  const justRestoredFromCacheRef = useRef(false); // Track if we just restored balances from cache
  const lastLoadedKeyRef = useRef(null); // Track last loaded chain+token key to avoid reloading immediately after cache restore
  const accountStatusCheckInProgressRef = useRef(false); // Track if account status check is currently running
  const lastAccountStatusCheckRef = useRef(0); // Track last time we checked account status (for throttling)
  const prevPendingActionKindRef = useRef(null); // Track previous pendingActionKind to detect when operation completes

  // Utility helpers
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  /**
   * Switch the wallet onto the UI-selected chain, then open an SDK session.
   * Hybrid RPC retargeting alone is not enough — signing uses the wallet
   * chain, which is how Secure Claim could land on Arb One while the UI
   * showed Arbitrum Sepolia.
   */
  const getSessionForSelectedChain = async () => {
    const chainConfig = CHAINS[selectedChainId];
    if (!chainConfig?.contractAddress) {
      throw new Error("Contract address is not configured for this chain");
    }
    if (switchToChain) {
      const switched = await switchToChain(selectedChainId);
      if (!switched) {
        throw new Error(
          `Please switch your wallet to ${chainConfig.name} and try again.`,
        );
      }
    }
    return getReadyConfidentialSession(
      chainConfig,
      provider,
      userAccount,
      keypair,
    );
  };

  // Derived keypair (in-memory only)
  // Key derivation is now handled by the WASM hook

  // Transfer-specific state
  const [recipient, setRecipient] = useState("");
  const [recipientError, setRecipientError] = useState(null);
  const [recipientAccountStatus, setRecipientAccountStatus] = useState(null); // null | 'checking' | 'has_account' | 'no_account'
  const latestRecipientAddressRef = useRef(null);
  // Bumps on every recipient check so a slow/failed response for an earlier
  // attempt (same address, wrong-chain wallet lag) can't overwrite a newer
  // successful read.
  const recipientCheckSeqRef = useRef(0);

  // Get the selected chain's chainId for key derivation
  const getSelectedChainId = () => {
    const selectedChain = CHAINS[selectedChainId];
    return selectedChain ? selectedChain.chainIdDecimal : null;
  };

  // Get the selected chain's contract address for key derivation
  const getSelectedChainContractAddress = () => {
    const selectedChain = CHAINS[selectedChainId];
    return selectedChain ? selectedChain.contractAddress : null;
  };

  // Reads (balance, account core) always go to the UI-selected chain's
  // public RPC. App `contract` is bound to the wallet provider, which can
  // still be on Arbitrum One after the user picked Sepolia — that path
  // returns empty/0 and "No contract address configured for Arbitrum One".
  const getSelectedChainReadContract = () => {
    const cfg = CHAINS[selectedChainId];
    if (cfg?.contractAddress && cfg?.rpcUrl) {
      return new ethers.Contract(
        cfg.contractAddress,
        CONTRACT_ABI,
        new ethers.providers.JsonRpcProvider(cfg.rpcUrl),
      );
    }
    return contract;
  };

  const selectedChainIdForKeys = getSelectedChainId();
  const selectedChainContractAddress = getSelectedChainContractAddress();

  // Only provide verifyingContract when user has selected both chain and token
  const verifyingContract =
    selectedChainId && selectedTokenId
      ? selectedChainContractAddress || null
      : null;

  const {
    keypair,
    loading: keyLoading,
    error: keyError,
    needsConsent,
  } = useDeterministicElGamalKeypair(
    provider,
    userAccount,
    selectedChainIdForKeys,
    verifyingContract,
    keyDerivationConsent,
  );

  // Debug keypair state changes
  useEffect(() => {
  }, [
    keypair,
    keyLoading,
    keyError,
    selectedChainId,
    selectedChainIdForKeys,
    selectedChainContractAddress,
    verifyingContract,
    userAccount,
  ]);

  // Notify parent when keypair changes
  useEffect(() => {
    if (onKeypairUpdate) {
      onKeypairUpdate(keypair);
    }
    if (keypair) {
      setKeysDerived(true);
      // Mark this wallet+account combination as connected when keys are successfully derived
      if (userAccount && selectedChainIdForKeys && verifyingContract) {
        secureKeyCache.markAsConnected(
          userAccount,
          selectedChainIdForKeys,
          verifyingContract,
        );
      }
    } else {
      setKeysDerived(false);
    }
  }, [
    keypair,
    onKeypairUpdate,
    userAccount,
    selectedChainIdForKeys,
    verifyingContract,
  ]);

  // Keep onOperationComplete / contract refs updated without retriggering
  // auto-create (switchToChain rebuilds contract identity mid-flight).
  useEffect(() => {
    onOperationCompleteRef.current = onOperationComplete;
  }, [onOperationComplete]);

  useEffect(() => {
    contractRef.current = contract;
  }, [contract]);

  // Automatically create account after keys are derived if account doesn't exist
  useEffect(() => {
    // Onboarding decides when setup may start (see onboardingBlocksAutoSetup).
    if (onboardingBlocksAutoSetup) {
      return;
    }
    // Only run when keys are actually derived and account creation hasn't failed
    // Don't auto-retry if user cancelled account creation
    // Also require that both chain and token are selected
    if (
      !keysDerived ||
      !keypair ||
      accountCreationFailed ||
      !contract ||
      !userAccount ||
      !selectedChainId ||
      !selectedTokenId
    ) {
      return;
    }

    // --- StrictMode-safe session guard ---
    // Claim is held only while this effect run is alive. Cleanup MUST release
    // it — otherwise a remount/re-run (chain switch, contract identity change,
    // StrictMode) sees the stale claim and permanently skips ensureAccount.
    const sessionKey = makeAccountCreationKey(
      userAccount,
      selectedChainIdForKeys,
      verifyingContract,
    );

    if (accountCreationSessions.has(sessionKey)) {
      // Another invocation still holds the claim. If nothing is actually in
      // progress, the claim was abandoned — release and continue so returning
      // to a chain after a rejected/unfunded create can retry.
      if (!accountCreationInProgressRef.current) {
        accountCreationSessions.delete(sessionKey);
      } else {
        // Never leave the CTA stuck on "Creating account..." — clear local
        // creating state and refresh parent flags for existing accounts.
        if (isCreatingAccount) {
          setIsCreatingAccount(false);
          setAccountCreationStep("");
          accountCreationInProgressRef.current = false;
        }
        if (
          (!accountExists || !hasActiveAccount) &&
          onOperationCompleteRef.current
        ) {
          onOperationCompleteRef.current();
        }
        return;
      }
    }

    // Existing in-component guards
    if (
      accountCreationAttemptedRef.current ||
      accountCreationInProgressRef.current
    ) {
      return;
    }

    // Stale UI flag from an aborted run (cleanup used to leave this true) —
    // clear it and continue so we can still open the wallet popup.
    if (isCreatingAccount) {
      setIsCreatingAccount(false);
      setAccountCreationStep("");
    }

    // Mark this session as in-flight *before* any async work so a concurrent
    // mount can't double-submit. Cleanup releases the claim if this run aborts.
    accountCreationSessions.add(sessionKey);

    // Set ALL guards atomically before any async operations to prevent race conditions
    accountCreationAttemptedRef.current = true;
    accountCreationInProgressRef.current = true;
    accountCreationCancelledRef.current = false;
    // Per-run abort flag — must not rely only on the shared cancelled ref,
    // because the next effect run (or chain switch) clears that ref while this
    // async closure may still be awaiting a wallet popup.
    let effectAborted = false;
    const isAborted = () =>
      effectAborted || accountCreationCancelledRef.current;
    // Do NOT set isCreatingAccount yet — wait until we know the account is
    // missing. Otherwise a mainnet↔testnet switch briefly shows "Creating
    // account..." while we only perform a read.

    const autoCreateAccount = async () => {

      // Only proceed if:
      // 1. Keys are derived (keypair exists)
      // 2. We have a contract and user account
      // 3. We haven't already attempted account creation for this key derivation
      if (!keypair || !(contractRef.current || contract) || !userAccount) {
        // Allow retry if dependencies were missing
        accountCreationAttemptedRef.current = false;
        accountCreationInProgressRef.current = false;
        accountCreationSessions.delete(sessionKey);
        setIsCreatingAccount(false);
        return;
      }

      if (isAborted()) {
        accountCreationAttemptedRef.current = false;
        accountCreationInProgressRef.current = false;
        accountCreationSessions.delete(sessionKey);
        setIsCreatingAccount(false);
        return;
      }

      try {
        // Check if account exists on the *selected* chain — never treat a
        // mainnet-lagged CALL_EXCEPTION as "no Sepolia account".
        let accountExists = false;
        const selectedCfg = CHAINS[selectedChainId];
        const tryReadExists = async (reader) => {
          const accountDataTuple = await reader.getAccountCore(userAccount);
          return !!accountDataTuple[0];
        };
        try {
          // Retry through transient RPC 429s — Base's public endpoint especially
          // rate-limits hard and would otherwise abort before the wallet popup.
          accountExists = await withRateLimitRetry(
            async () => {
              if (selectedCfg?.rpcUrl && selectedCfg?.contractAddress) {
                const selectedReader = new ethers.Contract(
                  selectedCfg.contractAddress,
                  (contractRef.current || contract).interface,
                  new ethers.providers.JsonRpcProvider(selectedCfg.rpcUrl),
                );
                return tryReadExists(selectedReader);
              }
              return tryReadExists(contractRef.current || contract);
            },
            { retries: 4, baseDelayMs: 800 },
          );
        } catch (checkError) {
          if (
            checkError.code === "CALL_EXCEPTION" ||
            checkError.message?.includes("CALL_EXCEPTION")
          ) {
            accountExists = false;
          } else {
            throw checkError;
          }
        }

        if (isAborted()) {
          accountCreationAttemptedRef.current = false;
          accountCreationInProgressRef.current = false;
          accountCreationSessions.delete(sessionKey);
          setIsCreatingAccount(false);
          return;
        }

        if (!accountExists) {
          setIsCreatingAccount(true);

          // Check gas and token balances before attempting account creation
          setAccountCreationStep("Checking balances...");

          // Get chain configuration
          const chainConfig = CHAINS[selectedChainId];
          const nativeCurrencySymbol =
            chainConfig?.nativeCurrency?.symbol || "ETH";
          const nativeCurrencyDecimals =
            chainConfig?.nativeCurrency?.decimals || 18;
          const isTempo = selectedChainId === "tempo";

          // Check gas balance (or fee token balance for Tempo)
          let gasBalance;
          let hasInsufficientGas = false;
          let feeTokenInfo = null;

          if (isTempo) {
            // For Tempo, check if user has any fee token balance
            try {
              feeTokenInfo = await checkTempoFeeTokenBalance(
                provider,
                userAccount,
              );
              hasInsufficientGas = !feeTokenInfo.hasFeeToken;
              if (feeTokenInfo.hasFeeToken) {
              }
            } catch (balanceError) {
              console.error(
                "Error checking Tempo fee token balance:",
                balanceError,
              );
              throw new Error(
                "Could not check fee token balance. Check your network connection.",
              );
            }
          } else {
            // For other chains, check native gas on the *selected* chain RPC
            // (wallet provider may still be on another network).
            let lastError = null;
            const maxRetries = 3;
            const gasReadProvider = selectedCfg?.rpcUrl
              ? new ethers.providers.JsonRpcProvider(selectedCfg.rpcUrl)
              : provider;
            for (let attempt = 0; attempt < maxRetries; attempt++) {
              try {
                if (attempt > 0) {
                  // Wait before retry (exponential backoff)
                  await new Promise((resolve) =>
                    setTimeout(resolve, 1000 * attempt),
                  );
                }
                gasBalance = await gasReadProvider.getBalance(userAccount);
                // Use formatUnits with correct decimals instead of formatEther
                const formattedBalance = ethers.utils.formatUnits(
                  gasBalance,
                  nativeCurrencyDecimals,
                );
                hasInsufficientGas = gasBalance.isZero();
                lastError = null; // Success, clear error
                break; // Exit retry loop on success
              } catch (balanceError) {
                lastError = balanceError;
                console.error(
                  `Error checking gas balance (attempt ${attempt + 1}/${maxRetries}):`,
                  balanceError,
                );
                // If this is the last attempt, throw the error
                if (attempt === maxRetries - 1) {
                  // Provide more specific error message
                  const errorMessage =
                    balanceError.message || String(balanceError);
                  if (isSSLError(balanceError)) {
                    const chainName = chainConfig?.name || "this network";
                    throw new Error(
                      `Cannot connect securely to ${chainName}. Try again later.`,
                    );
                  } else if (
                    errorMessage.includes("network") ||
                    errorMessage.includes("connection") ||
                    errorMessage.includes("timeout") ||
                    errorMessage.includes("HTTP request failed") ||
                    errorMessage.includes("fetch failed") ||
                    errorMessage.includes("RPC")
                  ) {
                    throw new Error(
                      "RPC connection error: Cannot reach the network. Try again.",
                    );
                  } else {
                    throw new Error(
                      "Cannot check gas balance right now. Try again.",
                    );
                  }
                }
              }
            }
          }

          // Check token balance against the *selected* chain's RPC — the
          // wallet provider may still be on Sepolia while the UI is on
          // Arbitrum One, which makes balanceOf revert with data="0x".
          let hasInsufficientToken = false;
          let tokenSymbol = null;
          const tokenData = getTokenData();
          if (
            tokenData &&
            tokenData.address &&
            tokenData.address !== "0x0000000000000000000000000000000000000000"
          ) {
            try {
              const tokenReadProvider = selectedCfg?.rpcUrl
                ? new ethers.providers.JsonRpcProvider(selectedCfg.rpcUrl)
                : provider;
              const tokenContract = new ethers.Contract(
                tokenData.address,
                ["function balanceOf(address) view returns (uint256)"],
                tokenReadProvider,
              );
              const tokenBalance = await tokenContract.balanceOf(userAccount);
              const formattedTokenBalance = ethers.utils.formatUnits(
                tokenBalance,
                tokenData.decimals || 18,
              );
              hasInsufficientToken = tokenBalance.isZero();
              tokenSymbol = tokenData.symbol;
            } catch (tokenBalanceError) {
              console.error("Error checking token balance:", tokenBalanceError);
              // Don't block account creation if we can't check token balance
            }
          }

          // Funding gaps: block setup when gas is missing, but do not show an
          // inline fund banner (funding help lives in the onboarding).
          if (hasInsufficientGas && hasInsufficientToken) {
            setAccountSetupIssue({
              severity: "error",
              cta: "Add funds, then retry",
              hideNotice: true,
            });
            lastBalanceErrorShownRef.current = `insufficient-both-${selectedChainId}-${tokenSymbol}`;
            setAccountCreationStep("");
            // Reset states to allow retry
            accountCreationAttemptedRef.current = false;
            accountCreationInProgressRef.current = false;
            accountCreationSessions.delete(sessionKey);
            setIsCreatingAccount(false);
            setAccountCreationFailed(true);
            setKeysDerived(false);
            setKeyDerivationConsent(false);
            return;
          } else if (hasInsufficientGas) {
            setAccountSetupIssue({
              severity: "error",
              cta: "Add funds, then retry",
              hideNotice: true,
            });
            lastBalanceErrorShownRef.current = `insufficient-gas-${selectedChainId}`;
            setAccountCreationStep("");
            // Reset states to allow retry
            accountCreationAttemptedRef.current = false;
            accountCreationInProgressRef.current = false;
            accountCreationSessions.delete(sessionKey);
            setIsCreatingAccount(false);
            setAccountCreationFailed(true);
            setKeysDerived(false);
            setKeyDerivationConsent(false);
            return;
          } else if (hasInsufficientToken) {
            // Gas is enough to create the account; no inline funding notice.
            // Continue with account creation.
          } else {
            // Balances are fine — clear any stale funding block.
            setAccountSetupIssue(null);
          }

          setAccountCreationStep("Creating account...");

          // Convert Base64 public key to hex for contract
          const pkBase64 = keypair.public_key;
          const pkBytes = Uint8Array.from(atob(pkBase64), (c) =>
            c.charCodeAt(0),
          );
          const pkHexForContract =
            "0x" +
            Array.from(pkBytes)
              .map((b) => b.toString(16).padStart(2, "0"))
              .join("");

          if (isAborted()) {
            accountCreationAttemptedRef.current = false;
            accountCreationInProgressRef.current = false;
            accountCreationSessions.delete(sessionKey);
            setIsCreatingAccount(false);
            return;
          }

      setAccountCreationStep("Creating confidential account...");
      {
        const chainConfig = CHAINS[selectedChainId];
        if (!chainConfig?.contractAddress) {
          throw new Error("Contract address is not configured for this chain");
        }
        // Signing still requires the wallet on the selected chain. Prefer an
        // explicit switch before ensureAccount so we don't submit/read against
        // the wrong network (BAD_DATA value="0x" on getAccountCore).
        if (isAborted()) {
          accountCreationAttemptedRef.current = false;
          accountCreationInProgressRef.current = false;
          accountCreationSessions.delete(sessionKey);
          setIsCreatingAccount(false);
          return;
        }
        // Switches the wallet onto the selected chain and checks it got there.
        await getSessionForSelectedChain();
        // Sent here rather than through the SDK's ensureAccount, which leaves
        // fees to the wallet: MetaMask's own estimate can land just under a
        // rising base fee (seen on Arbitrum Sepolia) and the network rejects
        // it. Same buffered-fee path the transfer uses.
        const createContract = new ethers.Contract(
          chainConfig.contractAddress,
          CONTRACT_ABI,
          provider.getSigner(userAccount),
        );
        const createTx = await executeContractTxWithGasFallback(
          createContract,
          "createConfidentialAccount",
          [pkHexForContract],
          { manualGasLimit: 1000000 },
        );
        await createTx.wait();
      }
      setAccountCreationStep("Waiting for transaction confirmation...");

          setAccountCreationStep("Waiting for account to be activated...");

          // Wait for account to be processed and finalized
          let attempts = 0;
          const maxAttempts = 30; // 30 seconds

          while (attempts < maxAttempts) {
            if (isAborted()) {
              accountCreationAttemptedRef.current = false;
              accountCreationInProgressRef.current = false;
              accountCreationSessions.delete(sessionKey);
              setIsCreatingAccount(false);
              setAccountCreationStep("");
              return;
            }
            await new Promise((resolve) => setTimeout(resolve, 1000));

            // Refresh account status
            await onOperationCompleteRef.current();

            // Check if account is now active by checking the contract directly
            try {
              const activeContract = contractRef.current || contract;
              const accountDataTuple =
                await activeContract.getAccountCore(userAccount);
              const isActive = accountDataTuple[0] && accountDataTuple[1]; // exists && finalized

              if (isActive) {
                setAccountCreationStep("Account created successfully!");
                // Onboarding shows its own success screen instead.
                if (!onboardingOpenRef.current) {
                  showNotification("Account created successfully!", "success");
                }
                // Clear account creation failed flag on success
                setAccountCreationFailed(false);
                setAccountSetupIssue(null);

                // Final refresh to ensure UI updates
                await onOperationCompleteRef.current();

                setTimeout(() => {
                  setIsCreatingAccount(false);
                  setAccountCreationStep("");
                  accountCreationInProgressRef.current = false;
                }, 2000);
                return; // Exit the function successfully
              }
            } catch (error) {
              // If getAccountCore still reverts, account isn't ready yet
              if (
                error.code === "CALL_EXCEPTION" ||
                error.message?.includes("CALL_EXCEPTION")
              ) {
                // Account still doesn't exist, continue waiting
              } else {
                console.error(
                  "Error checking account activation status:",
                  error,
                );
              }
            }

            attempts++;
            setAccountCreationStep(
              `Waiting for account to be activated... (${attempts}/${maxAttempts}s)`,
            );
          }

          // If we get here, account creation is still pending
          setAccountCreationStep(
            "Account creation pending. You can proceed with deposit.",
          );
          //  showNotification('Account creation is pending. You can proceed with deposit.', 'info');

          // Refresh account status one more time
          await onOperationCompleteRef.current();

          setTimeout(() => {
            setIsCreatingAccount(false);
            setAccountCreationStep("");
            accountCreationInProgressRef.current = false;
          }, 2000);
        } else {
          // Account already exists — clear the creating state and refresh
          // parent account flags. Previously we only cleared the in-progress
          // ref and left isCreatingAccount=true, which permanently stuck the
          // CTA on "Creating account..." for users who already have an account.
          accountCreationInProgressRef.current = false;
          setIsCreatingAccount(false);
          setAccountCreationStep("");
          if (onOperationCompleteRef.current) {
            try {
              await onOperationCompleteRef.current();
            } catch (refreshError) {
              console.error(
                "Failed to refresh account status for existing account:",
                refreshError,
              );
            }
          }
        }
      } catch (error) {
        // Effect was torn down (chain switch / remount). Don't treat a rejected
        // wallet popup or mid-flight error as a permanent failure for the new run.
        if (isAborted()) {
          accountCreationAttemptedRef.current = false;
          accountCreationInProgressRef.current = false;
          accountCreationSessions.delete(sessionKey);
          setIsCreatingAccount(false);
          setAccountCreationStep("");
          return;
        }
        console.error(
          "Error checking/creating account after key derivation:",
          error,
        );
        console.error("Error details:", {
          message: error.message,
          code: error.code,
          data: error.data,
          stack: error.stack,
        });
        // Only show error notification if we were actually trying to create
        if (accountCreationAttemptedRef.current) {
          setAccountCreationStep("");
          // User closed/rejected the wallet popup — reset quietly so they can retry.
          if (!isUserRejectedError(error)) {
            const cleanError = getCleanErrorMessage(error);
            // Check if this is an RPC error and adjust message accordingly
            const errorMessage = error.message || String(error);
            const isRpcError =
              errorMessage.includes("HTTP request failed") ||
              errorMessage.includes("fetch failed") ||
              (errorMessage.includes("RPC") && !isFeeTooLowError(error)) ||
              errorMessage.includes("network") ||
              errorMessage.includes("connection") ||
              cleanError.includes("RPC connection error") ||
              cleanError.includes("RPC error");
            setAccountSetupIssue({
              title: "Couldn't finish setup",
              node: isRpcError
                ? "We couldn't reach the network just now. Check your connection and try again."
                : cleanError,
              severity: "error",
              cta: "Retry account setup",
            });
          } else {
            setAccountSetupIssue(null);
          }
          // Mark account creation as failed so "Get Keys" buttons show again
          setAccountCreationFailed(true);
          // Reset keysDerived so "Get Keys" buttons show again, allowing user to retry
          // This allows users to retry account creation after cancellation/failure
          setKeysDerived(false);
          setKeyDerivationConsent(false); // Reset consent so they can go through flow again
          // Reset the attempt flag so user can retry account creation
          accountCreationAttemptedRef.current = false;
          accountCreationInProgressRef.current = false;
          // IMPORTANT: allow retry on *failure*
          accountCreationSessions.delete(sessionKey);
        }
        setIsCreatingAccount(false);
      }
    };

    autoCreateAccount();

    // Cleanup function to cancel any pending operations if effect re-runs
    return () => {
      // Abort this run specifically — shared cancelled ref alone is not safe
      // because the next effect clears it while this async may still be live.
      effectAborted = true;
      accountCreationCancelledRef.current = true;
      // Release the in-flight claim so a remount/re-run (e.g. switching back
      // to Arb mainnet after testnet) can submit ensureAccount again. Leaving
      // the key in the set permanently skipped creation after a rejected or
      // underfunded first attempt.
      accountCreationSessions.delete(sessionKey);
      accountCreationAttemptedRef.current = false;
      accountCreationInProgressRef.current = false;
      // Always clear the CTA — otherwise a dep-change mid-flight leaves
      // "Creating account..." with no wallet popup and blocks the next run.
      setIsCreatingAccount(false);
      setAccountCreationStep("");
    };
  }, [
    keysDerived,
    keypair,
    // Intentionally depend on presence/address, not contract object identity.
    // switchToChain rebuilds `contract` before ensureAccount; that must not
    // abort this effect or the wallet popup never opens.
    Boolean(contract),
    userAccount,
    accountCreationFailed,
    selectedChainIdForKeys,
    verifyingContract,
    onboardingBlocksAutoSetup,
  ]);

  // Reset account creation attempt flag when account or chain changes
  useEffect(() => {
    accountCreationAttemptedRef.current = false;
    lastBalanceErrorShownRef.current = null; // Reset error tracking when account/chain changes
  }, [userAccount, selectedChainId]);

  // Reset isCreatingAccount state when account becomes active
  // This fixes the issue where the deposit button shows "Creating Account..."
  // even when the account already exists and is active
  useEffect(() => {
    if (hasActiveAccount && accountExists && isCreatingAccount) {
      setIsCreatingAccount(false);
      setAccountCreationStep("");
      accountCreationInProgressRef.current = false;
    }
  }, [hasActiveAccount, accountExists, isCreatingAccount]);

  // Refresh account status when account creation completes
  // This fixes the issue where account is created onchain but frontend state isn't updated
  const prevIsCreatingAccountRef = useRef(isCreatingAccount);
  useEffect(() => {
    const wasCreating = prevIsCreatingAccountRef.current;
    prevIsCreatingAccountRef.current = isCreatingAccount;

    // If account creation just completed (was creating, now not), refresh account status
    // Throttle to avoid excessive RPC calls (minimum 2 seconds between checks)
    if (wasCreating && !isCreatingAccount && onOperationCompleteRef.current) {
      const now = Date.now();
      const timeSinceLastCheck = now - lastAccountStatusCheckRef.current;
      const minInterval = 2000; // 2 seconds minimum between checks

      if (timeSinceLastCheck >= minInterval) {
        lastAccountStatusCheckRef.current = now;
        // Small delay to allow blockchain state to propagate
        setTimeout(() => {
          if (onOperationCompleteRef.current) {
            onOperationCompleteRef.current();
          }
        }, 1500);
      }
    }
  }, [isCreatingAccount]);

  // Check and refresh account status if it seems stale
  // This handles cases where account was created onchainbut state wasn't refreshed
  // Also handles cases where account exists but isn't finalized yet (hasActiveAccount = false)
  // Uses throttling and single-instance checks to avoid RPC overload
  useEffect(() => {
    if (!contract || !userAccount || !keysDerived) return;

    // If keys are derived but account is not active (either doesn't exist or isn't finalized),
    // check if account was actually created/finalized onchain
    // Only check if not already checking and not too soon since last check
    const now = Date.now();
    const timeSinceLastCheck = now - lastAccountStatusCheckRef.current;
    const minInterval = 3000; // 3 seconds minimum between checks

    // Check if account creation is done but account isn't active yet
    // This handles the case where account creation takes longer than expected
    const shouldCheck =
      !hasActiveAccount &&
      !isCreatingAccount &&
      !accountCreationFailed &&
      !accountStatusCheckInProgressRef.current &&
      timeSinceLastCheck >= minInterval;

    if (shouldCheck) {

      let timeoutId = null;
      let cancelled = false;
      let checkCount = 0;
      const initialIntervalMs = 8000; // 8 seconds for first ~80 seconds (10 checks)
      const checksBeforeExponentialBackoff = 10; // First 10 checks at 8s intervals
      let currentIntervalMs = initialIntervalMs;
      const startTime = Date.now();

      // Calculate interval with exponential backoff after 80 seconds
      const getNextInterval = (attemptNumber) => {
        if (attemptNumber <= checksBeforeExponentialBackoff) {
          // First ~80 seconds: fixed 8 second intervals
          return initialIntervalMs;
        } else {
          // After 80 seconds: exponential backoff starting at 16s
          // Attempt 11: 16s, 12: 32s, 13: 64s, 14: 128s, etc.
          const backoffAttempt = attemptNumber - checksBeforeExponentialBackoff;
          return initialIntervalMs * Math.pow(2, backoffAttempt);
        }
      };

      // Recursive function to perform periodic checks
      const scheduleNextCheck = async () => {
        if (cancelled || accountStatusCheckInProgressRef.current) {
          return;
        }

        // Throttle checks
        const now = Date.now();
        if (now - lastAccountStatusCheckRef.current < minInterval) {
          // Reschedule with remaining time
          const remainingTime =
            minInterval - (now - lastAccountStatusCheckRef.current);
          timeoutId = setTimeout(scheduleNextCheck, remainingTime);
          return;
        }

        accountStatusCheckInProgressRef.current = true;
        lastAccountStatusCheckRef.current = now;
        checkCount++;
        const elapsedSeconds = Math.floor((now - startTime) / 1000);
        const nextInterval = getNextInterval(checkCount + 1);
        const nextIntervalSeconds = Math.floor(nextInterval / 1000);

        if (checkCount <= checksBeforeExponentialBackoff) {
        } else {
        }

        try {
          // Directly check the contract to see if account exists and is finalized
          const accountDataTuple = await contract.getAccountCore(userAccount);
          const exists = accountDataTuple[0];
          const finalized = accountDataTuple[1];
          const isActive = exists && finalized;

          // If account is active (exists AND finalized), refresh the UI state
          if (isActive && !cancelled && onOperationCompleteRef.current) {
            await onOperationCompleteRef.current();
            accountStatusCheckInProgressRef.current = false;
            return; // Stop checking - account is active
          } else if (exists && !finalized) {
          } else if (!exists) {
          }

          // Schedule next check with appropriate interval
          accountStatusCheckInProgressRef.current = false;
          currentIntervalMs = getNextInterval(checkCount + 1);
          if (!cancelled) {
            timeoutId = setTimeout(scheduleNextCheck, currentIntervalMs);
          }
        } catch (error) {
          accountStatusCheckInProgressRef.current = false;
          if (cancelled) return;

          // If account doesn't exist yet, that's fine - we'll keep checking
          if (
            error.code === "CALL_EXCEPTION" ||
            error.message?.includes("CALL_EXCEPTION")
          ) {
          } else {
            console.error("Error during periodic account status check:", error);
          }

          // Schedule next check even on error
          currentIntervalMs = getNextInterval(checkCount + 1);
          if (!cancelled) {
            timeoutId = setTimeout(scheduleNextCheck, currentIntervalMs);
          }
        }
      };

      // Throttled immediate check
      const immediateCheck = async () => {
        if (accountStatusCheckInProgressRef.current || cancelled) return;

        accountStatusCheckInProgressRef.current = true;
        lastAccountStatusCheckRef.current = Date.now();

        try {
          const accountDataTuple = await contract.getAccountCore(userAccount);
          const exists = accountDataTuple[0];
          const finalized = accountDataTuple[1];
          const isActive = exists && finalized;

          if (isActive && !cancelled && onOperationCompleteRef.current) {
            await onOperationCompleteRef.current();
            accountStatusCheckInProgressRef.current = false;
            return; // Account is active, no need for periodic checks
          } else if (exists && !finalized) {
          } else {
          }
        } catch (error) {
          if (cancelled) {
            accountStatusCheckInProgressRef.current = false;
            return;
          }
          // If account doesn't exist yet, that's fine - we'll keep checking
          if (
            error.code === "CALL_EXCEPTION" ||
            error.message?.includes("CALL_EXCEPTION")
          ) {
          } else {
            console.error(
              "Error during immediate account status check:",
              error,
            );
          }
        }

        // If immediate check didn't find active account, set up periodic checks
        if (cancelled) {
          accountStatusCheckInProgressRef.current = false;
          return;
        }

        accountStatusCheckInProgressRef.current = false;

        // Schedule first periodic check
        timeoutId = setTimeout(scheduleNextCheck, initialIntervalMs);
      };

      // Delay immediate check slightly to avoid race conditions
      const initialTimeoutId = setTimeout(() => {
        immediateCheck();
      }, 500);

      return () => {
        cancelled = true;
        clearTimeout(initialTimeoutId);
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        accountStatusCheckInProgressRef.current = false;
      };
    }
  }, [
    contract,
    userAccount,
    keysDerived,
    accountExists,
    hasActiveAccount,
    isCreatingAccount,
    accountCreationFailed,
  ]);

  // Show consent modal when keys need to be derived (e.g., after chain change)
  // But actual wallet signing only happens when user clicks "Get Keys"
  useEffect(() => {
    // Only auto-show modal if user hasn't explicitly cancelled AND account creation hasn't failed
    // But allow explicit triggers via triggerKeyDerivation() to always show
    // Also require that user has explicitly selected both chain and token
    // Onboarding is its own consent step: never pop this modal over it, or
    // before it's known whether onboarding applies.
    if (
      onboardingPhase === "off" &&
      needsConsent &&
      !keyDerivationConsent &&
      !showConsentModal &&
      !userCancelledKeyDerivation &&
      !accountCreationFailed &&
      selectedChainId &&
      selectedTokenId
    ) {
      // Explicitly ensure consent is false before showing modal
      setKeyDerivationConsent(false);
      setShowConsentModal(true);
    } else if (keysDerived && showConsentModal) {
      // Close modal when keys are successfully derived
      setShowConsentModal(false);
    }
  }, [
    needsConsent,
    keyDerivationConsent,
    keysDerived,
    showConsentModal,
    userCancelledKeyDerivation,
    accountCreationFailed,
    selectedChainId,
    selectedTokenId,
    onboardingPhase,
  ]);

  // Reset consent when account or chain changes
  // This MUST run before key derivation attempts
  useEffect(() => {
    setKeyDerivationConsent(false);
    setKeysDerived(false);
    setShowConsentModal(false); // Ensure modal is hidden when switching chains
    setUserCancelledKeyDerivation(false); // Reset cancellation flag when account/chain changes
    setAccountCreationFailed(false); // Reset account creation failed flag when account/chain changes
    setAccountSetupIssue(null); // Funding notice is chain/token-specific — drop it on switch
    lastBalanceErrorShownRef.current = null;

    // Check if this wallet+account combination has been connected before
    // Use synchronous checks to ensure this completes before modal is shown
    if (userAccount && selectedChainIdForKeys && verifyingContract) {
      // Synchronous check - no async needed since the cache service reads browser storage
      const hasBeenConnected = secureKeyCache.hasBeenConnectedBefore(
        userAccount,
        selectedChainIdForKeys,
        verifyingContract,
      );
      // Also check if keys are cached (which means they've connected before)
      const hasCachedKeys = secureKeyCache.hasCachedKeysSync(
        userAccount,
        selectedChainIdForKeys,
        verifyingContract,
      );
      const isFirstTime = !hasBeenConnected && !hasCachedKeys;
      setIsFirstTimeConnection(isFirstTime);

    } else {
      setIsFirstTimeConnection(true); // Default to first time if we don't have all required info
    }
  }, [userAccount, selectedChainId, selectedChainIdForKeys, verifyingContract]);

  // Drop post-submit status/receipt UI when chain, token, or tab changes —
  // the confirmation belongs to that prior context; returning to a tab
  // should show the form so the user can run another operation.
  useEffect(() => {
    setTransferStatus("idle");
    setTransferReceiptData(null);
    setDepositStatus("idle");
    setDepositReceiptData(null);
    setWithdrawStatus("idle");
    setWithdrawReceiptData(null);
    setReceiptTab(null);
    setReceiptShared(false);
  }, [selectedChainId, selectedTokenId, activeTab]);

  // Handlers for consent modal
  const handleKeyDerivationConsent = () => {
    setKeyDerivationConsent(true);
    // Don't close modal yet - let it close automatically when keys are derived
  };

  const handleKeyDerivationCancel = () => {
    setKeyDerivationConsent(false);
    setShowConsentModal(false);
    setUserCancelledKeyDerivation(true); // Track that user explicitly cancelled
    // Don't show notification - user can still click "Get Keys" buttons to retry
    // showNotification('Key derivation cancelled. You will need to approve to use confidential features.', 'info');
  };

  // Function to trigger key derivation
  const triggerKeyDerivation = () => {
    // A wallet with no account sets up through onboarding.
    if (onboardingPhase === "skipped" || onboardingPhase === "open") {
      openOnboarding();
      return;
    }
    if (!keysDerived || accountCreationFailed) {
      // Reset cancellation flag, consent state, and account creation failed flag when user explicitly triggers
      setUserCancelledKeyDerivation(false);
      setAccountCreationFailed(false); // Clear failed flag so buttons work correctly
      setAccountSetupIssue(null); // Clear stale funding notice — this retry re-checks
      lastBalanceErrorShownRef.current = null;
      setKeyDerivationConsent(false); // Reset consent to ensure fresh flow
      setShowConsentModal(true); // Force show modal when user explicitly requests it
    }
  };

  // --- Onboarding ------------------------------------------------------------

  // Whether this wallet has an account on the selected chain, read straight
  // from that chain's RPC (not App's accountData) so a network switch can
  // never show the previous chain's answer. Polls while one is on its way.
  useEffect(() => {
    if (!userAccount || onboardingPhase === "off") return undefined;
    const cfg = CHAINS[selectedChainId];
    if (!cfg?.contractAddress || !cfg?.rpcUrl) {
      if (onboardingPhase === "checking") setOnboardingPhase("off");
      return undefined;
    }

    let cancelled = false;
    let timerId = null;
    const reader = new ethers.Contract(
      cfg.contractAddress,
      CONTRACT_ABI,
      new ethers.providers.StaticJsonRpcProvider(cfg.rpcUrl),
    );

    const readAccountState = async () => {
      try {
        const core = parseAccountCore(await reader.getAccountCore(userAccount));
        if (!core?.exists) return "missing";
        return core.finalized ? "active" : "pending";
      } catch (error) {
        // Same convention as the auto-create check: an empty revert from the
        // selected chain's own contract means no account there.
        return isCallException(error) ? "missing" : null;
      }
    };

    const tick = async () => {
      const state = await readAccountState();
      if (cancelled) return;
      setOnboardingAccountState(state);

      if (onboardingPhase === "checking") {
        if (state === "missing") {
          setOnboardingPhase(
            isOnboardingSkipped(userAccount) ? "skipped" : "open",
          );
        } else {
          // Has an account (or already submitted one), or the read failed.
          setOnboardingPhase("off");
        }
        return;
      }
      if (onboardingPhase === "skipped" && state && state !== "missing") {
        setOnboardingPhase("off");
        return;
      }
      if (onboardingPhase === "open") {
        // Switched to a network where this wallet already has an account.
        if (state === "active" && !onboardingAttempted) {
          setOnboardingPhase("off");
          return;
        }
        if (
          state === "pending" ||
          (onboardingCreateRequested && state !== "active")
        ) {
          timerId = setTimeout(tick, 3000);
        }
      }
    };

    tick();
    return () => {
      cancelled = true;
      if (timerId) clearTimeout(timerId);
    };
  }, [
    userAccount,
    selectedChainId,
    onboardingPhase,
    onboardingCreateRequested,
    onboardingAttempted,
  ]);

  // A failed sign / create attempt becomes an inline onboarding error, and
  // control goes back to its "Try again" button.
  useEffect(() => {
    if (onboardingPhase !== "open" || !onboardingCreateRequested) return;
    if (keyError) {
      setOnboardingSetupError({
        kind: "signature",
        message:
          "The signature request was cancelled or didn't go through. Try again when you're ready.",
      });
      setOnboardingCreateRequested(false);
      // Flipping consent back on is what re-runs derivation on retry.
      setKeyDerivationConsent(false);
      return;
    }
    if (!accountCreationFailed) return;
    const chainConfig = CHAINS[selectedChainId];
    let error;
    if (accountSetupIssue?.hideNotice) {
      // The auto-create balance check found no gas.
      const feeLabel =
        selectedChainId === "tempo"
          ? "fee tokens"
          : chainConfig?.nativeCurrency?.symbol || "ETH";
      error = {
        kind: "funds",
        message: `Not enough ${feeLabel} to pay the network fee on ${chainConfig?.name || "this network"}. Add some and try again.`,
      };
    } else if (accountSetupIssue?.node) {
      error = { kind: "transaction", message: accountSetupIssue.node };
    } else {
      // Failure without an issue = the wallet request was rejected.
      error = {
        kind: "transaction",
        message: "The transaction was cancelled in your wallet.",
      };
    }
    setOnboardingSetupError(error);
    setOnboardingCreateRequested(false);
  }, [
    keyError,
    accountCreationFailed,
    accountSetupIssue,
    onboardingPhase,
    onboardingCreateRequested,
    selectedChainId,
  ]);

  // Setup errors and attempts belong to the network they happened on.
  // Re-enter 'checking' only from 'off': that phase early-returns in the
  // probe effect, so a switch onto a chain with no account would otherwise
  // never reopen onboarding. 'open' / 'skipped' / 'checking' already react
  // to selectedChainId, so forcing checking there would just remount the modal.
  useEffect(() => {
    setOnboardingSetupError(null);
    setOnboardingAttempted(false);
    setOnboardingAccountState(null);
    setOnboardingCreateRequested(false);
    if (!userAccount) {
      setOnboardingPhase("off");
      return;
    }
    setOnboardingPhase((phase) => (phase === "off" ? "checking" : phase));
  }, [userAccount, selectedChainId]);

  const openOnboarding = () => {
    setOnboardingSkipped(userAccount, false);
    setShowConsentModal(false);
    setOnboardingPhase("open");
  };

  const skipOnboarding = () => {
    setOnboardingSkipped(userAccount, true);
    setShowTokenSelector(false);
    setOnboardingPhase("skipped");
  };

  const startOnboardingSetup = () => {
    setOnboardingSetupError(null);
    setOnboardingAttempted(true);
    setUserCancelledKeyDerivation(false);
    setAccountCreationFailed(false);
    setAccountSetupIssue(null);
    lastBalanceErrorShownRef.current = null;
    setOnboardingCreateRequested(true);
    // Cached key: straight to the create transaction. Otherwise consent
    // starts derivation, which is the wallet signature.
    if (keypair) {
      setKeysDerived(true);
    } else {
      setKeyDerivationConsent(true);
    }
  };

  const finishOnboarding = () => {
    setOnboardingSkipped(userAccount, false);
    setOnboardingPhase("off");
    setOnboardingCreateRequested(false);
    setShowTokenSelector(false);
    setActiveTab("deposit");
    onOperationCompleteRef.current?.();
  };

  // Where account creation is, in onboarding's terms. accountCreationStep is
  // the auto-create flow's own progress text; "Waiting for…" starts once the
  // create transaction is mined.
  const onboardingSetupStage =
    onboardingAccountState === "active" && onboardingAttempted
      ? "done"
      : onboardingAccountState === "pending" ||
          accountCreationStep.startsWith("Waiting for") ||
          accountCreationStep === "Account created successfully!"
        ? "activating"
        : !onboardingCreateRequested
          ? "idle"
          : isCreatingAccount || (keypair && keysDerived)
            ? "confirming"
            : "signing";

  // Onboarding skipped and still no account: every form CTA leads back into
  // setup instead of a dead "Creating account...".
  const needsOnboardingSetup = !!userAccount && onboardingPhase === "skipped";
  const SETUP_CTA_LABEL = "Set up your account";

  const tokenSelectorTitle =
    onboardingPhase === "open"
      ? "Choose a network"
      : activeTab === "transfer"
        ? "Select confidential token to transfer"
        : activeTab === "deposit"
          ? "Select token to deposit"
          : activeTab === "withdraw"
            ? "Select confidential token to withdraw"
            : "Select token";
  const tokenSelectorShowConfidential =
    onboardingPhase !== "open" &&
    (activeTab === "transfer" || activeTab === "withdraw");

  // Get token data from chain configuration
  // Helper function to check if a token address is valid
  const isValidTokenAddress = (address) => {
    if (!address) return false;
    const addr = address.trim().toLowerCase();
    // Check if it's empty or zero address
    if (addr === "" || addr === "0x0000000000000000000000000000000000000000")
      return false;
    // Check if it's a valid Ethereum address format
    return /^0x[a-fA-F0-9]{40}$/.test(addr);
  };

  const getTokenData = () => {
    // Always resolve from CHAINS via selectedChainId + selectedTokenId.
    // selectedTokenData can lag one render behind a token switch and would
    // make balanceOf hit the previous token (e.g. USDC) while the pill
    // already shows USDT.
    if (!selectedChainId || !selectedTokenId) {
      return null;
    }

    const chain = CHAINS[selectedChainId];
    if (chain && chain.tokens[selectedTokenId]) {
      return {
        ...chain.tokens[selectedTokenId],
        chainId: selectedChainId,
        chainName: chain.name,
        chainLogo: chain.icon,
        chainColor: chain.color,
      };
    }

    return null;
  };

  const tokenData = getTokenData();
  const parseAmountForContract = (amountStr) => {
    const numeric = parseFloat(amountStr);
    if (!Number.isFinite(numeric) || numeric <= 0) return 0;
    return Math.floor(numeric * 100);
  };

  useEffect(() => {
    transferProofPreviewCacheRef.current.clear();
    withdrawProofPreviewCacheRef.current.clear();
  }, [
    selectedChainId,
    selectedTokenId,
    userAccount,
    confidentialBalance,
    keypair?.private_key,
  ]);

  // Real-time gas breakdown: refetch when panel opens and every 10s (refs keep amount/token current)
  useEffect(() => {
    if (!showGasBreakdown || !gasBreakdownOperation) return;
    const getBreakdownOpts = () => {
      if (!tokenData?.address) return {};
      if (gasBreakdownOperation === "transfer") {
        const trimmedRecipient = (recipient || "").trim();
        const amount = parseAmountForContract(transferAmountRef.current);
        return {
          tokenAddress: tokenData.address,
          recipient: trimmedRecipient,
          amountForContract: amount,
        };
      }
      if (gasBreakdownOperation === "deposit") {
        const amount = parseAmountForContract(depositAmountRef.current);
        return { tokenAddress: tokenData.address, amountForContract: amount };
      }
      if (gasBreakdownOperation === "withdraw") {
        const amount = parseAmountForContract(withdrawAmountRef.current);
        return { tokenAddress: tokenData.address, amountForContract: amount };
      }
      return {};
    };
    fetchGasBreakdown(gasBreakdownOperation, getBreakdownOpts());
    const intervalId = setInterval(() => {
      fetchGasBreakdown(gasBreakdownOperation, getBreakdownOpts());
    }, 10000);
    return () => clearInterval(intervalId);
  }, [showGasBreakdown, gasBreakdownOperation, tokenData?.address, recipient]);

  // When deposit amount changes and breakdown is open, debounce refetch
  useEffect(() => {
    if (
      !showGasBreakdown ||
      gasBreakdownOperation !== "transfer" ||
      !tokenData?.address
    )
      return;
    const t = setTimeout(() => {
      const trimmedRecipient = (recipient || "").trim();
      const amount = parseAmountForContract(transferAmountRef.current);
      fetchGasBreakdown("transfer", {
        tokenAddress: tokenData.address,
        recipient: trimmedRecipient,
        amountForContract: amount,
      });
    }, 500);
    return () => clearTimeout(t);
  }, [
    transferAmount,
    recipient,
    showGasBreakdown,
    gasBreakdownOperation,
    tokenData?.address,
  ]);

  // When deposit amount changes and breakdown is open, debounce refetch
  useEffect(() => {
    if (
      !showGasBreakdown ||
      gasBreakdownOperation !== "deposit" ||
      !tokenData?.address
    )
      return;
    const t = setTimeout(() => {
      const amount = parseAmountForContract(depositAmountRef.current);
      fetchGasBreakdown("deposit", {
        tokenAddress: tokenData.address,
        amountForContract: amount,
      });
    }, 500);
    return () => clearTimeout(t);
  }, [
    depositAmount,
    showGasBreakdown,
    gasBreakdownOperation,
    tokenData?.address,
  ]);

  // When withdraw amount changes and breakdown is open, debounce refetch
  useEffect(() => {
    if (
      !showGasBreakdown ||
      gasBreakdownOperation !== "withdraw" ||
      !tokenData?.address
    )
      return;
    const t = setTimeout(() => {
      const amount = parseAmountForContract(withdrawAmountRef.current);
      fetchGasBreakdown("withdraw", {
        tokenAddress: tokenData.address,
        amountForContract: amount,
      });
    }, 500);
    return () => clearTimeout(t);
  }, [
    withdrawAmount,
    showGasBreakdown,
    gasBreakdownOperation,
    tokenData?.address,
  ]);

  const getSecureClaimKey = () => {
    const t = getTokenData();
    if (!userAccount || !t?.chainId || !t?.address) return null;
    const address =
      typeof t.address === "string" ? t.address : String(t.address || "");
    const account =
      typeof userAccount === "string" ? userAccount : String(userAccount || "");
    return `${account.toLowerCase()}::${t.chainId}::${address.toLowerCase()}`;
  };

  const formatTokenAmount = (value, decimals = 6) => {
    if (value === null || value === undefined) return "";
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return "";
    if (numericValue === 0) return "0";

    // If value is very small and rounds to zero with the given decimals,
    // find the first significant digit and show at least one digit
    const fixed = numericValue.toFixed(decimals);
    const trimmed = fixed.replace(/\.?0+$/, "");

    // If trimmed is empty or '0', but original value is > 0, find first significant digit
    if ((trimmed === "" || trimmed === "0") && numericValue > 0) {
      // Find the position of the first non-zero digit after the decimal point
      const valueStr = numericValue.toString();
      if (valueStr.includes("e")) {
        // Handle scientific notation (e.g., 1e-7)
        const [base, exp] = valueStr.split("e");
        const exponent = parseInt(exp, 10);
        if (exponent < 0) {
          // For very small numbers, show at least one significant digit
          const baseNum = parseFloat(base);
          const firstDigit = Math.floor(Math.abs(baseNum) * 10) % 10;
          const decimalPlaces = Math.abs(exponent) + (baseNum < 1 ? 1 : 0);
          return numericValue.toFixed(Math.min(decimalPlaces, 20));
        }
      } else {
        // Find first non-zero digit position
        const absValue = Math.abs(numericValue);
        if (absValue < 1) {
          // Count leading zeros after decimal point
          const str = absValue.toString();
          if (str.includes(".")) {
            const decimalPart = str.split(".")[1];
            if (decimalPart) {
              // Find first non-zero digit
              let firstNonZero = -1;
              for (let i = 0; i < decimalPart.length; i++) {
                if (decimalPart[i] !== "0") {
                  firstNonZero = i;
                  break;
                }
              }
              if (firstNonZero >= 0) {
                // Show at least one digit after the first non-zero
                const totalDecimals = firstNonZero + 1;
                return numericValue.toFixed(Math.min(totalDecimals, 20));
              }
            }
          }
        }
      }
      // Fallback: use more decimal places to show at least one digit
      return numericValue.toFixed(Math.min(decimals + 10, 20));
    }

    return trimmed === "" ? "0" : trimmed;
  };

  const isValidRecipientAddress = (value) => /^0x[a-fA-F0-9]{40}$/.test(value);

  const SELF_TRANSFER_ERROR = "Cannot transfer to yourself";
  const NO_RECIPIENT_ACCOUNT_ERROR =
    "Recipient does not have a confidential account";

  const isSelfTransfer = (address) =>
    !!(
      userAccount &&
      address &&
      address.toLowerCase() === userAccount.toLowerCase()
    );

  // Dev-only, so the "no account" invite UI can be exercised without a real
  // second wallet. Gated behind the same env check as every other dev bypass
  // in this file — never active against a real Privy app.
  const DEV_FORCE_NO_ACCOUNT_ADDRESSES = new Set([
    "0x4c99cb95545e1f91c62910c144d32f7e44b923bd",
  ]);

  // Check if recipient has a Stabletrust account on the *UI-selected* chain.
  // Must not use the wallet-bound `contract`: after Mainnet↔Testnet the wallet
  // provider can still be on the previous network, getAccountCore then throws
  // CALL_EXCEPTION, and we used to treat that as "no account" — fixed by a
  // reload once the wallet caught up. Same selected-chain reader pattern as
  // balance / auto-create / onboarding.
  const checkRecipientAccount = async (address) => {
    if (!address || !isValidRecipientAddress(address)) {
      setRecipientAccountStatus(null);
      return;
    }

    const reader = getSelectedChainReadContract();
    if (!reader) {
      setRecipientAccountStatus(null);
      return;
    }

    if (
      !process.env.REACT_APP_PRIVY_APP_ID &&
      DEV_FORCE_NO_ACCOUNT_ADDRESSES.has(address.toLowerCase())
    ) {
      setRecipientAccountStatus("no_account");
      return;
    }

    const checkSeq = ++recipientCheckSeqRef.current;
    setRecipientAccountStatus("checking");

    try {
      const recipientAccountTuple = await reader.getAccountCore(address);
      // Drop stale results: either the field changed, or a newer check for
      // the same address already started (e.g. after selectedChainId updated).
      if (
        checkSeq !== recipientCheckSeqRef.current ||
        latestRecipientAddressRef.current !== address
      ) {
        return;
      }
      const hasActiveAccount =
        recipientAccountTuple[0] && recipientAccountTuple[1]; // exists && finalized

      setRecipientAccountStatus(
        hasActiveAccount ? "has_account" : "no_account",
      );
      setRecipientError(
        hasActiveAccount ? null : NO_RECIPIENT_ACCOUNT_ERROR,
      );
    } catch (error) {
      if (
        checkSeq !== recipientCheckSeqRef.current ||
        latestRecipientAddressRef.current !== address
      ) {
        return;
      }
      // Match prior production behavior: any failed read → treat as no
      // account (invite CTA). With the selected-chain reader above, this
      // is no longer tripped by wallet mainnet↔testnet lag; true missing
      // accounts still land here when the RPC reverts, or via the
      // exists/finalized=false success path.
      setRecipientAccountStatus("no_account");
      setRecipientError(NO_RECIPIENT_ACCOUNT_ERROR);
    }
  };

  const handleRecipientChange = (value) => {
    setRecipient(value);
    const trimmed = value.trim();

    // Tracks the address this field currently holds, so a contract call
    // that's still in flight when the user edits the field again can tell
    // its result is stale (checked in checkRecipientAccount above) —
    // repurposed from a setTimeout-id ref (see note below).
    latestRecipientAddressRef.current = trimmed;

    if (!trimmed) {
      setRecipientError(null);
      setRecipientAccountStatus(null);
      return;
    }

    if (!isValidRecipientAddress(trimmed)) {
      setRecipientError("Invalid address");
      setRecipientAccountStatus(null);
      return;
    }

    if (isSelfTransfer(trimmed)) {
      setRecipientError(SELF_TRANSFER_ERROR);
      setRecipientAccountStatus(null);
      return;
    }

    setRecipientError(null);

    // This used to debounce via setTimeout(..., 500) to avoid a contract
    // call on every keystroke. Removed: setTimeout callbacks scheduled
    // from inside a React event-handler execution context don't reliably
    // fire in this sandbox (the same class of bug documented earlier for
    // the transfer/deposit "minimum pending duration" and the receipt
    // canvas download) — confirmed here too: the timer was scheduled
    // correctly every time, it just never went off, so this check
    // silently never ran and every address looked "fine" by default.
    // Calling it directly has no such failure mode.
    checkRecipientAccount(trimmed);
  };

  // Re-check when selected-chain reader / network becomes ready while a
  // recipient is already entered (wallet `contract` can still be lagged).
  useEffect(() => {
    const trimmed = (recipient || "").trim();
    if (
      !trimmed ||
      !isValidRecipientAddress(trimmed) ||
      isSelfTransfer(trimmed) ||
      !getSelectedChainReadContract()
    ) {
      return;
    }
    checkRecipientAccount(trimmed);
  }, [contract, selectedChainId]);

  // Faucet helpers (env-driven)
  const getFaucetEnvVarName = () => {
    const chainKey = (CHAINS[selectedChainId]?.name || selectedChainId || "")
      .toUpperCase()
      .replace(/\s+/g, "_");
    const symbolKey = (getTokenData().symbol || "").toUpperCase();
    return `REACT_APP_${chainKey}_${symbolKey}_FAUCET_ADDRESS`;
  };

  const getFaucetAddressForSelection = () => {
    const envKey = getFaucetEnvVarName();
    // Access environment variable in CRA:
    // eslint-disable-next-line no-undef
    const fromEnv = process.env[envKey];
    return fromEnv || getTokenData().faucetAddress || null;
  };

  const handleGetTestTokens = async () => {
    try {
      if (!userAccount) {
        showNotification(
          "Wallet not connected. Please connect your wallet",
          "error",
        );
        return;
      }
      if (!contract?.signer) {
        showNotification("Signer not ready. Try again.", "error");
        return;
      }
      const faucetAddr = getFaucetAddressForSelection();
      if (!faucetAddr) {
        showNotification(
          `Test token faucet is not available for ${getTokenData().symbol} on this network.`,
          "error",
        );
        return;
      }

      setIsFaucetProcessing(true);
      // Removed "Requesting test tokens..." notification - faucet action is already clear

      // Check if faucet is a web URL (like Circle's faucet) or a contract address
      if (faucetAddr.startsWith("http")) {
        // Web-based faucet (like Circle's)
        showNotification(
          `Please visit ${faucetAddr} to claim test ${getTokenData().symbol}. Select Ethereum Sepolia and enter your address: ${userAccount}`,
          "info",
        );

        // Open the faucet in a new tab
        const opened = window.open(faucetAddr, "_blank", "noopener,noreferrer");
        if (opened) opened.opener = null;

        // Show instructions
        setTimeout(() => {
          showNotification(
            "After claiming tokens from the web faucet, refresh this page to see your balance.",
            "info",
          );
        }, 2000);

        setIsFaucetProcessing(false);
        return;
      }

      // Contract-based faucet
      // Try different faucet patterns
      // Pattern 1: claim() - simple faucet
      // Pattern 2: mint(address) - mint to address
      // Pattern 3: drip() - drip faucet
      const faucetABI = [
        "function claim() external",
        "function mint(address to, uint256 amount) external",
        "function drip() external",
        "function requestTokens() external",
        "function getTokens() external",
      ];

      const faucet = new ethers.Contract(
        faucetAddr,
        faucetABI,
        contract.signer,
      );

      let tx;
      let method = "claim";

      try {
        // Try claim() first (most common)
        tx = await faucet.claim();
      } catch (claimErr) {

        try {
          // Try drip()
          method = "drip";
          tx = await faucet.drip();
        } catch (dripErr) {
          try {
            // Try requestTokens()
            method = "requestTokens";
            tx = await faucet.requestTokens();
          } catch (requestErr) {
            try {
              // Try getTokens()
              method = "getTokens";
              tx = await faucet.getTokens();
            } catch (getErr) {
              // All methods failed, throw the original error
              throw claimErr;
            }
          }
        }
      }

      await tx.wait();
      showNotification(
        "Faucet claim successful! Tokens sent to your wallet.",
        "success",
      );

      // Refresh displayed ERC20 wallet balance
      await updateTokenBalance();

      // Refresh account data to clear any pending action state
      await onOperationComplete();
    } catch (err) {
      console.error("Faucet claim failed:", err);

      // Parse error message
      let errorMsg = "Faucet claim failed";

      // Check for common error codes
      if (err?.code === "UNPREDICTABLE_GAS_LIMIT") {
        const errorData = err?.error?.data || err?.data;

        // Try to decode common error selectors
        if (errorData === "0x3db2a12a") {
          errorMsg =
            "Already claimed recently. Please wait before claiming again (cooldown period).";
        } else if (errorData === "0x" || !errorData) {
          errorMsg =
            "Faucet transaction would fail. The faucet may be empty or you may have already claimed.";
        } else {
          errorMsg =
            "Faucet claim failed. You may need to wait before claiming again, or the faucet may be empty.";
        }
      } else if (isUserRejectedError(err)) {
        // User closed/rejected the wallet popup — no error toast.
        return;
      } else if (err?.message?.includes("insufficient funds")) {
        const faucetChain = CHAINS[selectedChainId];
        errorMsg = insufficientFundsMessage({
          isMainnet: faucetChain?.network === "mainnet",
          kind: "gas",
          nativeSymbol: faucetChain?.nativeCurrency?.symbol || "ETH",
          chainName: faucetChain?.name || "this network",
        });
      } else {
        // Use error parser for clean error messages
        errorMsg = getCleanErrorMessage(err);
      }

      // Persistent notification for insufficient funds, regular for other errors
      showNotification(
        errorMsg,
        "error",
        err?.message?.includes("insufficient funds"),
      );
    } finally {
      setIsFaucetProcessing(false);
    }
  };

  const handleChainSelect = async (chainId) => {
    // Mainnet/testnet siblings are separate configs — keep the mode toggle
    // aligned with the chain that was actually selected.
    const chainNetwork = getNetworkModeForChain(chainId);
    if (chainNetwork && chainNetwork !== networkMode) {
      setNetworkMode(chainNetwork);
    }

    // Clear all input fields when switching chains
    setRecipient("");
    setRecipientError(null);
    setRecipientAccountStatus(null);
    setTransferAmount("");
    setTransferAmountError("");
    setDepositAmount("");
    setDepositAmountError("");
    setWithdrawAmount("");
    setWithdrawAmountError("");

    // Cancel any in-flight auto account-creation from the previous chain so
    // the CTA never sticks on "Creating account..." after a mainnet↔testnet
    // flip where no create tx was submitted.
    accountCreationCancelledRef.current = true;
    accountCreationAttemptedRef.current = false;
    accountCreationInProgressRef.current = false;
    setIsCreatingAccount(false);
    setAccountCreationStep("");
    setAccountCreationFailed(false);
    // Drop prior session keys for this wallet so the new chain can run a
    // fresh exists-check (keyed by chainId + verifyingContract).
    if (userAccount) {
      const prefix = `${userAccount.toLowerCase()}::`;
      for (const key of Array.from(accountCreationSessions)) {
        if (key.startsWith(prefix)) {
          accountCreationSessions.delete(key);
        }
      }
    }

    // IMMEDIATELY reset consent to prevent race conditions
    // This must happen synchronously BEFORE any async operations
    setKeyDerivationConsent(false);
    setKeysDerived(false);
    setShowConsentModal(false);

    // Update loading refs immediately to prevent race conditions
    loadingChainRef.current = chainId;
    // Note: loadingTokenRef will be updated when we determine the new token below

    // Reset loading flag to allow new load after chain change
    loadingPendingBalanceRef.current = false;

    // Save current balances to cache before switching (if we have balances for current chain+token)
    if (
      selectedChainId &&
      selectedTokenId &&
      (confidentialBalance !== null || pendingBalance !== null)
    ) {
      const cacheKey = `${selectedChainId}_${selectedTokenId}`;
      balanceCacheRef.current.set(cacheKey, {
        available: confidentialBalance,
        pending: pendingBalance,
      });
    }

    // Always clear displayed balances until the new chain is confirmed and
    // its own keys/balance load finish — otherwise a late Sepolia decrypt
    // can paint the testnet amount onto the Arbitrum One pill.
    justRestoredFromCacheRef.current = false;
    lastLoadedKeyRef.current = null;
    setPendingBalance(null);
    setConfidentialBalance(null);

    // Get the token that will be selected on the new chain (we'll determine this below)
    const newChainConfig = CHAINS[chainId];
    let newTokenId = null;
    if (newChainConfig && newChainConfig.tokens) {
      const chainTokens = Object.keys(newChainConfig.tokens);
      const isValidTokenAddress = (address) => {
        if (!address) return false;
        const addr = address.trim().toLowerCase();
        if (
          addr === "" ||
          addr === "0x0000000000000000000000000000000000000000"
        )
          return false;
        return /^0x[a-fA-F0-9]{40}$/.test(addr);
      };
      const activeTokens = chainTokens.filter((tokenId) => {
        const token = newChainConfig.tokens[tokenId];
        return isValidTokenAddress(token.address);
      });
      // Only select from active tokens - never fallback to inactive tokens
      if (activeTokens.length > 0) {
        newTokenId = activeTokens.includes(selectedTokenId)
          ? selectedTokenId
          : activeTokens[0];
      }
      // If no active tokens, newTokenId remains null and we won't select any token
    }

    // Pin race guards before any await so a late decrypt from the previous
    // chain (e.g. Sepolia) cannot write into the new selection.
    if (newTokenId) {
      loadingTokenRef.current = newTokenId;
    }

    if (chainId !== selectedChainId && switchToChain) {
      const switched = await switchToChain(chainId);

      if (!switched) {
        // Network switch failed or was cancelled — leave balances cleared;
        // caller can re-select the previous chain.
        return;
      }

      // Removed duplicate "Switched to..." notification - chain change is already handled by App.js
    }

    // Do not restore cached balances across a chain switch. A late Sepolia
    // decrypt used to write into the wrong cache key / UI; always clear and
    // wait for a fresh load with the new chain's keys.
    justRestoredFromCacheRef.current = false;
    lastLoadedKeyRef.current = null;
    setPendingBalance(null);
    setConfidentialBalance(null);

    // Update selected chain ID
    setSelectedChainId(chainId);

    // Helper function to check if a token address is valid
    const isValidTokenAddress = (address) => {
      if (!address) return false;
      const addr = address.trim().toLowerCase();
      if (addr === "" || addr === "0x0000000000000000000000000000000000000000")
        return false;
      return /^0x[a-fA-F0-9]{40}$/.test(addr);
    };

    // Update selected token to a default token from the new chain
    // Use newChainConfig that was already determined above
    // Only select active tokens - never select inactive tokens
    if (newChainConfig && newChainConfig.tokens && newTokenId) {
      const chainTokens = Object.keys(newChainConfig.tokens);
      if (chainTokens.length > 0) {
        // Find active tokens (tokens with valid addresses)
        const activeTokens = chainTokens.filter((tokenId) => {
          const token = newChainConfig.tokens[tokenId];
          return isValidTokenAddress(token.address);
        });

        let finalTokenId = newTokenId; // Use the tokenId we determined earlier for cache lookup
        // If there's exactly one active token, use it
        if (activeTokens.length === 1) {
          finalTokenId = activeTokens[0];
        } else if (activeTokens.length > 0) {
          // If multiple active tokens, use the first active one or keep current if it's active
          finalTokenId = activeTokens.includes(selectedTokenId)
            ? selectedTokenId
            : activeTokens[0];
        } else {
          // No active tokens - don't select any token
          setSelectedTokenData(null);
          setSelectedTokenId(null);
          setPendingBalance(null);
          setConfidentialBalance(null);
          return;
        }

        // Keep balances cleared — never restore cache after a chain switch.
        setPendingBalance(null);
        setConfidentialBalance(null);

        // Update loading token ref to prevent race conditions
        loadingTokenRef.current = finalTokenId;

        const tokenData = newChainConfig.tokens[finalTokenId];
        const fullTokenData = {
          ...tokenData,
          chainId: chainId,
          chainName: newChainConfig.name,
          id: `${chainId}_${finalTokenId}`,
          chainLogo: newChainConfig.icon,
        };
        setSelectedTokenData(fullTokenData);
        setSelectedTokenId(finalTokenId);
      }
    } else if (newChainConfig && newChainConfig.tokens) {
      // No active tokens available for this chain
      setSelectedTokenData(null);
      setSelectedTokenId(null);
      setPendingBalance(null);
      setConfidentialBalance(null);
    }
  };

  const handleTokenSelect = async (tokenData) => {

    // Clear all input fields when switching tokens
    setRecipient("");
    setRecipientError(null);
    setRecipientAccountStatus(null);
    setTransferAmount("");
    setTransferAmountError("");
    setDepositAmount("");
    setDepositAmountError("");
    setWithdrawAmount("");
    setWithdrawAmountError("");

    const newTokenId =
      tokenData.tokenId ||
      tokenIdFromCompositeId(tokenData.id, tokenData.chainId);
    const newCacheKey = `${tokenData.chainId}_${newTokenId}`;
    const chainChanged = tokenData.chainId !== selectedChainId;

    if (chainChanged) {
      accountCreationCancelledRef.current = true;
      accountCreationAttemptedRef.current = false;
      accountCreationInProgressRef.current = false;
      setIsCreatingAccount(false);
      setAccountCreationStep("");
      setAccountCreationFailed(false);
      setKeyDerivationConsent(false);
      setKeysDerived(false);
      setShowConsentModal(false);
      const chainNetwork = getNetworkModeForChain(tokenData.chainId);
      if (chainNetwork && chainNetwork !== networkMode) {
        setNetworkMode(chainNetwork);
      }
      if (userAccount) {
        const prefix = `${userAccount.toLowerCase()}::`;
        for (const key of Array.from(accountCreationSessions)) {
          if (key.startsWith(prefix)) {
            accountCreationSessions.delete(key);
          }
        }
      }
    }

    // IMMEDIATELY update loading refs to prevent race conditions
    // This must happen before any async operations or state updates
    loadingTokenRef.current = newTokenId;
    loadingChainRef.current = tokenData.chainId;

    // Save current balances to cache before switching tokens
    if (confidentialBalance !== null || pendingBalance !== null) {
      const cacheKey = `${selectedChainId}_${selectedTokenId}`;
      balanceCacheRef.current.set(cacheKey, {
        available: confidentialBalance,
        pending: pendingBalance,
      });
    }

    // Cross-chain: always clear. Same-chain token switch may restore cache.
    if (chainChanged) {
      justRestoredFromCacheRef.current = false;
      lastLoadedKeyRef.current = null;
      setPendingBalance(null);
      setConfidentialBalance(null);
      // Drop any cached row for the destination so we never paint another
      // chain's CUSDT (e.g. Ethereum 0.01) onto Base before the fresh load.
      balanceCacheRef.current.delete(newCacheKey);
    } else {
      // Try to restore cached balances for the new chain+token combination
      const cachedBalances = balanceCacheRef.current.get(newCacheKey);
      if (cachedBalances) {
        // Mark that we just restored from cache and track the key
        justRestoredFromCacheRef.current = true;
        lastLoadedKeyRef.current = newCacheKey;
        // Restore cached balances immediately
        if (
          cachedBalances.available !== null &&
          cachedBalances.available !== undefined
        ) {
          setConfidentialBalance(cachedBalances.available);
        } else {
          setConfidentialBalance(null);
        }
        if (
          cachedBalances.pending !== null &&
          cachedBalances.pending !== undefined
        ) {
          setPendingBalance(cachedBalances.pending);
        } else {
          setPendingBalance(null);
        }
      } else {
        // No cached balance, clear them IMMEDIATELY to prevent showing old balance
        justRestoredFromCacheRef.current = false;
        lastLoadedKeyRef.current = null;
        // Clear balances synchronously before updating token selection
        setPendingBalance(null);
        setConfidentialBalance(null);
      }
    }

    // Prefer handleChainSelect for wallet switch when the chain actually changes
    if (chainChanged && switchToChain) {
      const switched = await switchToChain(tokenData.chainId);
      if (!switched) {
        showNotification(
          `Please switch your wallet to ${CHAINS[tokenData.chainId]?.name || "the selected network"} and try again.`,
          "error",
        );
        return;
      }
    }

    // Chain switching is now handled by handleChainSelect
    // Just update the token data and IDs
    setSelectedTokenData(tokenData);
    setSelectedChainId(tokenData.chainId);
    setSelectedTokenId(newTokenId);
  };

  // Let App.js (BalancesModal) switch the selected token by id without
  // opening the token picker — used when "Deposit" is clicked for a
  // specific wallet row (USDC vs USDT).
  const handleTokenSelectRef = useRef(handleTokenSelect);
  handleTokenSelectRef.current = handleTokenSelect;
  const selectedChainIdRef = useRef(selectedChainId);
  selectedChainIdRef.current = selectedChainId;
  const selectedTokenIdRef = useRef(selectedTokenId);
  selectedTokenIdRef.current = selectedTokenId;

  useEffect(() => {
    if (!onSelectTokenRegister) return undefined;
    onSelectTokenRegister(() => (tokenId) => {
      if (!tokenId) return;
      const chainId = selectedChainIdRef.current;
      const chain = chainId ? CHAINS[chainId] : null;
      const token = chain?.tokens?.[tokenId];
      if (!token) return;
      handleTokenSelectRef.current({
        ...token,
        tokenId,
        chainId,
        chainName: chain.name,
        id: `${chainId}_${tokenId}`,
        chainLogo: chain.icon,
        chainColor: chain.color,
      });
    });
    return () => {
      onSelectTokenRegister(null);
    };
  }, [onSelectTokenRegister]);

  const incomingAppliedRef = useRef(null);
  const prefillRef = useRef(null);
  const prefillSwitchingRef = useRef(false);
  const prefillSettleTimerRef = useRef(null);

  useEffect(() => {
    if (!incomingRequest) return;
    const key = `${incomingRequest.to}|${incomingRequest.chainKey}|${incomingRequest.tokenId}|${incomingRequest.amount}`;
    if (incomingAppliedRef.current === key) return;
    incomingAppliedRef.current = key;
    prefillRef.current = { ...incomingRequest };
    prefillSwitchingRef.current = false;
    if (setActiveTab) setActiveTab("transfer");
  }, [incomingRequest]);

  useEffect(() => {
    clearTimeout(prefillSettleTimerRef.current);
    const p = prefillRef.current;
    if (!p) return;
    const chain = p.chainKey ? CHAINS[p.chainKey] : null;
    const token = chain && chain.tokens ? chain.tokens[p.tokenId] : null;
    const chainMatches = !p.chainKey || selectedChainId === p.chainKey;
    const tokenMatches = !p.tokenId || selectedTokenId === p.tokenId;

    // Phase A — get onto the requested chain + token (once).
    if (!chainMatches || !tokenMatches) {
      if (token && !prefillSwitchingRef.current) {
        prefillSwitchingRef.current = true;
        handleTokenSelect({
          ...token,
          tokenId: p.tokenId,
          chainId: p.chainKey,
          chainName: chain.name,
          id: `${p.chainKey}_${p.tokenId}`,
          chainLogo: chain.icon,
          chainColor: chain.color,
        })
          .catch((e) => console.warn("[TransferRequest] chain/token switch failed:", e && e.message ? e.message : e))
          .finally(() => { prefillSwitchingRef.current = false; });
      }
      return; // wait for chain/token to settle before filling recipient+amount
    }

    // Phase B — chain+token in place → (re-)fill recipient + amount.
    const wantAmount = p.amount != null ? String(p.amount).trim() : "";
    if (p.to && recipient !== p.to) handleRecipientChange(p.to);
    if (wantAmount && transferAmount !== wantAmount) setTransferAmount(wantAmount);
    const filled = (!p.to || recipient === p.to) && (!wantAmount || transferAmount === wantAmount);

    if (filled && userAccount) {
      prefillSettleTimerRef.current = setTimeout(() => {
        prefillRef.current = null;
        if (onIncomingConsumed) onIncomingConsumed();
      }, 2000);
    }
  }, [incomingRequest, selectedChainId, selectedTokenId, recipient, transferAmount, userAccount]);

  useEffect(() => {
    setSavedChainId(selectedChainId || null);
  }, [selectedChainId]);

  useEffect(() => {
    setSavedTokenId(selectedTokenId || null);
  }, [selectedTokenId]);

  // Detect wallet's current network and handle mismatches
  useEffect(() => {
    const detectCurrentChain = async () => {
      if (!provider || !userAccount) return;

      try {
        const network = await provider.getNetwork();
        const currentChainId = network.chainId;

        // Resolve wallet EVM id → CHAINS config key. Prefer the UI selection so
        // mainnet/testnet siblings (arbitrum vs arbitrum_mainnet,
        // ethereum vs ethereum_mainnet, base vs base_mainnet,
        // bnb vs bnb_mainnet) are never confused when comparing wallet vs UI.
        // Do NOT filter by networkMode here — we need to detect cross-network
        // mismatches and switch the wallet.
        const matching = resolveChainEntry(currentChainId, {
          preferredKey: selectedChainId,
        });

        if (matching) {
          const chainId = matching.key;

          // Check if wallet chain matches UI selection (by config key, not brand name)
          if (chainId !== selectedChainId) {

            // Check if we've already attempted auto-switch in this session (prevent infinite loops)
            const autoSwitchAttempted = sessionStorage.getItem(
              "autoSwitchAttempted",
            );
            const lastAttemptTime =
              sessionStorage.getItem("lastAutoSwitchTime");
            const now = Date.now();

            // Reset flag after 30 seconds to allow retry if needed
            if (lastAttemptTime && now - parseInt(lastAttemptTime) > 30000) {
              sessionStorage.removeItem("autoSwitchAttempted");
              sessionStorage.removeItem("lastAutoSwitchTime");
            }

            // Only auto-switch if user has explicitly selected a chain
            // Never auto-select chain based on wallet - user must choose explicitly
            if (selectedChainId && switchToChain && !autoSwitchAttempted) {

              // Mark that we've attempted auto-switch to prevent infinite loops
              sessionStorage.setItem("autoSwitchAttempted", "true");
              sessionStorage.setItem("lastAutoSwitchTime", now.toString());

              // Try to switch wallet to match user's selection
              const switched = await switchToChain(selectedChainId);
              if (!switched) {
                // If switch failed, keep user's selection - don't override with wallet chain
                // Do NOT sync UI to wallet - respect user's explicit choice
              }
            } else {
              // No chain selected by user - don't auto-select based on wallet
              // Do NOT sync UI to wallet - keep it null until user chooses
            }
          } else {
            // Clear auto-switch flag when chains are in sync
            sessionStorage.removeItem("autoSwitchAttempted");
            sessionStorage.removeItem("lastAutoSwitchTime");
          }
        } else {
          //  showNotification('Please switch your wallet to a supported network', 'warning');
        }
      } catch (error) {
        console.error("Error detecting current chain:", error);
      }
    };

    detectCurrentChain();
  }, [provider, userAccount]); // Only run when provider or account changes

  // Validate that the selected token is active for the selected chain
  // This ensures inactive tokens are never selected by default
  useEffect(() => {
    const chain = CHAINS[selectedChainId];
    if (!chain || !chain.tokens || !selectedTokenId) return;

    const isValidTokenAddress = (address) => {
      if (!address) return false;
      const addr = address.trim().toLowerCase();
      if (addr === "" || addr === "0x0000000000000000000000000000000000000000")
        return false;
      return /^0x[a-fA-F0-9]{40}$/.test(addr);
    };

    const token = chain.tokens[selectedTokenId];
    if (!token || !isValidTokenAddress(token.address)) {
      // Current token is inactive - find an active token or clear selection
      const chainTokens = Object.keys(chain.tokens);
      const activeTokens = chainTokens.filter((tokenId) => {
        const t = chain.tokens[tokenId];
        return isValidTokenAddress(t.address);
      });

      if (activeTokens.length > 0) {
        // Select the first active token
        const newTokenId = activeTokens[0];
        // Clear all input fields when token is automatically switched
        setRecipient("");
        setRecipientError(null);
        setRecipientAccountStatus(null);
        setTransferAmount("");
        setTransferAmountError("");
        setDepositAmount("");
        setDepositAmountError("");
        setWithdrawAmount("");
        setWithdrawAmountError("");

        const tokenData = chain.tokens[newTokenId];
        const fullTokenData = {
          ...tokenData,
          chainId: selectedChainId,
          chainName: chain.name,
          id: `${selectedChainId}_${newTokenId}`,
          chainLogo: chain.icon,
        };
        setSelectedTokenData(fullTokenData);
        setSelectedTokenId(newTokenId);
      } else {
        // No active tokens available - clear selection
        // Clear all input fields when token selection is cleared
        setRecipient("");
        setRecipientError(null);
        setRecipientAccountStatus(null);
        setTransferAmount("");
        setTransferAmountError("");
        setDepositAmount("");
        setDepositAmountError("");
        setWithdrawAmount("");
        setWithdrawAmountError("");

        setSelectedTokenData(null);
        setSelectedTokenId(null);
      }
    }
  }, [selectedChainId]); // Only validate when chain changes, not on every token change

  // Update wallet ERC-20 balance when token/chain selection changes.
  // Clear immediately so a previous token's amount never lingers under the
  // new symbol while the fresh read is in flight.
  useEffect(() => {
    if (
      activeTab === "deposit" ||
      activeTab === "withdraw" ||
      activeTab === "transfer" ||
      activeTab === "apply"
    ) {
      walletBalanceRequestIdRef.current += 1;
      setBalance("Loading...");
      updateTokenBalance();
    }
  }, [selectedTokenId, selectedChainId, activeTab, userAccount, contract]);

  // Load confidential balance from contract when derived key is available
  useEffect(() => {
    // Update the refs to track current chain and token (in case they changed outside handlers)
    loadingChainRef.current = selectedChainId;
    loadingTokenRef.current = selectedTokenId;

    const canReadBalance =
      !!(CHAINS[selectedChainId]?.contractAddress || contract) &&
      userAccount &&
      wasmModule &&
      keypair;

    if (canReadBalance) {
      // Only load if we're still on the same chain and token (prevents race conditions during switches)
      // The keypair hook uses selectedChainIdForKeys, so keypair should match, but we check to be safe
      if (
        loadingChainRef.current === selectedChainId &&
        loadingTokenRef.current === selectedTokenId
      ) {
        const cacheKey = `${selectedChainId}_${selectedTokenId}`;

        // CRITICAL CHECK: If we just restored this exact key from cache, skip loading entirely
        // This prevents overwriting the cached balance that was just restored
        if (
          justRestoredFromCacheRef.current &&
          lastLoadedKeyRef.current === cacheKey
        ) {
          return;
        }

        // Normal loading path - not from cache restore
        // Clear flag only if we're loading a different key than what was restored
        if (lastLoadedKeyRef.current !== cacheKey) {
          justRestoredFromCacheRef.current = false;
        }
        lastLoadedKeyRef.current = cacheKey; // Track that we're loading this key
        loadConfidentialBalance();
        loadPendingBalance();
      }
    }
    // Don't clear pending balance when keypair is null - it might just be temporarily unavailable
    // during chain switches. Only clear it when explicitly changing chains (in handleChainSelect)
  }, [
    contract,
    userAccount,
    wasmModule,
    keypair,
    selectedTokenId,
    selectedChainId,
  ]);

  // Check for pending action when accountData changes or periodically
  useEffect(() => {
    if (!contract || !userAccount) {
      setPendingActionKind(null);
      return;
    }

    // Check immediately
    checkPendingAction();

    // Set up periodic check every 5 seconds to catch updates
    const interval = setInterval(() => {
      checkPendingAction();
    }, 5000);

    return () => clearInterval(interval);
  }, [accountData, contract, userAccount, selectedChainId]);

  // Keep parent secure-claim in-flight state aligned with on-chain APPLY so
  // the header/banner stay hidden after refresh while a claim is processing.
  useEffect(() => {
    if (!onSecureClaimInFlightChange) return;
    const claimKey = getSecureClaimKey();
    if (!claimKey) return;

    if (isPendingKind(pendingActionKind, PENDING_KIND_APPLY)) {
      onSecureClaimInFlightChange(true, claimKey);
    }
  }, [pendingActionKind, userAccount, selectedChainId, selectedTokenId]);

  // Refresh balance when pending operation completes (pendingActionKind goes from non-null to null)
  useEffect(() => {
    // If pendingActionKind changed from a non-null value to null, refresh balance
    if (
      prevPendingActionKindRef.current !== null &&
      pendingActionKind === null
    ) {
      const completedKind = prevPendingActionKindRef.current;
      (async () => {
        if (contract && userAccount && keypair && wasmModule) {
          try {
            await loadConfidentialBalance(0, true); // Force load to get latest balance
          } catch (error) {
            console.error(
              "Error loading balance after pending action completed:",
              error,
            );
          }
          try {
            await loadPendingBalance(0, true); // Force load to get latest pending balance
          } catch (error) {
            console.error(
              "Error loading pending balance after pending action completed:",
              error,
            );
          }
        }
        // Clear secure-claim in-flight only after pending has been reloaded,
        // so the header/banner don't briefly reappear with a stale amount.
        if (
          onSecureClaimInFlightChange &&
          isPendingKind(completedKind, PENDING_KIND_APPLY)
        ) {
          onSecureClaimInFlightChange(false, null);
        }
      })();
    }

    // Update ref for next comparison
    prevPendingActionKindRef.current = pendingActionKind;
  }, [pendingActionKind, contract, userAccount, keypair, wasmModule]);

  // When pending balance loads, decide whether to show notification
  useEffect(() => {
    if (!userAccount) {
      setShowPendingNotification(false);
      return;
    }

    // Hide notification when token selector modal is open
    if (showTokenSelector) {
      setShowPendingNotification(false);
      return;
    }

    // Hide while a secure claim is already in progress on-chain (survives refresh)
    const applyInProgress = isPendingKind(
      pendingActionKind,
      PENDING_KIND_APPLY,
    );

    // Show notification if there's a pending balance and it hasn't been dismissed
    if (
      pendingBalance &&
      pendingBalance > 0 &&
      !pendingNotificationDismissed &&
      !applyInProgress
    ) {
      setShowPendingNotification(true);
    } else {
      setShowPendingNotification(false);
    }
  }, [
    pendingBalance,
    userAccount,
    pendingNotificationDismissed,
    showTokenSelector,
    pendingActionKind,
  ]);

  // Inform parent of pending balance changes
  useEffect(() => {
    if (onPendingBalanceChange) {
      onPendingBalanceChange(pendingBalance);
    }
  }, [pendingBalance, onPendingBalanceChange]);

  // Check for pending deposits - only use contract state, never check Firebase
  useEffect(() => {
    if (!userAccount || !selectedChainId) {
      setHasPendingDeposit(false);
      return;
    }

    // Don't show pending deposits if account doesn't exist yet
    if (!accountExists) {
      setHasPendingDeposit(false);
      return;
    }

    // Only use contract state to determine pending deposits
    // Never check Firebase for pending actions - always get state from contract
    const hasContractPendingDeposit = isPendingKind(
      pendingActionKind,
      PENDING_KIND_DEPOSIT,
    );
    setHasPendingDeposit(accountExists && hasContractPendingDeposit);
  }, [userAccount, selectedChainId, accountExists, pendingActionKind]);

  // Register the secure claim handler with parent
  useEffect(() => {
    if (onSecureClaimRegister) {
      // Wrap in a function so the state setter stores a callable instead of treating it as an updater
      onSecureClaimRegister(() => () => {
        if (secureClaimHandlerRef.current) {
          secureClaimHandlerRef.current();
        }
      });
    }
    return () => {
      // Clear the handler when unmounting
      if (onSecureClaimRegister) {
        onSecureClaimRegister(null);
      }
    };
  }, [onSecureClaimRegister]);

  // Update pending notification position to match deposit banner
  useEffect(() => {
    const updatePosition = () => {
      if (pendingNotificationRef.current && showPendingNotification) {
        const depositBanner = document.querySelector(".deposit-banner");
        if (depositBanner) {
          // Deposit banner exists - position below it (current behavior)
          const rect = depositBanner.getBoundingClientRect();
          pendingNotificationRef.current.style.left = `${rect.left}px`;
          // Reset top position to let CSS handle it (position below banner)
          pendingNotificationRef.current.style.top = "";
        } else {
          // No deposit banner - position at top where deposit banner would be
          // Since deposit banner is in a grid with align-items: start, it aligns with main-card's top
          const mainCard = document.querySelector(".main-card");
          const mainContentLayout = document.querySelector(
            ".main-content-layout",
          );
          const isMobile = window.innerHeight <= 820;
          const isLargeScreen =
            window.innerWidth >= 1200 && window.innerHeight >= 821;

          if (mainCard && mainContentLayout) {
            const rootStyles = getComputedStyle(document.documentElement);
            const mainWidth =
              rootStyles.getPropertyValue("--main-width")?.trim() ||
              (isMobile ? "600px" : "800px");
            const mainWidthNum = parseInt(mainWidth);
            const gap = isLargeScreen ? 32 : 16;

            // Calculate left position to match where deposit banner would be in grid column 3
            // Deposit banner is at: center + main-width/2 + gap
            const leftOffset = isMobile
              ? `calc(50vw + ${mainWidthNum}px / 2 + ${gap}px - 12px)`
              : `calc(50vw + ${mainWidthNum}px / 2 + ${gap}px)`;
            pendingNotificationRef.current.style.left = leftOffset;

            // Get the main-card's position - deposit banner aligns with it (align-items: start)
            const cardRect = mainCard.getBoundingClientRect();
            const layoutRect = mainContentLayout.getBoundingClientRect();

            // On larger screens, deposit banner might be sticky with top offset
            // On MacBook, it's position: relative with top: 0
            // So we use the main-card's top position directly
            const topPosition = `${cardRect.top}px`;
            pendingNotificationRef.current.style.top = topPosition;
          } else {
            // Fallback: use calculated values if elements not available
            const mainWidth = isMobile ? 600 : 800;
            const gap = isLargeScreen ? 32 : 16;
            const viewportCenter = window.innerWidth / 2;
            const leftOffset = isMobile
              ? viewportCenter + mainWidth / 2 + gap - 12
              : viewportCenter + mainWidth / 2 + gap;
            pendingNotificationRef.current.style.left = `${leftOffset}px`;

            // Fallback top position using main-content position
            const mainContent = document.querySelector(".main-content");
            if (mainContent) {
              const contentRect = mainContent.getBoundingClientRect();
              pendingNotificationRef.current.style.top = `${contentRect.top}px`;
            } else {
              // Last resort: use viewport calculation
              const vh = window.innerHeight;
              const headerGapMax = isMobile ? 24 : 144;
              const headerGapMin = isMobile ? 12 : 32;
              const headerGapVh = isMobile ? vh * 0.025 : vh * 0.08;
              const headerGap = Math.max(
                headerGapMin,
                Math.min(headerGapMax, headerGapVh),
              );
              pendingNotificationRef.current.style.top = `${headerGap}px`;
            }
          }
        }
      }
    };

    // Update on mount and when notification is shown
    updatePosition();

    // Watch for deposit banner being added/removed from DOM
    // This handles the case when user closes the "Let's get started" banner
    const mainContentLayout = document.querySelector(".main-content-layout");
    let mutationObserver = null;
    let updateTimeout = null;

    if (mainContentLayout && showPendingNotification) {
      mutationObserver = new MutationObserver(() => {
        // Debounce updates to avoid excessive calls
        if (updateTimeout) {
          clearTimeout(updateTimeout);
        }
        updateTimeout = setTimeout(() => {
          updatePosition();
        }, 50); // Small delay to batch DOM changes
      });

      mutationObserver.observe(mainContentLayout, {
        childList: true,
        subtree: true,
      });
    }

    // Update on window resize
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition);

    // Cleanup on unmount
    return () => {
      if (mutationObserver) {
        mutationObserver.disconnect();
      }
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition);
    };

    return () => {
      if (mutationObserver) {
        mutationObserver.disconnect();
      }
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition);
    };
  }, [showPendingNotification, confidentialBalance]);

  // Set balance to 0 for users without accounts
  // But don't clear if we just restored from cache (preserve cached value)
  useEffect(() => {
    if (
      contract &&
      userAccount &&
      !hasActiveAccount &&
      !justRestoredFromCacheRef.current
    ) {
      // Check if we have a cached balance first - if so, don't clear it
      const cacheKey = `${selectedChainId}_${selectedTokenId}`;
      const cached = balanceCacheRef.current.get(cacheKey);
      if (
        !cached ||
        cached.available === null ||
        cached.available === undefined
      ) {
        setConfidentialBalance(0);
      }
    }
  }, [
    contract,
    userAccount,
    hasActiveAccount,
    selectedChainId,
    selectedTokenId,
  ]);

  // Set up blockchain event listeners for automatic balance updates
  useEffect(() => {
    if (!contract || !userAccount) return;


    // Listen for account creation processed events
    // This ensures UI refreshes when account is created onchain
    const createAccountFilter =
      contract.filters.CreateAccountProcessed(userAccount);
    const createAccountListener = () => {
      // Throttle to avoid immediate RPC calls right after event
      const now = Date.now();
      if (now - lastAccountStatusCheckRef.current >= 2000) {
        lastAccountStatusCheckRef.current = now;
        setTimeout(async () => {
          try {
            // Load the balance (this is account creation, not a balance-changing operation)
            await loadConfidentialBalance(0, true);
          } catch (error) {
            console.error(
              "Error loading balance after account creation:",
              error,
            );
          }
          // Refresh account status to update accountExists and hasActiveAccount
          if (onOperationCompleteRef.current) {
            await onOperationCompleteRef.current();
          }
        }, 1500); // Wait 1.5 seconds for blockchain state to update
      }
    };

    // Listen for deposit processed events
    const depositFilter = contract.filters.DepositProcessed(userAccount);
    const depositListener = () => {
      setTimeout(async () => {
        try {
          // AWAIT the balance load to ensure new balance is displayed before clearing pending status
          await loadConfidentialBalance(0, true); // Force load after operation
        } catch (error) {
          console.error("Error loading balance after deposit:", error);
        }
        loadPendingBalance(0, true); // Force load after operation
        updateTokenBalance();
        // Refresh account data to clear pendingAction flag AFTER balance is loaded
        if (onOperationCompleteRef.current) {
          await onOperationCompleteRef.current();
        }
      }, 2000); // Wait 2 seconds for transaction to be mined
    };

    // Listen for withdraw processed events
    const withdrawFilter = contract.filters.WithdrawProcessed(userAccount);
    const withdrawListener = () => {
      setTimeout(async () => {
        try {
          // AWAIT the balance load to ensure new balance is displayed before clearing pending status
          await loadConfidentialBalance(0, true); // Force load after operation
        } catch (error) {
          console.error("Error loading balance after withdraw:", error);
        }
        loadPendingBalance(0, true); // Force load after operation
        updateTokenBalance();
        // Refresh account data to clear pendingAction flag AFTER balance is loaded
        if (onOperationCompleteRef.current) {
          await onOperationCompleteRef.current();
        }
      }, 2000);
    };

    // Listen for secure claim processed events
    const applyFilter = contract.filters.ApplyPendingProcessed(userAccount);
    const applyListener = () => {
      setTimeout(async () => {
        try {
          // AWAIT the balance load to ensure new balance is displayed before clearing pending status
          await loadConfidentialBalance(0, true); // Force load after operation
        } catch (error) {
          console.error("Error loading balance after secure claim:", error);
        }
        try {
          // Await pending reload so we clear stale pending before showing header/banner again
          await loadPendingBalance(0, true);
        } catch (error) {
          console.error(
            "Error loading pending balance after secure claim:",
            error,
          );
        }
        // Claim is processed; allow the header button to reappear when there's new pending balance.
        if (onSecureClaimInFlightChange) {
          onSecureClaimInFlightChange(false, null);
        }
        // Refresh account data to clear pendingAction flag AFTER balance is loaded
        if (onOperationCompleteRef.current) {
          await onOperationCompleteRef.current();
        }
      }, 2000);
    };

    // Listen for transfer processed events (both as sender and recipient)
    const transferSenderFilter = contract.filters.TransferProcessed(
      userAccount,
      null,
    );
    const transferRecipientFilter = contract.filters.TransferProcessed(
      null,
      userAccount,
    );

    // Listener for when user sends a transfer
    const transferSenderListener = () => {
      setTimeout(async () => {
        try {
          // AWAIT the balance load to ensure new balance is displayed before clearing pending status
          await loadConfidentialBalance(0, true); // Force load to get latest balance
        } catch (error) {
          console.error("Error loading balance after transfer:", error);
        }
        loadPendingBalance(0, true); // Force load to get latest pending balance
        // Refresh account data to clear pendingAction flag AFTER balance is loaded
        if (onOperationCompleteRef.current) {
          await onOperationCompleteRef.current();
        }
      }, 2000);
    };

    // Listener for when user receives a transfer - reload pending balance immediately
    const transferRecipientListener = async (
      sender,
      recipient,
      txId,
      ok,
      err,
      token,
      ...restArgs
    ) => {

      // Verify that recipient matches current user (should be true due to filter, but double-check)
      if (
        recipient &&
        recipient.toLowerCase() === userAccount.toLowerCase() &&
        ok
      ) {

        // Clear any pending timeout to prevent multiple scheduled loads
        if (recipientTransferTimeoutRef.current) {
          clearTimeout(recipientTransferTimeoutRef.current);
          recipientTransferTimeoutRef.current = null;
        }

        // Use a single timeout to avoid duplicate RPC calls
        // Delay slightly to allow blockchain state to update after event emission
        recipientTransferTimeoutRef.current = setTimeout(async () => {
          recipientTransferTimeoutRef.current = null;
          try {
            // Only reload pending balance - this is what the user needs to see
            // Confidential balance and account data will be updated by polling or other operations
            // loadPendingBalance already has protection against concurrent calls
            loadPendingBalance(0, true); // Force load to get latest pending balance
          } catch (error) {
            console.error(
              "Error reloading pending balance for received transfer:",
              error,
            );
          }
        }, 800); // Single 800ms delay (balances speed and reliability)
      } else {
        if (!ok) {
        } else {
          console.warn(
            "Transfer recipient does not match current user, skipping balance reload",
          );
        }
      }
    };

    // Set up event listeners
    contract.on(createAccountFilter, createAccountListener);
    contract.on(depositFilter, depositListener);
    contract.on(withdrawFilter, withdrawListener);
    contract.on(applyFilter, applyListener);
    contract.on(transferSenderFilter, transferSenderListener);
    contract.on(transferRecipientFilter, transferRecipientListener);

    // Cleanup function to remove listeners and clear pending timeouts
    return () => {

      // Clear any pending timeout
      if (recipientTransferTimeoutRef.current) {
        clearTimeout(recipientTransferTimeoutRef.current);
        recipientTransferTimeoutRef.current = null;
      }

      contract.off(createAccountFilter, createAccountListener);
      contract.off(depositFilter, depositListener);
      contract.off(withdrawFilter, withdrawListener);
      contract.off(applyFilter, applyListener);
      contract.off(transferSenderFilter, transferSenderListener);
      contract.off(transferRecipientFilter, transferRecipientListener);
    };
  }, [contract, userAccount, keypair]);

  // Polling fallback for balance updates (in case event listeners don't work)
  // Use more aggressive polling for Privy/WalletConnect as they may not support event subscriptions reliably
  useEffect(() => {
    if (!contract || !userAccount || !keypair) return;

    const isPrivy = activeWalletOrigin === "privy";
    // Use 5 second polling for Privy, 30 seconds for other wallets
    const pollIntervalMs = isPrivy ? 5000 : 30000;


    // Poll for balance updates
    const pollInterval = setInterval(() => {
      loadConfidentialBalance(0, true); // Force load on poll
      loadPendingBalance(0, true); // Force load on poll
      updateTokenBalance();
    }, pollIntervalMs);

    // Refresh balance when page becomes visible (user might have received tokens while away)
    const handleVisibilityChange = () => {
      if (!document.hidden && contract && userAccount && keypair) {
        loadConfidentialBalance(0, true);
        loadPendingBalance(0, true);
        updateTokenBalance();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(pollInterval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [contract, userAccount, keypair, activeWalletOrigin]);

  // Helper function to refresh balances aggressively for Privy users
  const refreshBalancesForPrivy = (operationName) => {
    if (activeWalletOrigin === "privy") {
      // Refresh immediately, then at 5s, 10s, 20s intervals
      const refreshDelays = [0, 5000, 10000, 20000];
      refreshDelays.forEach((delay, index) => {
        setTimeout(() => {
          loadConfidentialBalance(0, true);
          loadPendingBalance(0, true);
          updateTokenBalance();
        }, delay);
      });
    }
  };

  // Notify parent of token data changes
  useEffect(() => {
    if (onTokenDataChange) {
      const tokenData = getTokenData();
      onTokenDataChange(tokenData);
    }
  }, [selectedTokenId, selectedChainId, onTokenDataChange]);

  // Key derivation is now automatic via the WASM hook

  // Helper function to convert hex to bytes
  const hexToBytes = (hex) => {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) {
      bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
    }
    return bytes;
  };

  // Check if user has active account with non-zero balance
  const checkAccountStatus = () => {
    // Prefer live balance/pending evidence over possibly-stale App props —
    // those props can lag while balances have already loaded from the contract.
    const knownActive =
      (accountExists && hasActiveAccount) ||
      (typeof confidentialBalance === "number" && confidentialBalance > 0) ||
      (typeof pendingBalance === "number" && pendingBalance > 0);

    if (!knownActive && !accountExists) {
      return {
        hasAccount: false,
        hasBalance: false,
        message:
          "No account found. Please go to the Deposit tab to create an account and add funds.",
      };
    }

    if (!knownActive && !hasActiveAccount) {
      return {
        hasAccount: true,
        hasBalance: false,
        message:
          "Account exists but is not yet finalized. Please wait for account activation or go to the Deposit tab.",
      };
    }

    // If keypair is not available, we can't check balance yet
    if (!keypair) {
      return {
        hasAccount: true,
        hasBalance: false,
        message: "Loading balance...",
      };
    }

    // If balance is null, it's still loading
    if (confidentialBalance === null) {
      return {
        hasAccount: true,
        hasBalance: false,
        message: "Loading balance...",
      };
    }

    // Check if balance is greater than 0 (using > 0 instead of === 0 to handle floating point precision and very small balances)
    // confidentialBalance is stored as raw value (scaled by 100), so even 1 unit would be > 0
    if (confidentialBalance > 0) {
      return { hasAccount: true, hasBalance: true, message: "" };
    }

    // Balance is 0 or negative — still a real account (may only have pending)
    if (typeof pendingBalance === "number" && pendingBalance > 0) {
      return { hasAccount: true, hasBalance: true, message: "" };
    }

    return {
      hasAccount: true,
      hasBalance: false,
      message:
        "Account has zero balance. Please go to the Deposit tab to add funds.",
    };
  };

  // Wait for account activation function
  const waitForAccountActivation = async () => {
    setIsCreatingAccount(true);
    setAccountCreationStep("Waiting for account activation...");

    try {
      let attempts = 0;
      const maxAttempts = 30; // reduce attempts

      while (attempts < maxAttempts) {
        await sleep(2000); // poll every 2s to avoid rate limits
        await onOperationComplete(); // Refresh account status

        // Check if account is now active by checking the contract directly
        try {
          const accountDataTuple = await contract.getAccountCore(userAccount);
          const isActive = accountDataTuple[0] && accountDataTuple[1]; // exists && finalized

          if (isActive) {
            setAccountCreationStep("Account activated successfully!");
            // Removed notification - account activation is already clear from UI state
            setTimeout(() => {
              setIsCreatingAccount(false);
              setAccountCreationStep("");
            }, 2000);
            return true;
          }
        } catch (error) {
          console.error("Error checking account status:", error);
        }

        attempts++;
        setAccountCreationStep(
          `Waiting for account activation... (${attempts}/${maxAttempts}s)`,
        );
      }

      // If we get here, account activation timed out
      setAccountCreationStep("Account activation timed out.");
      showNotification(
        "Account activation timed out. Try again later.",
        "error",
      );
      setTimeout(() => {
        setIsCreatingAccount(false);
        setAccountCreationStep("");
      }, 2000);
      return false;
    } catch (error) {
      console.error("Error waiting for account activation:", error);
      setAccountCreationStep("");
      const cleanError = getCleanErrorMessage(error);
      showNotification(
        `Error waiting for account activation: ${cleanError}`,
        "error",
      );
      return false;
    } finally {
      setIsCreatingAccount(false);
    }
  };

  // Create account function for deposit flow
  const createAccount = async () => {
    if (!contract || !keypair) {
      showNotification(
        "Contract or keys not ready. Try again.",
        "error",
      );
      return false;
    }

    setIsCreatingAccount(true);
    setAccountCreationStep("Checking balances...");

    try {
      // Check gas and token balances before attempting account creation
      const chainConfig = CHAINS[selectedChainId];
      const nativeCurrencySymbol = chainConfig?.nativeCurrency?.symbol || "ETH";
      const nativeCurrencyDecimals =
        chainConfig?.nativeCurrency?.decimals || 18;
      const isTempo = selectedChainId === "tempo";

      // Check gas balance (or fee token balance for Tempo)
      let gasBalance;
      let hasInsufficientGas = false;
      let feeTokenInfo = null;

      if (isTempo) {
        // For Tempo, check if user has any fee token balance
        try {
          feeTokenInfo = await checkTempoFeeTokenBalance(provider, userAccount);
          hasInsufficientGas = !feeTokenInfo.hasFeeToken;
          if (feeTokenInfo.hasFeeToken) {
          }
        } catch (balanceError) {
          console.error(
            "Error checking Tempo fee token balance:",
            balanceError,
          );
          throw new Error(
            "Could not check fee token balance. Check your network connection.",
          );
        }
      } else {
        // For other chains, check native gas on the selected chain's RPC.
        let lastError = null;
        const maxRetries = 3;
        const gasReadProvider = chainConfig?.rpcUrl
          ? new ethers.providers.JsonRpcProvider(chainConfig.rpcUrl)
          : provider;
        for (let attempt = 0; attempt < maxRetries; attempt++) {
          try {
            if (attempt > 0) {
              // Wait before retry (exponential backoff)
              await new Promise((resolve) =>
                setTimeout(resolve, 1000 * attempt),
              );
            }
            gasBalance = await gasReadProvider.getBalance(userAccount);
            // Use formatUnits with correct decimals instead of formatEther
            const formattedBalance = ethers.utils.formatUnits(
              gasBalance,
              nativeCurrencyDecimals,
            );
            hasInsufficientGas = gasBalance.isZero();
            lastError = null; // Success, clear error
            break; // Exit retry loop on success
          } catch (balanceError) {
            lastError = balanceError;
            console.error(
              `Error checking gas balance (attempt ${attempt + 1}/${maxRetries}):`,
              balanceError,
            );
            // If this is the last attempt, throw the error
            if (attempt === maxRetries - 1) {
              // Provide more specific error message
              const errorMessage = balanceError.message || String(balanceError);
              if (isSSLError(balanceError)) {
                const chainName = chainConfig?.name || "this network";
                throw new Error(
                  `Cannot connect securely to ${chainName}. Try again later.`,
                );
              } else if (
                errorMessage.includes("network") ||
                errorMessage.includes("connection") ||
                errorMessage.includes("timeout") ||
                errorMessage.includes("HTTP request failed") ||
                errorMessage.includes("fetch failed") ||
                errorMessage.includes("RPC")
              ) {
                throw new Error(
                  "RPC connection error: Cannot reach the network. Try again.",
                );
              } else {
                throw new Error(
                  "Cannot check gas balance right now. Try again.",
                );
              }
            }
          }
        }
      }

      // Check token balance against the selected chain's RPC (wallet may lag).
      let hasInsufficientToken = false;
      let tokenSymbol = null;
      const tokenData = getTokenData();
      if (
        tokenData &&
        tokenData.address &&
        tokenData.address !== "0x0000000000000000000000000000000000000000"
      ) {
        try {
          const tokenReadProvider = chainConfig?.rpcUrl
            ? new ethers.providers.JsonRpcProvider(chainConfig.rpcUrl)
            : provider;
          const tokenContract = new ethers.Contract(
            tokenData.address,
            ["function balanceOf(address) view returns (uint256)"],
            tokenReadProvider,
          );
          const tokenBalance = await tokenContract.balanceOf(userAccount);
          const formattedTokenBalance = ethers.utils.formatUnits(
            tokenBalance,
            tokenData.decimals || 18,
          );
          hasInsufficientToken = tokenBalance.isZero();
          tokenSymbol = tokenData.symbol;
        } catch (tokenBalanceError) {
          console.error("Error checking token balance:", tokenBalanceError);
          // Don't block account creation if we can't check token balance
        }
      }

      // Funding gaps: block setup when gas is missing, but do not toast a
      // fund error — the right-hand tip cards already explain how to get tokens.
      if (hasInsufficientGas) {
        setAccountCreationStep("");
        setIsCreatingAccount(false);
        return false;
      }
      // Token-only gap: gas is enough for account creation — continue.

      setAccountCreationStep("Creating account...");

      // Convert Base64 public key to hex for contract
      const pkBase64 = keypair.public_key;
      const pkBytes = Uint8Array.from(atob(pkBase64), (c) => c.charCodeAt(0));
      const pkHexForContract =
        "0x" +
        Array.from(pkBytes)
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("");

      setAccountCreationStep("Creating confidential account...");
      {
        const chainConfig = CHAINS[selectedChainId];
        if (!chainConfig?.contractAddress) {
          throw new Error("Contract address is not configured for this chain");
        }
        // Switches the wallet onto the selected chain and checks it got there.
        await getSessionForSelectedChain();
        // Sent here rather than through the SDK's ensureAccount, which leaves
        // fees to the wallet: MetaMask's own estimate can land just under a
        // rising base fee (seen on Arbitrum Sepolia) and the network rejects
        // it. Same buffered-fee path the transfer uses.
        const createContract = new ethers.Contract(
          chainConfig.contractAddress,
          CONTRACT_ABI,
          provider.getSigner(userAccount),
        );
        const createTx = await executeContractTxWithGasFallback(
          createContract,
          "createConfidentialAccount",
          [pkHexForContract],
          { manualGasLimit: 1000000 },
        );
        await createTx.wait();
      }
      setAccountCreationStep("Waiting for transaction confirmation...");

      setAccountCreationStep("Creating account");
      // Removed "Creating account" notification - step indicator already shows progress

      // Wait for account to be processed
      setAccountCreationStep("Waiting for account to be activated...");
      let attempts = 0;
      const maxAttempts = 30; // 30 seconds

      while (attempts < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Check if account is now active by checking the contract directly
        // Using the same approach as the top bar status check
        try {
          const accountDataTuple = await contract.getAccountCore(userAccount);
          const isActive = accountDataTuple[0] && accountDataTuple[1]; // exists && finalized

          if (isActive) {
            setAccountCreationStep("Account activated successfully!");
            // Removed notification - account activation is already clear from UI state
            // Clear account creation failed flag on success
            setAccountCreationFailed(false);

            // Refresh the parent state to update the top bar
            await onOperationComplete();

            setTimeout(() => {
              setIsCreatingAccount(false);
              setAccountCreationStep("");
            }, 2000);
            return true;
          }
        } catch (error) {
          console.error("Error checking account activation status:", error);
        }

        attempts++;
        setAccountCreationStep(
          `Waiting for account to be activated... (${attempts}/${maxAttempts}s)`,
        );
      }

      // If we get here, account creation is still pending
      setAccountCreationStep(
        "Account creation pending. You can proceed with deposit.",
      );
      //showNotification('Account creation is pending. You can proceed with deposit.', 'info');
      setTimeout(() => {
        setIsCreatingAccount(false);
        setAccountCreationStep("");
      }, 2000);
      return true;
    } catch (error) {
      console.error("Error creating account:", error);
      setAccountCreationStep("");
      if (!isUserRejectedError(error)) {
        const cleanError = getCleanErrorMessage(error);
        // Check if this is an RPC error and adjust message accordingly
        const errorMessage = error.message || String(error);
        const isRpcError =
          errorMessage.includes("HTTP request failed") ||
          errorMessage.includes("fetch failed") ||
          (errorMessage.includes("RPC") && !isFeeTooLowError(error)) ||
          errorMessage.includes("network") ||
          errorMessage.includes("connection") ||
          cleanError.includes("RPC connection error") ||
          cleanError.includes("RPC error");
        if (isRpcError) {
          showNotification(
            `Account creation failed. Cannot reach the network. Try again.`,
            "error",
          );
        } else {
          showNotification(`Account creation failed: ${cleanError}`, "error");
        }
      }
      // Mark account creation as failed so "Get Keys" buttons show again
      setAccountCreationFailed(true);
      // Reset keysDerived so "Get Keys" buttons show again, allowing user to retry
      // This allows users to retry account creation after cancellation/failure
      setKeysDerived(false);
      setKeyDerivationConsent(false); // Reset consent so they can go through flow again
      // Reset the attempt flag so user can retry account creation
      accountCreationAttemptedRef.current = false;
      return false;
    } finally {
      setIsCreatingAccount(false);
    }
  };

  const loadConfidentialBalance = async (retryCount = 0, forceLoad = false) => {
    const MAX_RETRIES = 2; // Will try up to 3 times total (initial + 2 retries)
    // Capture the chain/token this load is for. Late completions from a
    // previous network (e.g. Ethereum decrypt finishing after Base was
    // selected) must not write into the new selection's balance.
    const expectedChainId = selectedChainId;
    const expectedTokenId = selectedTokenId;
    const expectedCacheKey = `${expectedChainId}_${expectedTokenId}`;
    const reader = getSelectedChainReadContract();

    if (!reader || !userAccount || !wasmModule) return;

    const stillOnExpectedSelection = () =>
      loadingChainRef.current === expectedChainId &&
      loadingTokenRef.current === expectedTokenId &&
      selectedChainIdRef.current === expectedChainId;

    // Check if chain/token has changed during loading (race condition protection)
    if (!stillOnExpectedSelection()) {
      return;
    }

    // CRITICAL: If we just restored this exact key from cache, don't load —
    // preserve the cached value. UNLESS forceLoad (after deposit/transfer/etc.).
    if (
      retryCount === 0 &&
      !forceLoad &&
      justRestoredFromCacheRef.current &&
      lastLoadedKeyRef.current === expectedCacheKey
    ) {
      return;
    }

    // If forceLoad is true, clear the flag to allow this load
    if (forceLoad && justRestoredFromCacheRef.current) {
      justRestoredFromCacheRef.current = false;
    }

    // If no keypair is available, skip balance decryption
    // Don't clear the balance here - it might be temporarily unavailable during keypair derivation
    // The balance will be cleared in handleChainSelect when the chain actually changes
    if (!keypair) {
      return;
    }

    /** Commit available balance only if this load is still for the UI selection. */
    const commitAvailable = (amount) => {
      if (!stillOnExpectedSelection()) return;
      // Same-chain cache restore: don't clobber a just-restored value with a
      // soft-zero from a parallel empty read — but DO overwrite when this load
      // is for a different key than what was restored (cross-chain switch).
      if (
        justRestoredFromCacheRef.current &&
        lastLoadedKeyRef.current === expectedCacheKey &&
        amount === 0 &&
        !forceLoad
      ) {
        return;
      }
      setConfidentialBalance(amount);
      const cached = balanceCacheRef.current.get(expectedCacheKey) || {};
      balanceCacheRef.current.set(expectedCacheKey, {
        ...cached,
        available: amount,
      });
      if (lastLoadedKeyRef.current === expectedCacheKey) {
        justRestoredFromCacheRef.current = false;
      }
    };

    try {
      // Get account data from the selected chain (not the wallet's lagged network)
      const accountDataTuple = await reader.getAccountCore(userAccount);
      if (!stillOnExpectedSelection()) return;

      const hasActiveAccountLocal = accountDataTuple[0] && accountDataTuple[1]; // exists && finalized

      // If account is active but props haven't been updated, refresh account status
      // This fixes the issue where account data shows active but deposit button is still disabled
      if (
        hasActiveAccountLocal &&
        (!accountExists || !hasActiveAccount || isCreatingAccount) &&
        onOperationCompleteRef.current
      ) {
        // Balance load proved the account is live — clear creating CTA immediately
        // even if parent props are still stale.
        if (isCreatingAccount) {
          setIsCreatingAccount(false);
          setAccountCreationStep("");
          accountCreationInProgressRef.current = false;
        }
        // Use setTimeout to avoid calling during render
        setTimeout(() => {
          if (onOperationCompleteRef.current) {
            onOperationCompleteRef.current();
          }
        }, 0);
      }

      if (!hasActiveAccountLocal) {
        // Always soft-zero for THIS chain/token — never leave a previous
        // chain's CUSDT painted on Base (or vice versa).
        commitAvailable(0);
        return;
      }

      // Get available balance ciphertext from contract
      const currentTokenData = getTokenData();
      if (
        !currentTokenData?.address ||
        currentTokenData.chainId !== expectedChainId ||
        selectedTokenId !== expectedTokenId
      ) {
        return;
      }
      const tokenAddr = currentTokenData.address;
      const [availC1, availC2] = await reader.getAvailable(
        userAccount,
        tokenAddr,
      );

      if (!stillOnExpectedSelection()) return;

      // Check if ciphertext is truly uninitialized (empty strings, not zero values)
      // Note: C2 can be all zeros and still be a valid ciphertext that decrypts correctly
      const c1Empty = !availC1 || availC1 === "0x";
      const c2Empty = !availC2 || availC2 === "0x";

      if (c1Empty && c2Empty) {
        commitAvailable(0);
        return;
      }

      // Convert hex to bytes and combine into 64-byte ciphertext (32 bytes commitment + 32 bytes handle)
      const c1Bytes = hexToBytes(availC1.slice(2));
      const c2Bytes = hexToBytes(availC2.slice(2));
      const combinedBytes = new Uint8Array(64);
      combinedBytes.set(c1Bytes, 0);
      combinedBytes.set(c2Bytes, 32);
      const combinedCiphertext = btoa(String.fromCharCode(...combinedBytes));

      // Decrypt the balance using the user's derived secret key
      try {
        // Check if keypair is available
        if (!keypair || !keypair.private_key) {
          commitAvailable(0);
          return;
        }

        // Create secure wrapper for keypair
        const secureKeypair = createSecureKeypair(keypair);

        // Get the private key before clearing
        const privateKey = secureKeypair.getPrivateKey();
        if (!privateKey) {
          throw new Error("Failed to retrieve private key from secure wrapper");
        }

        // Validate inputs before decryption
        if (!combinedCiphertext || combinedCiphertext.length === 0) {
          throw new Error("Invalid ciphertext: empty or null");
        }
        if (!privateKey || privateKey.length === 0) {
          throw new Error("Invalid keypair: empty or null");
        }

        // Ensure WASM module is initialized before calling decrypt_ciphertext
        // The decrypt_ciphertext function uses a global wasm variable that needs to be initialized
        try {
          await init();
        } catch (initError) {
          secureKeypair.clear();
          if (retryCount === 0) {
            console.error("Failed to initialize WASM module:", initError);
          }
          throw new Error("Could not start the crypto module. Refresh the page and try again.");
        }

        let decryptedResult;
        try {
          decryptedResult = decrypt_ciphertext(combinedCiphertext, privateKey);
        } catch (wasmError) {
          // Clear the secure keypair from memory before rethrowing
          secureKeypair.clear();
          if (retryCount === 0) {
            console.error("WASM decrypt_ciphertext threw an error:", wasmError);
            console.error("Error details:", {
              message: wasmError?.message,
              stack: wasmError?.stack,
              name: wasmError?.name,
              toString: String(wasmError),
            });
          }
          throw wasmError;
        }

        // Clear the secure keypair from memory
        secureKeypair.clear();

        if (decryptedResult === undefined || decryptedResult === null) {
          throw new Error("WASM decryption returned undefined/null");
        }

        // Parse JSON response from WASM
        const result = JSON.parse(decryptedResult);

        // Check again if selection has changed after decryption
        if (!stillOnExpectedSelection()) {
          return;
        }

        const numericBalance = parseFloat(result.decrypted_amount);
        if (isNaN(numericBalance)) {
          if (retryCount === 0) {
            console.error(
              "Decrypted amount is not a number:",
              result.decrypted_amount,
            );
          }
          commitAvailable(0);
        } else {
          commitAvailable(numericBalance);
        }
      } catch (decryptError) {
        // Always log the error on first attempt for debugging
        if (retryCount === 0) {
          console.error("Decryption error (attempt 1):", decryptError);
          console.error("Error message:", decryptError?.message);
          console.error("Error stack:", decryptError?.stack);
        }

        // Retry logic for decryption errors
        if (retryCount < MAX_RETRIES) {
          await new Promise((resolve) =>
            setTimeout(resolve, 300 * (retryCount + 1)),
          );
          if (!stillOnExpectedSelection()) return;
          return loadConfidentialBalance(retryCount + 1, forceLoad);
        }

        // Log error details after all retries failed
        const errorMsg = decryptError?.message || String(decryptError);
        console.error("WASM decryption error after all retries:", {
          error: decryptError,
          message: errorMsg,
          retryCount: retryCount + 1,
        });

        commitAvailable(0);
      }
    } catch (error) {
      if (!stillOnExpectedSelection()) return;

      // getAccountCore reverts with CALL_EXCEPTION when the confidential
      // account does not exist yet — that is the expected cold-start path,
      // not a balance-loading failure. Same shape also appears briefly after
      // a mainnet↔testnet switch while the provider catches up; either way
      // surface 0 and stop retrying as an error.
      if (isCallException(error)) {
        commitAvailable(0);
        return;
      }

      // Retry logic for general errors
      // For SSL errors, use longer delays as they're often infrastructure issues
      const isSSL = isSSLError(error);
      const retryDelay = isSSL
        ? 2000 * (retryCount + 1)
        : 300 * (retryCount + 1);

      if (retryCount < MAX_RETRIES) {
        if (isSSL) {
          console.warn(
            `SSL error detected, retrying with longer delay (attempt ${retryCount + 1}/${MAX_RETRIES})...`,
          );
        }
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
        if (!stillOnExpectedSelection()) return;
        return loadConfidentialBalance(retryCount + 1, forceLoad);
      }

      // Only log error if all retries failed
      console.error("Error loading confidential balance after retries:", error);

      // Show user-friendly error message for SSL errors
      if (isSSL) {
        const chainConfig = CHAINS[expectedChainId];
        const chainName = chainConfig?.name || "this network";
        console.error(getNetworkErrorMessage(error, chainName));
      }

      commitAvailable(0);
    }
  };

  const loadPendingBalance = async (retryCount = 0, forceLoad = false) => {
    const MAX_RETRIES = 2; // Will try up to 3 times total (initial + 2 retries)
    const expectedChainId = selectedChainId;
    const expectedTokenId = selectedTokenId;

    // Prevent multiple simultaneous loads
    if (retryCount === 0 && loadingPendingBalanceRef.current) {
      return;
    }

    const reader = getSelectedChainReadContract();

    if (!reader || !userAccount || !wasmModule) {
      loadingPendingBalanceRef.current = false;
      return;
    }

    // Check if chain has changed during loading (race condition protection)
    if (
      loadingChainRef.current !== expectedChainId ||
      loadingTokenRef.current !== expectedTokenId
    ) {
      loadingPendingBalanceRef.current = false;
      return;
    }

    const stillOnExpectedSelection = () =>
      loadingChainRef.current === expectedChainId &&
      loadingTokenRef.current === expectedTokenId &&
      selectedChainIdRef.current === expectedChainId;

    // CRITICAL: If we just restored from cache, don't load - preserve the cached value
    // UNLESS forceLoad is true (e.g., after operations that change the balance)
    const cacheKey = `${expectedChainId}_${expectedTokenId}`;
    if (
      retryCount === 0 &&
      !forceLoad &&
      justRestoredFromCacheRef.current &&
      lastLoadedKeyRef.current === cacheKey
    ) {
      loadingPendingBalanceRef.current = false;
      return;
    }

    // If forceLoad is true, clear the flag to allow this load
    if (forceLoad && justRestoredFromCacheRef.current) {
      justRestoredFromCacheRef.current = false;
    }

    // If no keypair is available, skip balance decryption
    // Don't clear pending balance here - it might just be temporarily unavailable during chain switches
    // The balance will be cleared in handleChainSelect when the chain actually changes
    if (!keypair) {
      loadingPendingBalanceRef.current = false;
      return;
    }

    // Mark as loading (only on first attempt)
    if (retryCount === 0) {
      loadingPendingBalanceRef.current = true;
    }

    try {
      if (retryCount === 0) {
      }

      // Get account data from the selected chain (not the wallet's lagged network)
      const accountDataTuple = await reader.getAccountCore(userAccount);
      const hasActiveAccount = accountDataTuple[0] && accountDataTuple[1]; // exists && finalized

      // Check chain again before proceeding (async operations might take time)
      if (!stillOnExpectedSelection()) {
        loadingPendingBalanceRef.current = false;
        return;
      }

      if (!hasActiveAccount) {
        if (retryCount === 0) {
        }
        // Only set if chain hasn't changed
        if (stillOnExpectedSelection()) {
          setPendingBalance(0);
          // Update cache
          const cached = balanceCacheRef.current.get(cacheKey) || {};
          balanceCacheRef.current.set(cacheKey, { ...cached, pending: 0 });
        }
        loadingPendingBalanceRef.current = false;
        return;
      }

      // Get pending balance ciphertext from contract
      const currentTokenData = getTokenData();
      const tokenAddr = currentTokenData.address; // Use selected token for balance check
      const [pendingC1, pendingC2] = await reader.getPending(
        userAccount,
        tokenAddr,
      );

      // Check chain again after async operation
      if (!stillOnExpectedSelection()) {
        loadingPendingBalanceRef.current = false;
        return;
      }

      if (retryCount === 0) {
      }

      // Check if ciphertext is empty (no pending balance)
      if (
        pendingC1 === "0x" ||
        pendingC2 === "0x" ||
        pendingC1 ===
          "0x0000000000000000000000000000000000000000000000000000000000000000" ||
        pendingC2 ===
          "0x0000000000000000000000000000000000000000000000000000000000000000"
      ) {
        if (retryCount === 0) {
        }
        // Empty ciphertext means there is genuinely no pending balance on-chain
        // (e.g. right after a successful secure claim). Always clear the local
        // value to 0 unless we just restored it from cache during a chain/token
        // switch — otherwise the header/banner keep showing a stale amount.
        if (
          stillOnExpectedSelection() &&
          !justRestoredFromCacheRef.current
        ) {
          if (retryCount === 0) {
          }
          setPendingBalance(0);
          // Update cache
          const cached = balanceCacheRef.current.get(cacheKey) || {};
          balanceCacheRef.current.set(cacheKey, { ...cached, pending: 0 });
        } else if (justRestoredFromCacheRef.current) {
          if (retryCount === 0) {
          }
        }
        loadingPendingBalanceRef.current = false;
        return;
      }

      // Convert hex to bytes and combine into 64-byte ciphertext (32 bytes commitment + 32 bytes handle)
      const p1Bytes = hexToBytes(pendingC1.slice(2));
      const p2Bytes = hexToBytes(pendingC2.slice(2));
      const combinedPendingBytes = new Uint8Array(64);
      combinedPendingBytes.set(p1Bytes, 0);
      combinedPendingBytes.set(p2Bytes, 32);
      const combinedPendingCiphertext = btoa(
        String.fromCharCode(...combinedPendingBytes),
      );

      if (retryCount === 0) {

      }

      // Decrypt the pending balance using the user's derived secret key
      try {
        const decryptedPendingResult = decrypt_ciphertext(
          combinedPendingCiphertext,
          keypair.private_key,
        );

        if (retryCount === 0) {
        }

        if (
          decryptedPendingResult === undefined ||
          decryptedPendingResult === null
        ) {
          throw new Error("WASM decryption returned undefined/null");
        }

        // Parse JSON response from WASM
        const result = JSON.parse(decryptedPendingResult);
        if (retryCount === 0) {
        }

        // Check chain again before setting state (decryption might have taken time)
        if (!stillOnExpectedSelection()) {
          loadingPendingBalanceRef.current = false;
          return;
        }

        const numericPendingBalance = parseFloat(result.decrypted_amount);
        if (isNaN(numericPendingBalance)) {
          if (retryCount === 0) {
            console.error(
              "Decrypted pending amount is not a number:",
              result.decrypted_amount,
            );
          }
          // Only set if chain hasn't changed and we didn't just restore from cache
          if (
            stillOnExpectedSelection() &&
            !justRestoredFromCacheRef.current
          ) {
            setPendingBalance(0);
          }
        } else {
          // Only set if chain hasn't changed
          if (stillOnExpectedSelection()) {
            setPendingBalance(numericPendingBalance);
            // Update cache with the new pending balance
            const cached = balanceCacheRef.current.get(cacheKey) || {};
            balanceCacheRef.current.set(cacheKey, {
              ...cached,
              pending: numericPendingBalance,
            });
            // Only clear the flag if this was the key we restored from cache
            if (lastLoadedKeyRef.current === cacheKey) {
              justRestoredFromCacheRef.current = false;
            }
          }
        }
        // Mark as not loading after successful completion
        loadingPendingBalanceRef.current = false;
      } catch (decryptError) {
        // Retry logic for decryption errors
        if (retryCount < MAX_RETRIES) {
          await new Promise((resolve) =>
            setTimeout(resolve, 300 * (retryCount + 1)),
          );
          return loadPendingBalance(retryCount + 1);
        }

        // Only log error details if all retries failed, but don't spam console
        // Decryption errors can happen if keypair doesn't match the ciphertext (e.g., after chain change)
        if (retryCount === MAX_RETRIES) {
          // Only log once when all retries are exhausted, and only if it's not likely a chain mismatch
          const errorMsg = decryptError?.message || String(decryptError);
          if (!errorMsg.includes("decode discrete log")) {
            // If it's not the expected discrete log error, log it
            console.error(
              "WASM pending decryption error after retries:",
              decryptError,
            );
          }
          // Silently set to 0 - this is expected if keypair doesn't match ciphertext
        }
        // Only set if chain hasn't changed
        if (stillOnExpectedSelection()) {
          setPendingBalance(0);
        }
        // Mark as not loading after error handling
        loadingPendingBalanceRef.current = false;
      }
    } catch (error) {
      // Check chain before retrying
      if (!stillOnExpectedSelection()) {
        loadingPendingBalanceRef.current = false;
        return;
      }

      // No confidential account yet (or brief wrong-chain lag after a
      // mainnet↔testnet switch) — soft-zero, don't treat as a hard failure.
      if (isCallException(error)) {
        if (
          stillOnExpectedSelection() &&
          !justRestoredFromCacheRef.current
        ) {
          setPendingBalance(0);
        }
        loadingPendingBalanceRef.current = false;
        return;
      }

      // Retry logic for general errors
      if (retryCount < MAX_RETRIES) {
        await new Promise((resolve) =>
          setTimeout(resolve, 300 * (retryCount + 1)),
        );
        return loadPendingBalance(retryCount + 1);
      }

      // Only log error if all retries failed
      console.error("Error loading pending balance after retries:", error);
      // Only set if chain hasn't changed and we didn't just restore from cache
      if (
        stillOnExpectedSelection() &&
        !justRestoredFromCacheRef.current
      ) {
        setPendingBalance(0);
      }
      // Mark as not loading after all retries exhausted
      loadingPendingBalanceRef.current = false;
    }
  };

  const updateTokenBalance = async (retryCount = 0) => {
    // Prefer App.js useBalances() — it loads USDC and USDT independently for
    // the selected chain, so Deposit can switch tokens without a racing
    // single-token `balance` string. Callers (faucet, deposit events, …)
    // still invoke this helper to refresh.
    if (typeof refreshWalletBalances === "function") {
      try {
        await refreshWalletBalances();
      } catch (error) {
        console.error("Error refreshing wallet balances:", error);
      }
      return;
    }

    const MAX_RETRIES = 3;
    const RETRY_DELAY = 1000; // Start with 1 second

    // Fallback path if parent did not pass refreshWalletBalances.
    if (!userAccount) {
      if (balance === "Loading...") {
        return;
      }
      setBalance("Loading...");
      return;
    }

    // Stamp this read so a slower USDC response can't overwrite USDT (or
    // vice versa) after the user switches tokens mid-flight.
    const requestId =
      retryCount === 0
        ? (walletBalanceRequestIdRef.current += 1)
        : walletBalanceRequestIdRef.current;
    const expectedChainId = selectedChainId;
    const expectedTokenId = selectedTokenId;

    const isStale = () =>
      requestId !== walletBalanceRequestIdRef.current ||
      selectedChainId !== expectedChainId ||
      selectedTokenId !== expectedTokenId;

    try {
      const currentTokenData = getTokenData();
      if (!currentTokenData || !currentTokenData.address) {
        if (!isStale()) setBalance("Token not found");
        return;
      }
      const expectedTokenAddress = String(currentTokenData.address).toLowerCase();

      const selectedCfg = CHAINS[selectedChainId];
      const tokenReadProvider = selectedCfg?.rpcUrl
        ? new ethers.providers.JsonRpcProvider(selectedCfg.rpcUrl)
        : contract?.provider || provider;
      if (!tokenReadProvider) {
        if (!isStale() && (balance === "Loading..." || !balance)) {
          setBalance("Loading...");
        }
        return;
      }

      const tokenContract = new ethers.Contract(
        currentTokenData.address,
        [
          "function balanceOf(address) view returns (uint256)",
          "function decimals() view returns (uint8)",
          "function symbol() view returns (string)",
        ],
        tokenReadProvider,
      );

      const rawBalance = await tokenContract.balanceOf(userAccount);
      if (isStale()) return;

      // Re-resolve after the await — selection must still match this read's token.
      const latestTokenData = getTokenData();
      if (
        !latestTokenData?.address ||
        String(latestTokenData.address).toLowerCase() !== expectedTokenAddress
      ) {
        return;
      }

      const decimals =
        latestTokenData.decimals ?? (await tokenContract.decimals());
      if (isStale()) return;
      const formattedBalance = ethers.utils.formatUnits(rawBalance, decimals);

      // Use the proper token name from configuration instead of contract symbol
      // Clean up the formatted balance to remove trailing zeros
      const cleanBalance = parseFloat(formattedBalance);

      // Ensure we have a valid number
      if (isNaN(cleanBalance) || !isFinite(cleanBalance)) {
        // Retry if we haven't exceeded max retries
        if (retryCount < MAX_RETRIES && !isStale()) {
          await sleep(RETRY_DELAY * (retryCount + 1)); // Exponential backoff
          if (isStale()) return;
          return updateTokenBalance(retryCount + 1);
        }
        if (!isStale()) setBalance("Error loading balance");
        return;
      }

      const tokenName =
        latestTokenData.name || latestTokenData.symbol || "Token";
      const balanceString = `${cleanBalance} ${tokenName}`;

      // Only update if we have a valid balance string and this read is still current
      if (isStale()) return;
      if (balanceString && balanceString.trim().length > 0) {
        setBalance(balanceString);
      } else {
        // Retry if we haven't exceeded max retries
        if (retryCount < MAX_RETRIES) {
          await sleep(RETRY_DELAY * (retryCount + 1)); // Exponential backoff
          if (isStale()) return;
          return updateTokenBalance(retryCount + 1);
        }
        if (!isStale()) setBalance("Error loading balance");
      }
    } catch (error) {
      if (isStale()) return;

      if (isCallException(error) && retryCount < MAX_RETRIES) {
        await sleep(RETRY_DELAY * (retryCount + 1));
        if (isStale()) return;
        return updateTokenBalance(retryCount + 1);
      }

      if (!isCallException(error)) {
        console.error("Error fetching token balance:", error);
      }

      // Detect SSL errors and use longer retry delays
      const isSSL = isSSLError(error);
      const retryDelay = isSSL
        ? RETRY_DELAY * 5 * (retryCount + 1)
        : RETRY_DELAY * (retryCount + 1);

      // Retry if we haven't exceeded max retries
      if (retryCount < MAX_RETRIES) {
        if (isSSL) {
          console.warn(
            `SSL error detected, retrying with longer delay (attempt ${retryCount + 1}/${MAX_RETRIES})...`,
          );
        }
        await sleep(retryDelay); // Exponential backoff, longer for SSL errors
        if (isStale()) return;
        return updateTokenBalance(retryCount + 1);
      }

      if (isStale()) return;

      // Show more specific error message for SSL errors
      if (isSSL) {
        const chainConfig = CHAINS[selectedChainId];
        const chainName = chainConfig?.name || "this network";
        console.error(getNetworkErrorMessage(error, chainName));
        setBalance("SSL Error - Network issue");
      } else if (isCallException(error)) {
        // Token may not exist on this chain / RPC still settling — keep
        // Loading rather than a hard error so Deposit can re-fetch.
        setBalance("Loading...");
      } else {
        setBalance("Error loading balance");
      }
    }
  };

  const handlePercentageClick = (percentage) => {
    // Percent chips are disabled until connected; if the balance simply
    // hasn't loaded yet, no-op silently rather than firing a popup.
    if (confidentialBalance === null) return;

    const decimalsForDisplay = 2;
    // Convert raw balance to user-facing amount (contract stores as amount * 100)
    const userFacingBalance = confidentialBalance / 100;
    let amountValue =
      percentage === "max"
        ? userFacingBalance
        : userFacingBalance * (percentage / 100);

    // Cap amount to u32 max when multiplied by 100 (contract format). The
    // filled-in numeral shows the cap, so no separate notification is needed.
    const U32_MAX = 4294967295; // 2^32 - 1
    const maxAmount = U32_MAX / 100;
    if (amountValue > maxAmount) amountValue = maxAmount;

    const formattedAmount = formatTokenAmount(amountValue, decimalsForDisplay);
    setTransferAmount(formattedAmount);
    setTransferAmountError(""); // Clear any previous error since we're setting a valid amount
  };

  const handleWithdrawPercentageClick = (percentage) => {
    if (confidentialBalance === null) return;

    const decimalsForDisplay = 2;
    // Convert raw balance to user-facing amount (contract stores as amount * 100)
    const userFacingBalance = confidentialBalance / 100;
    let amountValue =
      percentage === "max"
        ? userFacingBalance
        : userFacingBalance * (percentage / 100);

    // Cap amount to u32 max when multiplied by 100 (contract format).
    const U32_MAX = 4294967295; // 2^32 - 1
    const maxAmount = U32_MAX / 100;
    if (amountValue > maxAmount) amountValue = maxAmount;

    const formattedAmount = formatTokenAmount(amountValue, decimalsForDisplay);
    setWithdrawAmount(formattedAmount);
    setWithdrawAmountError(""); // Clear any previous error since we're setting a valid amount
  };

  // Wallet ERC-20 balance for the currently selected token. Sourced from
  // App.js useBalances() which loads USDC and USDT independently per chain —
  // never from the legacy shared `balance` string (that raced on token switch).
  const getRegularBalanceValue = () => {
    if (!selectedTokenId || !walletBalances) return null;
    const entry = walletBalances[selectedTokenId];
    if (!entry || entry.balance == null) return null;
    const balanceValue = Number(entry.balance);
    return !isNaN(balanceValue) && isFinite(balanceValue) ? balanceValue : null;
  };

  // True only when the selected token's wallet balance has loaded and is 0.
  const hasInsufficientBalance = () => {
    const balanceValue = getRegularBalanceValue();
    return (
      balanceValue !== null &&
      !isNaN(balanceValue) &&
      isFinite(balanceValue) &&
      balanceValue === 0
    );
  };

  const handleDepositPercentageClick = (percentage) => {
    // Percent chips are disabled until connected / until the wallet balance
    // has loaded, and the CTA already says "Insufficient <token>" for a zero
    // balance — so every one of these is a silent no-op, not a popup.
    if (!userAccount) return;
    if (hasInsufficientBalance()) return;
    const balanceValue = getRegularBalanceValue();
    if (balanceValue == null) return;

    // Allow 0 balance (user might want to deposit 0, though unlikely)
    if (balanceValue === 0) {
      setDepositAmount("0");
      return;
    }

    const decimalsForDisplay = 2;
    let amountValue =
      percentage === "max" ? balanceValue : balanceValue * (percentage / 100);

    // Cap amount to max deposit limit (40M tokens). The filled numeral shows
    // the cap, so no separate notification is needed.
    const MAX_DEPOSIT_TOKENS = 40000000;

    if (amountValue > MAX_DEPOSIT_TOKENS) {
      amountValue = MAX_DEPOSIT_TOKENS;
    }

    const formattedAmount = formatTokenAmount(amountValue, decimalsForDisplay);
    setDepositAmount(formattedAmount);
    setDepositAmountError(""); // Clear any previous error since we're setting a valid amount
  };

  const getRecipientPublicKey = async (recipientAddress) => {
    try {
      // Selected-chain RPC — same reason as checkRecipientAccount: wallet
      // provider can still be on the previous network after a UI switch.
      const reader = getSelectedChainReadContract() || contract;
      const recipientAccountTuple =
        await reader.getAccountCore(recipientAddress);
      const hasActiveAccount =
        recipientAccountTuple[0] && recipientAccountTuple[1]; // exists && finalized

      if (!hasActiveAccount) {
        throw new Error(
          "Recipient does not have an active Stabletrust Pay account",
        );
      }

      const rawPubkey = recipientAccountTuple[4]; // elgamalPubkey is at index 4
      return rawPubkey;
    } catch (error) {
      console.error("Error fetching recipient public key:", error);
      throw error;
    }
  };

  const validateTransferInputs = async () => {
    const trimmedRecipient = recipient.trim();
    if (!transferAmount || !trimmedRecipient)
      return "Please enter amount and recipient address";
    if (transferAmountError) return transferAmountError;
    const transferAmountValue = parseFloat(transferAmount);
    if (isNaN(transferAmountValue) || transferAmountValue < 0)
      return "Amount cannot be negative";

    // Check if amount exceeds u32 max when multiplied by 100 (contract format)
    const currentTokenData = getTokenData();
    const U32_MAX = 4294967295; // 2^32 - 1
    const rawAmount = transferAmountValue * 100;
    if (rawAmount > U32_MAX) {
      const maxAmount = U32_MAX / 100;
      return `Amount exceeds maximum (${Math.floor(maxAmount)} ${currentTokenData?.symbol || "tokens"})`;
    }

    // Fetch fresh account data from contract to ensure we have latest state
    let currentAccountData = accountData;
    try {
      const accountDataTuple = await contract.getAccountCore(userAccount);
      currentAccountData = {
        exists: accountDataTuple[0],
        finalized: accountDataTuple[1],
        pendingAction: accountDataTuple[2],
      };
    } catch (error) {
      console.error(
        "Error fetching fresh account data for transfer validation:",
        error,
      );
    }

    // Check for pending action using fresh data
    if (currentAccountData && currentAccountData.pendingAction) {
      const pendingMessage = await getPendingActionMessage();
      return pendingMessage;
    }

    // Check if user can submit operations using fresh account data
    const canSubmit =
      currentAccountData &&
      currentAccountData.exists &&
      currentAccountData.finalized &&
      !currentAccountData.pendingAction;
    if (!canSubmit)
      return "Account not ready. Wait until it is finalized and no requests are in progress.";
    if (!keypair)
      return "Keypair not ready. Please wait for key generation to complete.";
    if (confidentialBalance === null)
      return "Balance not loaded. Please wait and try again.";
    if (!isValidRecipientAddress(trimmedRecipient))
      return "Enter a valid recipient address";
    if (isSelfTransfer(trimmedRecipient)) return SELF_TRANSFER_ERROR;
    if (recipientAccountStatus === "no_account")
      return NO_RECIPIENT_ACCOUNT_ERROR;
    if (recipientAccountStatus === "checking")
      return "Checking recipient account…";

    const userFacingBalance = confidentialBalance / 100;
    if (userFacingBalance < transferAmountValue)
      return `Insufficient balance. Available: ${formatTokenAmount(userFacingBalance)}, Requested: ${formatTokenAmount(transferAmountValue)}`;
    return null;
  };

  const fetchCurrentBalanceCiphertext = async (tokenAddr) => {
    const [availC1, availC2] = await contract.getAvailable(
      userAccount,
      tokenAddr,
    );
    const availC1Bytes = hexToBytes(availC1.slice(2));
    const availC2Bytes = hexToBytes(availC2.slice(2));
    const combinedCiphertextBytes = new Uint8Array(64);
    combinedCiphertextBytes.set(availC1Bytes, 0);
    combinedCiphertextBytes.set(availC2Bytes, 32);
    return btoa(String.fromCharCode(...combinedCiphertextBytes));
  };

  /** Decrypt on-chain available balance; use at proof time (not cached UI state). */
  const decryptBalanceFromCiphertext = async (ciphertextBase64, keypairBase64) => {
    if (!ciphertextBase64 || !keypairBase64) {
      throw new Error("Ciphertext and keypair are required to decrypt balance");
    }
    await init();
    const decryptedResult = decrypt_ciphertext(ciphertextBase64, keypairBase64);
    const result = JSON.parse(decryptedResult);
    const amount = Number(result.decrypted_amount);
    if (!Number.isFinite(amount) || amount < 0) {
      throw new Error(
        "Failed to decrypt current balance from contract ciphertext",
      );
    }
    return Math.floor(amount);
  };

  const encodeRecipientPubkey = (recipientPubkey) => {
    if (!recipientPubkey)
      throw new Error("Invalid recipient public key: null or undefined");


    if (typeof recipientPubkey === "string") {
      if (recipientPubkey.startsWith("0x")) {
        const hexString = recipientPubkey.slice(2);

        // Convert hex to string to check if it's JSON
        const hexBytes = new Uint8Array(
          hexString.match(/.{2}/g).map((byte) => parseInt(byte, 16)),
        );
        const decodedString = String.fromCharCode(...hexBytes);


        // Check if it's a JSON object containing a key
        try {
          const jsonObj = JSON.parse(decodedString);
          if (jsonObj && typeof jsonObj === "object" && jsonObj.key) {
            return jsonObj.key; // Return the base64 key directly
          }
        } catch (e) {
        }

        // If not JSON, treat as raw bytes
        const encoded = btoa(String.fromCharCode(...hexBytes));
        return encoded;
      }

      // Check if it's already base64
      try {
        atob(recipientPubkey);
        return recipientPubkey;
      } catch (_) {
        let keyBytes;
        if (
          recipientPubkey.length % 2 === 0 &&
          /^[0-9a-fA-F]+$/.test(recipientPubkey)
        ) {
          keyBytes = new Uint8Array(
            recipientPubkey.match(/.{2}/g).map((byte) => parseInt(byte, 16)),
          );
        } else {
          keyBytes = new Uint8Array(recipientPubkey.split(",").map(Number));
        }
        const encoded = btoa(String.fromCharCode(...keyBytes));
        return encoded;
      }
    }

    if (Array.isArray(recipientPubkey)) {
      const encoded = btoa(
        String.fromCharCode(...new Uint8Array(recipientPubkey)),
      );
      return encoded;
    }

    throw new Error("Invalid recipient public key: unsupported format");
  };

  // Helper function to get gas fee overrides with buffer to prevent "maxFeePerGas less than block base fee" errors
  const getGasFeeOverrides = async () => {
    try {
      const provider = contract.signer?.provider;
      if (provider) {
        const feeData = await provider.getFeeData();
        if (feeData.maxFeePerGas && feeData.maxPriorityFeePerGas) {
          // Add 20% buffer to maxFeePerGas to account for base fee increases
          const bufferedMaxFee = feeData.maxFeePerGas.mul(120).div(100);
          return {
            maxFeePerGas: bufferedMaxFee,
            maxPriorityFeePerGas: feeData.maxPriorityFeePerGas,
          };
        }
      }
    } catch (feeError) {
      console.warn(
        "Failed to get fee data, proceeding without gas overrides:",
        feeError,
      );
    }
    return {};
  };

  const generateTransferProof = (
    currentBalanceCiphertext,
    privateKey,
    encodedRecipientPubkey,
  ) => {
    const currentTokenData = getTokenData();
    // Scale the transfer amount to contract format (amount * 100, limited to 2 decimal places)
    // Use parseFloat to handle decimal values correctly (e.g., 0.5)
    const transferAmountScaled = Math.floor(parseFloat(transferAmount) * 100);

    const transferInput = {
      current_balance_ciphertext: currentBalanceCiphertext,
      current_balance: confidentialBalance,
      transfer_amount: transferAmountScaled,
      source_keypair: privateKey,
      destination_pubkey: encodedRecipientPubkey,
    };

    // Debug logging to verify amounts

    const proofResult = generate_transfer_proof(JSON.stringify(transferInput));
    const proof = JSON.parse(proofResult);
    if (!proof.success)
      throw new Error(proof.error || "Proof generation failed");

    // Extract randomness from proof data if available
    const randomness = {
      sender: proof.data?.sender_randomness || null,
      receiver: proof.data?.receiver_randomness || null,
    };

    // Log randomness values for verification
    if (randomness.sender) {
    }
    if (randomness.receiver) {
    }

    return { proof, randomness };
  };

  const submitTransfer = async (recipient, proofData) => {
    const currentTokenData = getTokenData();
    const transferProofData = encodeProofData(proofData.proof);
    let overrides = {};

    // Protocol fee: native fees attach as msg.value; ERC-20 fees need approval
    // (SDK 2.0.11+: nonAnonymousTransferFee). Tempo PathUSD is one ERC-20 case.
    const chainConfig = CHAINS[selectedChainId];
    const offchainZKP = shouldUseOffchainZKP(chainConfig?.chainIdDecimal);

    try {
      const [feeTokenAddr, feeAmt] = await Promise.all([
        contract.feeToken(),
        contract.nonAnonymousTransferFee(),
      ]);

      if (!feeAmt.isZero()) {
        if (feeTokenAddr === ethers.constants.AddressZero) {
          overrides = { value: feeAmt };
        } else {
          const feeTokenContract = new ethers.Contract(
            feeTokenAddr,
            [
              "function allowance(address owner, address spender) view returns (uint256)",
              "function approve(address spender, uint256 amount) returns (bool)",
              "function balanceOf(address account) view returns (uint256)",
            ],
            contract.signer,
          );

          const [balance, currentAllowance] = await Promise.all([
            withRateLimitRetry(
              () => feeTokenContract.balanceOf(userAccount),
              { retries: 3, baseDelayMs: 1200 },
            ),
            withRateLimitRetry(
              () => feeTokenContract.allowance(userAccount, contract.address),
              { retries: 3, baseDelayMs: 1200 },
            ),
          ]);

          if (balance.lt(feeAmt)) {
            throw new Error(
              `Insufficient fee token balance. Required: ${feeAmt.toString()}, available: ${balance.toString()}, fee token: ${feeTokenAddr}`,
            );
          }

          if (currentAllowance.lt(feeAmt)) {
            const gasOverrides = await getGasFeeOverrides();
            const approveTx = await withRateLimitRetry(
              () =>
                feeTokenContract.approve(
                  contract.address,
                  ethers.constants.MaxUint256,
                  gasOverrides,
                ),
              { retries: 3, baseDelayMs: 1500 },
            );
            await approveTx.wait();
            await sleep(1500);
          }
        }
      }
    } catch (error) {
      console.error("Error preparing transfer fee:", error);
      throw new Error(
        `Failed to prepare transfer fee: ${getCleanErrorMessage(error)}`,
      );
    }

    // Add gas fee overrides to prevent "maxFeePerGas less than block base fee" errors
    const gasOverrides = await getGasFeeOverrides();
    const finalOverrides = { ...overrides, ...gasOverrides };

    // When offchainZKP is true (Tempo by default), upload proof bytes to StableTrust
    // IPFS and put only the bare CID on-chain — matching @fairblock/stabletrust 2.0.11+.
    let transferZkpArg = transferProofData;
    if (offchainZKP) {
      try {
        const proofBytes = ethers.utils.arrayify(transferProofData);
        const cid = await uploadBytesToIpfs(proofBytes, "transfer-proof.bin");
        const bareCid = String(cid || "").trim().replace(/^ipfs:\/\//i, "");
        if (!bareCid) {
          throw new Error("IPFS upload returned no CID");
        }
        transferZkpArg = ethers.utils.toUtf8Bytes(bareCid);
      } catch (ipfsError) {
        console.error("Failed to upload transfer proof to IPFS:", ipfsError);
        throw new Error(
          "Could not upload transfer proof. Try again.",
        );
      }
    }

    await refreshGasBreakdownForExactTx(
      "transfer",
      "transferConfidential",
      [recipient, currentTokenData.address, transferZkpArg, offchainZKP],
      { manualGasLimit: 800000, txOverrides: finalOverrides },
    );

    // Execute transfer with gas estimation fallback
    // Note: The utility will add its own gas overrides, so we merge them here
    const tx = await executeContractTxWithGasFallback(
      contract,
      "transferConfidential",
      [recipient, currentTokenData.address, transferZkpArg, offchainZKP, finalOverrides],
      {
        manualGasLimit: 800000, // Transfer operations need more gas due to proof verification
        rateLimitRetry: withRateLimitRetry,
        retryOptions: { retries: 3, baseDelayMs: 1500 },
      },
    );
    const receipt = await tx.wait();
    const requestId = await resolveOnChainRequestId(
      receipt,
      ["TransferRequested", "TransferProcessed"],
      PENDING_KIND_TRANSFER,
    );

    if (!requestId) {
      throw new Error(
        "No numeric on-chain txId found in transfer transaction receipt events",
      );
    }

    // Pass the transfer amount and randomness to addPendingRequest for IBE encryption
    await addPendingRequest(
      requestId,
      2,
      {
        sender: userAccount.toLowerCase(),
        token: currentTokenData.symbol,
        tokenAddress: currentTokenData.address,
        recipient,
      },
      tx.hash,
      transferAmount,
      proofData.randomness,
    );
    return tx.hash;
  };

  // Preconditions are already reflected on the CTA button (its label says
  // why it's disabled). If a deeper async check — one that needs a fresh
  // contract read (pending action started elsewhere, account not finalized
  // yet) — trips after the click, route the reason to the same inline slots
  // the CTA/inputs already use instead of firing a popup toast.
  const surfaceValidationMessage = (message, { amountSetter, recipientSetter }) => {
    if (!message || typeof message !== "string") return;
    if (/amount|balance|maximum|decimal|negative|insufficient/i.test(message)) {
      amountSetter?.(message);
    } else if (
      recipientSetter &&
      /recipient|receiver|address|account|yourself/i.test(message)
    ) {
      recipientSetter(message);
    } else {
      // Account-state race (pending / still finalizing) — refresh so the
      // button re-renders into "… in progress" / "Creating account…".
      checkPendingAction();
      onOperationComplete?.();
    }
  };

  const handleTransfer = async () => {
    // Check if keys need to be derived first, or if account creation failed
    // (a skipped onboarding reopens through triggerKeyDerivation too)
    if (needsOnboardingSetup || !keysDerived || accountCreationFailed) {
      triggerKeyDerivation();
      return;
    }

    const validationError = await validateTransferInputs();
    if (validationError) {
      surfaceValidationMessage(validationError, {
        amountSetter: setTransferAmountError,
        recipientSetter: setRecipientError,
      });
      return;
    }

    const normalizedRecipient = recipient.trim();
    if (!normalizedRecipient || !isValidRecipientAddress(normalizedRecipient)) {
      setRecipientError("Invalid address");
      return;
    }

    setIsProcessing(true);
    setTransferStatus("pending");

    try {
      const currentTokenData = getTokenData();
      const chainConfig = CHAINS[selectedChainId];
      if (!chainConfig?.contractAddress) {
        throw new Error("Contract address is not configured for this chain");
      }
      if (keyLoading) {
        throw new Error("Keys are still being derived. Wait a moment...");
      }
      if (!keypair || !keypair.private_key) {
        throw new Error(
          "Keypair not available. Please wait for key derivation to complete.",
        );
      }

      const { client, signer } = await getSessionForSelectedChain();
      const amountWei = ethers.utils
        .parseUnits(transferAmount, currentTokenData.decimals)
        .toString();

      const receipt = await client.confidentialTransfer(
        signer,
        normalizedRecipient,
        currentTokenData.address,
        amountWei,
        {
          ...SDK_UX_OPTIONS,
          offchainZKP: shouldUseOffchainZKP(chainConfig.chainIdDecimal),
        },
      );
      const txHash = receiptTxHash(receipt);
      if (!txHash) throw new Error("Transfer submitted but no tx hash returned");

      const submittedAmount = transferAmount;
      const submittedRecipient = normalizedRecipient;

      setTransferReceiptData({
        amount: submittedAmount,
        recipient: submittedRecipient,
        tokenSymbol: currentTokenData.symbol,
        chainName: currentTokenData.chainName,
        txHash,
        time: new Date(),
      });
      setTransferAmount("");
      setRecipient("");
      setTransferAmountError("");
      setRecipientError(null);
      setRecipientAccountStatus(null);

      await onOperationComplete();
      checkPendingAction();
      loadPendingBalance(0, true);
      updateTokenBalance();
      refreshBalancesForPrivy("transfer");
      setTransferStatus("done");
    } catch (error) {
      console.error("Error transferring tokens:", error);
      if (!isUserRejectedError(error)) {
        const cleanError = getCleanErrorMessage(error);
        showNotification(`Transfer failed: ${cleanError}`, "error");
      }
      resetTransferStatus();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeposit = async () => {
    // Check if keys need to be derived first, or if account creation failed
    // (a skipped onboarding reopens through triggerKeyDerivation too)
    if (needsOnboardingSetup || !keysDerived || accountCreationFailed) {
      triggerKeyDerivation();
      return;
    }

    // These are all already surfaced on the CTA (label + disabled state) and
    // inline under the amount field — the click can only reach here in a race,
    // so re-assert the inline error rather than firing a popup toast.
    if (!depositAmount) return;
    if (depositAmountError) return;

    const depositAmountValue = parseFloat(depositAmount);
    if (isNaN(depositAmountValue) || depositAmountValue < 0) {
      setDepositAmountError("Amount cannot be negative");
      return;
    }

    // Check if amount exceeds max deposit limit (40M tokens)
    const currentTokenData = getTokenData();
    const MAX_DEPOSIT_TOKENS = 40000000;

    if (depositAmountValue > MAX_DEPOSIT_TOKENS) {
      setDepositAmountError(
        `Amount exceeds maximum (${MAX_DEPOSIT_TOKENS} ${currentTokenData?.symbol || "tokens"})`,
      );
      return;
    }

    // Fetch fresh account data from the contract FIRST
    // to ensure we have the latest state (props may not have updated yet after previous operation)
    let currentAccountData = accountData;
    try {
      const accountDataTuple = await contract.getAccountCore(userAccount);
      currentAccountData = parseAccountCore(accountDataTuple);
    } catch (error) {
      console.error("Error fetching fresh account data:", error);
    }

    // Account-state races (a pending action or a not-yet-finalized account) —
    // refresh so the CTA re-renders into "… in progress" / "Creating account…"
    // instead of surfacing a popup toast.
    if (currentAccountData && currentAccountData.pendingAction) {
      checkPendingAction();
      onOperationComplete?.();
      return;
    }

    const canSubmit =
      currentAccountData &&
      currentAccountData.exists &&
      currentAccountData.finalized &&
      !currentAccountData.pendingAction;

    if (!canSubmit) {
      checkPendingAction();
      onOperationComplete?.();
      return;
    }

    setIsProcessing(true);
    setDepositStatus("pending");

    try {
      const currentTokenData = getTokenData();
      const chainConfig = CHAINS[selectedChainId];
      if (!chainConfig?.contractAddress) {
        throw new Error("Contract address is not configured for this chain");
      }
      if (!keypair || !keypair.private_key) {
        throw new Error(
          "Keypair not available. Please wait for key derivation to complete.",
        );
      }

      const { client, signer } = await getSessionForSelectedChain();
      const amountWei = ethers.utils
        .parseUnits(depositAmount, currentTokenData.decimals)
        .toString();

      const receipt = await client.confidentialDeposit(
        signer,
        currentTokenData.address,
        amountWei,
        SDK_UX_OPTIONS,
      );
      const txHash = receiptTxHash(receipt);
      if (!txHash) throw new Error("Deposit submitted but no tx hash returned");

      setDepositReceiptData({
        amount: depositAmount,
        recipient: userAccount,
        tokenSymbol: currentTokenData.symbol,
        chainName: currentTokenData.chainName,
        txHash,
        time: new Date(),
      });
      setDepositAmount("");
      setDepositAmountError("");

      const checkAddress = getTokenData()?.address;
      const depositAddress = currentTokenData?.address;
      if (
        checkAddress &&
        depositAddress &&
        typeof checkAddress === "string" &&
        typeof depositAddress === "string" &&
        checkAddress.toLowerCase() === depositAddress.toLowerCase() &&
        accountExists
      ) {
        setHasPendingDeposit(true);
        checkPendingAction();
      }

      await onOperationComplete();
      loadPendingBalance();
      updateTokenBalance();
      refreshBalancesForPrivy("deposit");
      setDepositStatus("done");
    } catch (error) {
      console.error("Error depositing tokens:", error);
      checkPendingAction();
      if (!isUserRejectedError(error)) {
        const cleanError = getCleanErrorMessage(error);
        const isRpcConfigError =
          typeof cleanError === "string" &&
          cleanError.toLowerCase().includes("wallet rpc returned an error");
        showNotification(
          `Deposit failed: ${cleanError}`,
          "error",
          isRpcConfigError,
        );
      }
      resetDepositStatus();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleApplyPending = async () => {
    // Check if keys need to be derived first, or if account creation failed
    // (a skipped onboarding reopens through triggerKeyDerivation too)
    if (needsOnboardingSetup || !keysDerived || accountCreationFailed) {
      triggerKeyDerivation();
      return;
    }

    // Fetch fresh account data from contract to ensure we have latest state
    let currentAccountData = accountData;
    try {
      const accountDataTuple = await contract.getAccountCore(userAccount);
      currentAccountData = {
        exists: accountDataTuple[0],
        finalized: accountDataTuple[1],
        pendingAction: accountDataTuple[2],
      };
    } catch (error) {
      console.error(
        "Error fetching fresh account data for secure claim:",
        error,
      );
    }

    // Account-state races — refresh so the claim banner / CTA re-renders to
    // reflect the pending action rather than firing a popup toast.
    if (currentAccountData && currentAccountData.pendingAction) {
      checkPendingAction();
      onOperationComplete?.();
      return;
    }

    const canSubmit =
      currentAccountData &&
      currentAccountData.exists &&
      !currentAccountData.pendingAction;
    if (!canSubmit) {
      checkPendingAction();
      onOperationComplete?.();
      return;
    }

    // If pendingBalance is null or 0, try to reload it first
    if (pendingBalance === null || pendingBalance === 0) {
      await loadPendingBalance(0, true); // Force load

      // Nothing to claim — dismiss the claim prompt silently (the banner
      // only shows when there IS a pending balance, so this is a no-op race).
      if (pendingBalance === null || pendingBalance === 0) {
        setShowPendingNotification(false);
        return;
      }
    }

    setIsApplyingPending(true);

    // Close the notification when user clicks secure claim
    setShowPendingNotification(false);
    setPendingNotificationDismissed(true);

    try {
      const chainConfig = CHAINS[selectedChainId];
      if (!chainConfig?.contractAddress) {
        throw new Error("Contract address is not configured for this chain");
      }
      if (!keypair || !keypair.private_key) {
        throw new Error(
          "Keypair not available. Please wait for key derivation to complete.",
        );
      }

      const { client, signer } = await getSessionForSelectedChain();
      const currentTokenData = getTokenData();
      const receipt = await applyPendingWithClient(client, signer, {
        ...SDK_UX_OPTIONS,
        tokenAddress: currentTokenData?.address,
        pendingAmount:
          pendingBalance === null || pendingBalance === undefined
            ? undefined
            : pendingBalance.toString(),
      });
      const txHash = receiptTxHash(receipt);
      if (!txHash) {
        throw new Error("Secure claim submitted but no tx hash returned");
      }

      if (onSecureClaimInFlightChange) {
        onSecureClaimInFlightChange(true, getSecureClaimKey());
      }

      const numericChainId = chainConfig?.chainIdDecimal || null;
      const explorerUrl = numericChainId
        ? getExplorerTxUrl(numericChainId, txHash)
        : null;
      const shortTxHash = `${txHash.slice(0, 6)}...${txHash.slice(-4)}`;
      const claimAmount = formatTokenAmount(pendingBalance / 100, 2);

      showNotification(
        <>
          Secure claim initiated for {claimAmount} C{currentTokenData.symbol}{" "}
          (tx:{" "}
          {explorerUrl ? (
            <a
              href={explorerUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "inherit", textDecoration: "underline" }}
              onClick={(e) => e.stopPropagation()}
            >
              {shortTxHash}
            </a>
          ) : (
            shortTxHash
          )}
          ). It might take a few moments before the change takes effect.
        </>,
        "success",
      );

      onOperationComplete();
      checkPendingAction();
      setTimeout(async () => {
        loadPendingBalance();
        updateTokenBalance();
        refreshBalancesForPrivy("applyPending");
      }, 3000);
    } catch (error) {
      console.error("Error processing secure claim:", error);
      if (!isUserRejectedError(error)) {
        const cleanError = getCleanErrorMessage(error);
        showNotification(`Secure claim failed: ${cleanError}`, "error");
      }
    } finally {
      setIsApplyingPending(false);
    }
  };

  // Keep the secure claim handler reference fresh for the header button
  useEffect(() => {
    secureClaimHandlerRef.current = handleApplyPending;
  });

  // Helpers
  // Legacy event layouts (pre-offchainZKP bool) — kept so we can parse receipts from
  // older deployments whose topic0 differs from the current ABI.
  const LEGACY_REQUEST_EVENT_IFACE = new ethers.utils.Interface([
    "event WithdrawRequested(address indexed ownerAddr, uint256 indexed txId, address token, uint256 plainAmount, bytes proof, bytes userPubkey, bytes userCurrC1, bytes userCurrC2)",
    "event WithdrawProcessed(address indexed ownerAddr, uint256 indexed txId, bool ok, uint8 code, address token, uint256 plainAmount, bytes newAvailC1, bytes newAvailC2, uint64 pendingCreditCounter)",
    "event TransferRequested(address indexed sender, address indexed recipient, uint256 indexed txId, address token, bytes proof, bytes senderPubkey, bytes recipientPubkey, bytes senderCurrC1, bytes senderCurrC2)",
    "event TransferProcessed(address indexed sender, address indexed recipient, uint256 indexed txId, bool ok, uint8 code, address token, bytes senderNewAvailC1, bytes senderNewAvailC2, bytes recipientNewPendingC1, bytes recipientNewPendingC2, uint64 senderPendingCreditCounter, uint64 recipientPendingCreditCounter)",
    "event DepositRequested(address indexed ownerAddr, uint256 indexed txId, address indexed token, uint256 plainAmount, bytes availableC1, bytes availableC2)",
    "event DepositProcessed(address indexed ownerAddr, uint256 indexed txId, bool ok, uint8 code, address token, bytes newAvailC1, bytes newAvailC2, uint64 pendingCreditCounter)",
  ]);

  const parseRequestLog = (iface, log) => {
    try {
      return iface.parseLog({ topics: [...log.topics], data: log.data });
    } catch {
      return undefined;
    }
  };

  const extractRequestId = (parsed) => {
    if (!parsed || !parsed.args) return undefined;
    if (parsed.name === "TransferRequested" || parsed.name === "TransferProcessed") {
      const txId = parsed.args.txId ?? parsed.args[2];
      if (txId !== undefined && txId !== null) return txId.toString();
    }
    if (
      parsed.name === "DepositRequested" ||
      parsed.name === "DepositProcessed" ||
      parsed.name === "WithdrawRequested" ||
      parsed.name === "WithdrawProcessed" ||
      parsed.name === "CreateAccountRequested" ||
      parsed.name === "CreateAccountProcessed" ||
      parsed.name === "ApplyPendingRequested" ||
      parsed.name === "ApplyPendingProcessed"
    ) {
      const txId = parsed.args.txId ?? parsed.args[1];
      if (txId !== undefined && txId !== null) return txId.toString();
    }
    if (parsed.args.requestId !== undefined && parsed.args.requestId !== null) {
      return parsed.args.requestId.toString();
    }
    if (parsed.args.txId !== undefined && parsed.args.txId !== null) {
      return parsed.args.txId.toString();
    }
    return undefined;
  };

  /**
   * Extract numeric on-chain txId from indexed event topics without needing a
   * full ABI match. Owner/txId events: topics[1]=owner, topics[2]=txId.
   * Transfer events: topics[1]=sender, topics[2]=recipient, topics[3]=txId.
   */
  const getRequestIdFromLogTopics = (receipt, contractAddr, eventNames) => {
    const ownerTopic = userAccount
      ? `0x${"0".repeat(24)}${userAccount.toLowerCase().replace(/^0x/, "")}`
      : null;
    if (!ownerTopic || !contractAddr) return undefined;

    const isTransfer = eventNames.some((n) => n.startsWith("Transfer"));

    for (const log of receipt.logs || []) {
      const logAddr =
        log.address && typeof log.address === "string"
          ? log.address.toLowerCase()
          : null;
      if (logAddr !== contractAddr) continue;
      if (!Array.isArray(log.topics)) continue;

      if (isTransfer) {
        if (log.topics.length < 4) continue;
        if (String(log.topics[1]).toLowerCase() !== ownerTopic) continue;
        const txId = BigInt(log.topics[3]).toString();
        if (txId !== "0") return txId;
      } else {
        if (log.topics.length < 3) continue;
        if (String(log.topics[1]).toLowerCase() !== ownerTopic) continue;
        const txId = BigInt(log.topics[2]).toString();
        if (txId !== "0") return txId;
      }
    }
    return undefined;
  };

  const getRequestIdFromReceipt = (receipt, contractInstance, eventNames) => {
    try {
      const iface = contractInstance.interface;
      const contractAddr =
        contractInstance?.address &&
        typeof contractInstance.address === "string"
          ? contractInstance.address.toLowerCase()
          : null;

      if (!contractAddr) {
        console.warn("Contract address is not available, cannot match events");
        return null;
      }

      const tryParse = (logIface) => {
        for (const log of receipt.logs) {
          const logAddr =
            log.address && typeof log.address === "string"
              ? log.address.toLowerCase()
              : null;
          if (logAddr !== contractAddr) continue;
          const parsed = parseRequestLog(logIface, log);
          if (!parsed || !eventNames.includes(parsed.name)) continue;
          const id = extractRequestId(parsed);
          if (id !== undefined) return id;
        }
        // Second pass: any matching address (diamond/facet mismatch)
        for (const log of receipt.logs) {
          const parsed = parseRequestLog(logIface, log);
          if (!parsed || !eventNames.includes(parsed.name)) continue;
          const id = extractRequestId(parsed);
          if (id !== undefined) return id;
        }
        return undefined;
      };

      const fromCurrentAbi = tryParse(iface);
      if (fromCurrentAbi) return fromCurrentAbi;

      const fromLegacyAbi = tryParse(LEGACY_REQUEST_EVENT_IFACE);
      if (fromLegacyAbi) return fromLegacyAbi;

      const fromTopics = getRequestIdFromLogTopics(
        receipt,
        contractAddr,
        eventNames,
      );
      if (fromTopics) return fromTopics;

      console.warn(
        "No matching event found in receipt logs. Searched for:",
        eventNames,
      );
    } catch (e) {
      console.error("Failed to parse receipt for requestId:", e);
      console.error("Error details:", e.message, e.stack);
    }
    return undefined;
  };

  const getRequestIdFromPendingAction = async (expectedKind = null) => {
    if (!contract || !userAccount) return undefined;
    for (let attempt = 0; attempt < 4; attempt++) {
      try {
        const pendingActionTuple = await contract.getPendingAction(userAccount);
        const kind = parsePendingKind(pendingActionTuple[0]);
        const txIdRaw = pendingActionTuple[2];
        const txId = txIdRaw?.toString ? txIdRaw.toString() : String(txIdRaw);
        if (
          txId &&
          txId !== "0" &&
          (expectedKind == null || isPendingKind(kind, expectedKind))
        ) {
          return txId;
        }
      } catch {}
      await sleep(500);
    }
    return undefined;
  };

  const getRequestIdFromAccountCore = async () => {
    if (!contract || !userAccount) return undefined;
    try {
      const accountDataTuple = await contract.getAccountCore(userAccount);
      const txId = accountDataTuple[3]?.toString?.() || String(accountDataTuple[3] || "0");
      return txId && txId !== "0" ? txId : undefined;
    } catch {
      return undefined;
    }
  };

  /**
   * Resolve the numeric on-chain txId for backend persistence.
   * Never falls back to the Ethereum transaction hash — the API rejects that.
   */
  const resolveOnChainRequestId = async (
    receipt,
    eventNames,
    pendingKind = null,
  ) => {
    let requestId = getRequestIdFromReceipt(receipt, contract, eventNames);
    if (!requestId && pendingKind) {
      requestId = await getRequestIdFromPendingAction(pendingKind);
    }
    if (!requestId) {
      requestId = await getRequestIdFromAccountCore();
    }
    if (
      requestId &&
      typeof requestId === "string" &&
      /^0x[0-9a-fA-F]{64}$/.test(requestId)
    ) {
      console.warn(
        "Resolved requestId looks like a tx hash; refusing to use it:",
        requestId,
      );
      return undefined;
    }
    return requestId;
  };

  // WithdrawFacet increments account txId before recording the pending request;
  // the proof nonce must match that post-increment id (current txId + 1).
  const U64_MAX = ethers.BigNumber.from(
    "18446744073709551615",
  );

  const getUserTxIdNonce = async () => {
    if (!contract || !userAccount) {
      throw new Error("Wallet and contract required to read user tx-id");
    }
    const accountDataTuple = await contract.getAccountCore(userAccount);
    const userTxId = ethers.BigNumber.from(accountDataTuple[3] || 0);
    const proofNonce = userTxId.add(1);
    if (proofNonce.gt(U64_MAX)) {
      throw new Error(
        "Withdraw proof nonce (tx-id + 1) exceeds u64 range",
      );
    }
    return proofNonce.toString();
  };

  const validateWithdrawInputs = async () => {
    if (!withdrawAmount) return "Please enter amount to withdraw";
    if (withdrawAmountError) return withdrawAmountError;
    const withdrawAmountValue = parseFloat(withdrawAmount);
    if (isNaN(withdrawAmountValue) || withdrawAmountValue < 0)
      return "Amount cannot be negative";

    // Check if amount exceeds u32 max when multiplied by 100 (contract format)
    const currentTokenData = getTokenData();
    const U32_MAX = 4294967295; // 2^32 - 1
    const rawAmount = withdrawAmountValue * 100;
    if (rawAmount > U32_MAX) {
      const maxAmount = U32_MAX / 100;
      return `Amount exceeds maximum (${Math.floor(maxAmount)} ${currentTokenData?.symbol || "tokens"})`;
    }

    // Fetch fresh account data from contract to ensure we have latest state
    let currentAccountData = accountData;
    try {
      const accountDataTuple = await contract.getAccountCore(userAccount);
      currentAccountData = {
        exists: accountDataTuple[0],
        finalized: accountDataTuple[1],
        pendingAction: accountDataTuple[2],
      };
    } catch (error) {
      console.error(
        "Error fetching fresh account data for withdraw validation:",
        error,
      );
    }

    // Check for pending action using fresh data
    if (currentAccountData && currentAccountData.pendingAction) {
      const pendingMessage = await getPendingActionMessage();
      return pendingMessage;
    }

    // Check if user can submit operations using fresh account data
    const canSubmit =
      currentAccountData &&
      currentAccountData.exists &&
      currentAccountData.finalized &&
      !currentAccountData.pendingAction;
    if (!canSubmit)
      return "Account not ready. Wait until it is finalized and no requests are in progress.";
    if (!keypair)
      return "Keypair not ready. Please wait for key generation to complete.";
    if (confidentialBalance === null)
      return "Balance not loaded. Please wait and try again.";

    const userFacingBalance = confidentialBalance / 100;
    if (userFacingBalance < withdrawAmountValue)
      return `Insufficient balance. Available: ${formatTokenAmount(userFacingBalance)}, Requested: ${formatTokenAmount(withdrawAmountValue)}`;
    return null;
  };

  const handleWithdraw = async () => {
    // Check if keys need to be derived first, or if account creation failed
    // (a skipped onboarding reopens through triggerKeyDerivation too)
    if (needsOnboardingSetup || !keysDerived || accountCreationFailed) {
      triggerKeyDerivation();
      return;
    }

    const withdrawValidation = await validateWithdrawInputs();
    if (withdrawValidation) {
      surfaceValidationMessage(withdrawValidation, {
        amountSetter: setWithdrawAmountError,
      });
      return;
    }

    setIsProcessing(true);
    setWithdrawStatus("pending");

    try {
      const currentTokenData = getTokenData();
      const chainConfig = CHAINS[selectedChainId];
      if (!chainConfig?.contractAddress) {
        throw new Error("Contract address is not configured for this chain");
      }
      if (keyLoading) {
        throw new Error("Keys are still being derived. Wait a moment...");
      }
      if (!keypair || !keypair.private_key) {
        throw new Error(
          "Keypair not available. Please wait for key derivation to complete.",
        );
      }

      const { client, signer } = await getSessionForSelectedChain();
      const amountWei = ethers.utils
        .parseUnits(withdrawAmount, currentTokenData.decimals)
        .toString();

      const receipt = await client.withdraw(
        signer,
        currentTokenData.address,
        amountWei,
        {
          ...SDK_UX_OPTIONS,
          offchainZKP: shouldUseOffchainZKP(chainConfig.chainIdDecimal),
        },
      );
      const txHash = receiptTxHash(receipt);
      if (!txHash) throw new Error("Withdraw submitted but no tx hash returned");

      setWithdrawReceiptData({
        amount: withdrawAmount,
        recipient: userAccount,
        tokenSymbol: currentTokenData.symbol,
        chainName: currentTokenData.chainName,
        txHash,
        time: new Date(),
      });
      setWithdrawAmount("");
      setWithdrawAmountError("");

      await onOperationComplete();
      checkPendingAction();
      loadPendingBalance(0, true);
      updateTokenBalance();
      refreshBalancesForPrivy("withdraw");
      setWithdrawStatus("done");
    } catch (error) {
      console.error("Error withdrawing tokens:", error);
      if (!isUserRejectedError(error)) {
        const cleanError = getCleanErrorMessage(error);
        showNotification(`Withdraw failed: ${cleanError}`, "error");
      }
      resetWithdrawStatus();
    } finally {
      setIsProcessing(false);
    }
  };

  const encodeProofData = (proof) => {
    if (activeTab === "transfer") {
      const proofData = {
        equality_proof: proof.data.equality_proof,
        ciphertext_validity_proof: proof.data.ciphertext_validity_proof,
        range_proof: proof.data.range_proof,
      };

      return ethers.utils.defaultAbiCoder.encode(
        ["string", "string", "string"],
        [
          proofData.equality_proof,
          proofData.ciphertext_validity_proof,
          proofData.range_proof,
        ],
      );
    } else if (activeTab === "withdraw") {
      const proofData = {
        equality_proof: proof.data.equality_proof,
        range_proof: proof.data.range_proof,
      };

      return ethers.utils.defaultAbiCoder.encode(
        ["string", "string"],
        [proofData.equality_proof, proofData.range_proof],
      );
    }
    return "";
  };

  // Account creation is kicked off on its own once keys are derived, and the
  // account only becomes usable after the CREATE tx finalizes on-chain — which
  // can take a while. Every CTA is disabled for that whole window, so it says
  // what is happening instead of just going inert.
  //
  // IMPORTANT: parent accountExists/hasActiveAccount can lag behind local
  // contract reads (balance/pending already loaded). If we already see a live
  // balance or pending claim, do NOT keep the CTA on "Creating account...".
  const hasLiveAccountEvidence =
    (accountExists && hasActiveAccount) ||
    (typeof confidentialBalance === "number" && confidentialBalance > 0) ||
    (typeof pendingBalance === "number" && pendingBalance > 0);

  const isAccountCreationPending =
    !!userAccount &&
    !!verifyingContract &&
    !accountCreationFailed &&
    !hasLiveAccountEvidence &&
    (isCreatingAccount ||
      isPendingKind(pendingActionKind, PENDING_KIND_CREATE) ||
      (keysDerived && (!accountExists || !hasActiveAccount)));

  const ACCOUNT_CREATION_CTA_LABEL = "Creating account...";

  // Amount card top-right: show "Transfer in progress…" (etc.) while the
  // contract has a pending action, otherwise the confidential balance.
  // This logic lived in .balance-display-below before EditorialForm; it was
  // dropped in that migration even though getPendingProgressMessage stayed imported.
  const getAmountCardBalanceLabel = ({ showBalance = true } = {}) => {
    if (!userAccount || !tokenData) return "";

    const progressMessage = getPendingProgressMessage(pendingActionKind);
    if (progressMessage) return progressMessage;

    if (!showBalance) return "";

    if (confidentialBalance !== null) {
      // Transfer/withdraw move confidential balances (cUSDC/cUSDT), not the
      // underlying ERC-20 — match the C-prefix used on the token selector.
      return `Balance ${formatTokenAmount(confidentialBalance / 100, 2)} ${toConfidentialSymbol(tokenData.symbol, tokenData.name)}`;
    }
    return keyLoading ? "Deriving key..." : "Loading...";
  };

  // Balances-and-gas (handoff/BALANCES.md): "gas outranks a ready-to-send
  // form: nothing can be submitted without it." Once a positive amount is
  // typed, an empty gas balance overrides whatever the CTA would otherwise
  // say — including "Insufficient balance" / recipient errors — the same
  // way the design reference's renderVals() applies it after computing the
  // normal cta. It does NOT override the operational locks below it
  // (processing, an op already in progress, account creation, no
  // confidential balance) — those are reasons gas can't fix.
  const isPositiveAmount = (value) => {
    const n = parseFloat(value);
    return !!value && !isNaN(n) && n > 0;
  };

  /**
   * @param {string} amount - the tab's amount field value
   * @param {boolean} locked - true if an operational lock (processing, an
   *   op in progress, account creation, no confidential balance) already
   *   owns the CTA — gas shouldn't override those.
   */
  const gasBlocksSubmit = (amount, locked) =>
    gasState === "empty" && !!selectedChainId && !locked && isPositiveAmount(amount);

  // Distinguish zero balance from "have some ETH but estimate says it won't
  // cover a full confidential tx" — both gate submit, but the copy must not
  // tell a funded wallet it has no ETH.
  const NO_GAS_CTA_LABEL = () => {
    const symbol = nativeSymbol || "ETH";
    const hasSome =
      nativeBalance != null &&
      Number.isFinite(nativeBalance) &&
      nativeBalance > 0;
    return hasSome ? `Not enough ${symbol} for gas` : `No ${symbol} for gas`;
  };

  const renderTransferTab = () => {
    const isConnected = !!userAccount;
    const accountStatus = checkAccountStatus();
    const hasSelection = !!(
      selectedChainId &&
      selectedTokenId &&
      tokenData &&
      isValidTokenAddress(tokenData.address)
    );
    // Demo recipient is a testnet convenience — hide on mainnet.
    const isMainnet = CHAINS[selectedChainId]?.network === "mainnet";

    // Mirrors the old amount-input onChange's validation exactly, just
    // parameterized on a raw value instead of an event (EditorialForm's
    // AmountCard already strips non-numeric characters before calling this).
    const handleTransferAmountInput = (value) => {
      if (value === "" || value === "-") {
        setTransferAmount(value);
        setTransferAmountError("");
        return;
      }
      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < 0) {
        setTransferAmountError("Amount cannot be negative");
        setTransferAmount(value);
        return;
      }
      const decimalCheck = value.split(".");
      if (decimalCheck.length > 1 && decimalCheck[1].length > 2) {
        setTransferAmountError("Maximum 2 decimal places allowed");
        setTransferAmount(value);
        return;
      }
      const U32_MAX = 4294967295; // 2^32 - 1
      const rawAmount = numValue * 100;
      const shouldShowMaxError =
        selectedChainId &&
        confidentialBalance !== null &&
        confidentialBalance > 0;
      if (rawAmount > U32_MAX && shouldShowMaxError) {
        const maxAmount = U32_MAX / 100;
        setTransferAmountError(
          `Amount exceeds maximum (${Math.floor(maxAmount)} ${tokenData?.symbol || "tokens"})`,
        );
        setTransferAmount(value);
      } else if (
        confidentialBalance !== null &&
        rawAmount > confidentialBalance
      ) {
        setTransferAmountError("Amount exceeds your balance");
        setTransferAmount(value);
      } else {
        setTransferAmount(value);
        setTransferAmountError("");
      }
    };

    const netLabel =
      selectedChainId && tokenData
        ? `${tokenData.chainName} · ${toConfidentialSymbol(tokenData.symbol, tokenData.name)}`
        : "Select network";

    const balanceLabel = getAmountCardBalanceLabel();

    // A contract op is already mid-flight (submitted from any tab) — the CTA
    // says which one instead of just going inert.
    const opInProgressLabel =
      !isPendingKind(pendingActionKind, PENDING_KIND_CREATE) &&
      getPendingProgressMessage(pendingActionKind);
    const noConfidentialBalance =
      keysDerived &&
      !accountCreationFailed &&
      accountStatus.hasAccount &&
      !accountStatus.hasBalance &&
      confidentialBalance !== null;

    const setupBlocked = accountSetupIssue?.severity === "error";

    const baseCtaLabel = !isConnected
      ? "Connect"
      : !hasSelection
        ? "Select a network"
        : setupBlocked
          ? accountSetupIssue.cta || "Add funds, then retry"
          : isProcessing || transferStatus === "pending"
            ? "Processing..."
            : opInProgressLabel
              ? opInProgressLabel
              : isAccountCreationPending
                ? ACCOUNT_CREATION_CTA_LABEL
                : noConfidentialBalance
                  ? "No balance to transfer"
                  : !transferAmount
                    ? "Enter an amount"
                    : transferAmountError
                      ? transferAmountError
                      : !recipient.trim()
                        ? "Enter a receiver"
                        : recipientError
                          ? recipientAccountStatus === "no_account"
                            ? "Receiver has no account"
                            : recipientError
                          : recipientAccountStatus === "checking"
                            ? "Checking receiver..."
                            : "Transfer privately";

    const baseCtaEnabled = !isConnected
      ? true
      : setupBlocked
        ? true
        : !(
            !hasSelection ||
            !transferAmount ||
            !recipient.trim() ||
            isProcessing ||
            transferStatus === "pending" ||
            !!opInProgressLabel ||
            isCreatingAccount ||
            isPendingKind(pendingActionKind, PENDING_KIND_CREATE) ||
            !!transferAmountError ||
            !!recipientError ||
            recipientAccountStatus === "checking" ||
            recipientAccountStatus === "no_account" ||
            (keysDerived &&
              !accountCreationFailed &&
              (!accountStatus.hasAccount || !accountStatus.hasBalance))
          );

    // Gas gates the transaction (handoff/BALANCES.md): once a positive
    // amount is typed, empty gas outranks every other CTA state — including
    // "Enter a receiver" / recipient errors — the same way it does in the
    // design reference. Locked operational states (creating the account,
    // mid-submit) are left alone; gas can't fix those anyway.
    const transferLocked =
      isAccountCreationPending || isProcessing || isPendingKind(pendingActionKind, PENDING_KIND_CREATE);
    const transferGasBlocks = gasBlocksSubmit(transferAmount, transferLocked);
    const ctaLabel = needsOnboardingSetup
      ? SETUP_CTA_LABEL
      : transferGasBlocks
        ? NO_GAS_CTA_LABEL()
        : baseCtaLabel;
    const ctaEnabled = needsOnboardingSetup
      ? true
      : transferGasBlocks
        ? false
        : baseCtaEnabled;

    // A recipient address can be well-formed (recipientError stays null)
    // but not yet have a Stabletrust Pay account on-chain — that's a
    // separate check (recipientAccountStatus, set by checkRecipientAccount)
    // with its own visual treatment: same red border as a format error,
    // plus a shareable registration invite link (not an invite-code gate).
    const recipientHasNoAccount = recipientAccountStatus === "no_account";
    const recipientNote = recipientHasNoAccount ? (
      <RecipientInviteNote
        compact={isMobileViewport}
        onNotify={showNotification}
      />
    ) : null;

    if (isMobileViewport) {
      return (
        <>
          <MobileShell
            activeTab={activeTab}
            onTab={setActiveTab}
            connected={isConnected}
            address={truncatedAddress}
            onWallet={onConnect}
            fullAddress={userAccount}
            onDisconnect={onDisconnect}
            email={privyIdentityLabel}
            onSwitchAccount={onSwitchAccount}
            switchingAccount={switchingAccount}
            networkLabel={
              tokenData
                ? `${tokenData.chainName} · ${toConfidentialSymbol(tokenData.symbol, tokenData.name)}`
                : "Select network"
            }
            networkSelected={!!selectedChainId}
            tokenIcon={tokenData?.icon}
            chainIcon={tokenData?.chainLogo}
            onOpenNetwork={() => setShowTokenSelector(true)}
            amount={transferAmount}
            onAmountChange={handleTransferAmountInput}
            balanceLabel={
              // Prefer the shared label so "Transfer in progress…" etc.
              // surface on phone the same way they do on desktop.
              !isConnected
                ? "Connect to see your balance"
                : balanceLabel
            }
            isOver={!!transferAmountError}
            overMessage={transferAmountError || undefined}
            recipient={recipient}
            onRecipientChange={handleRecipientChange}
            recipientError={!!recipientError || recipientHasNoAccount}
            recipientNote={recipientNote}
            // Same demo address EditorialForm's ReceiverRow defaults to on
            // desktop — one well-known testnet recipient for exercising a
            // transfer without needing a second real wallet. Omitted on
            // mainnet so the shortcut never appears there.
            onUseDemoAccount={
              isMainnet
                ? undefined
                : () =>
                    handleRecipientChange(
                      "0x40D9b7F82ccC0CEc500120c48294F1aa84385B83",
                    )
            }
            onScan={undefined}
            onClearRecipient={() => handleRecipientChange("")}
            onRequestTransfer={() => setShowRequestModal(true)}
            detailRows={[
              ["Network", tokenData?.chainName || "—"],
              ["Protocol fee", "$0.00"],
            ]}
            percentEnabled={isConnected}
            onPercent={handlePercentageClick}
            ctaLabel={ctaLabel}
            ctaEnabled={ctaEnabled}
            claimBanner={renderMobileClaimBanner()}
            onSubmit={
              isConnected
                ? () => {
                    // Match desktop: key derivation / account retry goes
                    // straight to Privy; otherwise open the confirm sheet.
                    if (
                      needsOnboardingSetup ||
                      !keysDerived ||
                      accountCreationFailed
                    ) {
                      handleTransfer();
                      return;
                    }
                    setShowTransferConfirm(true);
                  }
                : onConnect
            }
          />
        </>
      );
    }

    return (
      <>
        <EditorialForm
          activeTab={activeTab}
          onTab={setActiveTab}
          netLabel={netLabel}
          onOpenNetwork={() => setShowTokenSelector(true)}
          tokenIcon={tokenData?.icon}
          chainIcon={tokenData?.chainLogo}
          balanceLabel={balanceLabel}
          amount={transferAmount}
          onAmount={handleTransferAmountInput}
          onPercent={handlePercentageClick}
          percentEnabled={isConnected}
          isOver={!!transferAmountError}
          amountError={transferAmountError}
          recipient={recipient}
          onRecipient={handleRecipientChange}
          recipientError={!!recipientError || recipientHasNoAccount}
          demoAddress={
            isMainnet
              ? null
              : "0x40D9b7F82ccC0CEc500120c48294F1aa84385B83"
          }
          note={recipientNote}
          ctaLabel={ctaLabel}
          ctaEnabled={ctaEnabled}
          onSubmit={isConnected ? handleTransfer : onConnect}
          onRequestTransfer={() => setShowRequestModal(true)}
        />
      </>
    );
  };

  const renderDepositTab = () => {
    const isConnected = !!userAccount;
    const hasSelection = !!(
      selectedChainId &&
      selectedTokenId &&
      tokenData &&
      isValidTokenAddress(tokenData.address)
    );

    // Mirrors the old amount-input onChange's validation exactly, just
    // parameterized on a raw value instead of an event.
    const handleDepositAmountInput = (value) => {
      if (value === "" || value === "-") {
        setDepositAmount(value);
        setDepositAmountError("");
        return;
      }
      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < 0) {
        setDepositAmountError("Amount cannot be negative");
        setDepositAmount(value);
        return;
      }
      const decimalCheck = value.split(".");
      if (decimalCheck.length > 1 && decimalCheck[1].length > 2) {
        setDepositAmountError("Maximum 2 decimal places allowed");
        setDepositAmount(value);
        return;
      }
      const MAX_DEPOSIT_TOKENS = 40000000;
      const shouldShowMaxError =
        selectedChainId &&
        confidentialBalance !== null &&
        confidentialBalance > 0;
      // Deposits draw from the regular (non-confidential) wallet balance,
      // not confidentialBalance — only checked once connected, so typing
      // while disconnected never turns the numeral red.
      const regularBalance = isConnected ? getRegularBalanceValue() : null;
      if (numValue > MAX_DEPOSIT_TOKENS && shouldShowMaxError) {
        setDepositAmountError(
          `Amount exceeds maximum (${MAX_DEPOSIT_TOKENS} ${tokenData?.symbol || "tokens"})`,
        );
        setDepositAmount(value);
      } else if (
        isConnected &&
        regularBalance !== null &&
        numValue > regularBalance
      ) {
        setDepositAmountError("Amount exceeds your balance");
        setDepositAmount(value);
      } else {
        setDepositAmount(value);
        setDepositAmountError("");
      }
    };

    const netLabel =
      selectedChainId && tokenData
        ? `${tokenData.chainName} · ${tokenData.symbol}`
        : "Select network";

    // Deposits draw from the regular (non-confidential) wallet balance, not
    // confidentialBalance — show that instead of leaving the card blank, so
    // there's actually something to base the amount on before submitting.
    // Only deposit/withdraw replace the label: those change the regular wallet
    // balance. Transfer and secure claim only move confidential funds.
    const depositProgressMessage =
      isPendingKind(pendingActionKind, PENDING_KIND_DEPOSIT) ||
      isPendingKind(pendingActionKind, PENDING_KIND_WITHDRAW)
        ? getPendingProgressMessage(pendingActionKind)
        : null;
    const regularBalanceValue = isConnected ? getRegularBalanceValue() : null;
    const balanceLabel = depositProgressMessage
      ? depositProgressMessage
      : isConnected && tokenData && regularBalanceValue !== null
        ? `Balance ${formatTokenAmount(regularBalanceValue, 2)} ${tokenData.symbol}`
        : "";

    const isFormValid = hasSelection && !!depositAmount && !depositAmountError;

    // A contract op is already mid-flight — say which one on the CTA.
    const opInProgressLabel =
      !isPendingKind(pendingActionKind, PENDING_KIND_CREATE) &&
      getPendingProgressMessage(pendingActionKind);

    const setupBlocked = accountSetupIssue?.severity === "error";

    const baseCtaLabel = !isConnected
      ? "Connect"
      : !hasSelection
        ? "Select a network"
        : setupBlocked
          ? accountSetupIssue.cta || "Add funds, then retry"
          : isProcessing || depositStatus === "pending"
            ? "Processing..."
            : opInProgressLabel
              ? opInProgressLabel
              : isAccountCreationPending
                ? ACCOUNT_CREATION_CTA_LABEL
                : !depositAmount
                  ? "Enter an amount"
                  : depositAmountError
                    ? depositAmountError
                    : hasInsufficientBalance()
                      ? `Insufficient ${tokenData?.symbol || "balance"}`
                      : "Deposit Tokens";

    const baseCtaEnabled = !isConnected
      ? true
      : setupBlocked
        ? true
        : !(
            !hasSelection ||
            !depositAmount ||
            isProcessing ||
            depositStatus === "pending" ||
            !!opInProgressLabel ||
            isCreatingAccount ||
            isPendingKind(pendingActionKind, PENDING_KIND_CREATE) ||
            !!depositAmountError ||
            hasInsufficientBalance() ||
            (keysDerived &&
              !accountCreationFailed &&
              (!accountExists || !hasActiveAccount))
          );

    // Gas gates the transaction — see the Transfer tab above for the reasoning.
    const depositLocked =
      isAccountCreationPending || isProcessing || isPendingKind(pendingActionKind, PENDING_KIND_CREATE);
    const depositGasBlocks = gasBlocksSubmit(depositAmount, depositLocked);
    const ctaLabel = needsOnboardingSetup
      ? SETUP_CTA_LABEL
      : depositGasBlocks
        ? NO_GAS_CTA_LABEL()
        : baseCtaLabel;
    const ctaEnabled = needsOnboardingSetup
      ? true
      : depositGasBlocks
        ? false
        : baseCtaEnabled;

    if (isMobileViewport) {
      return (
        <>
          <MobileShell
            activeTab={activeTab}
            onTab={setActiveTab}
            connected={isConnected}
            address={truncatedAddress}
            onWallet={onConnect}
            fullAddress={userAccount}
            onDisconnect={onDisconnect}
            email={privyIdentityLabel}
            onSwitchAccount={onSwitchAccount}
            switchingAccount={switchingAccount}
            networkLabel={
              tokenData
                ? `${tokenData.chainName} · ${tokenData.symbol}`
                : "Select network"
            }
            networkSelected={!!selectedChainId}
            tokenIcon={tokenData?.icon}
            chainIcon={tokenData?.chainLogo}
            onOpenNetwork={() => setShowTokenSelector(true)}
            amount={depositAmount}
            onAmountChange={handleDepositAmountInput}
            balanceLabel={
              !isConnected
                ? "Connect to see your balance"
                : balanceLabel
            }
            isOver={!!depositAmountError}
            overMessage={depositAmountError || undefined}
            detailRows={[
              ["Network", tokenData?.chainName || "—"],
              ["Protocol fee", "$0.00"],
            ]}
            percentEnabled={isConnected}
            onPercent={handleDepositPercentageClick}
            ctaLabel={ctaLabel}
            ctaEnabled={ctaEnabled}
            claimBanner={renderMobileClaimBanner()}
            onSubmit={isConnected ? handleDeposit : onConnect}
          />
        </>
      );
    }

    return (
      <>
        <EditorialForm
          activeTab={activeTab}
          onTab={setActiveTab}
          netLabel={netLabel}
          onOpenNetwork={() => setShowTokenSelector(true)}
          tokenIcon={tokenData?.icon}
          chainIcon={tokenData?.chainLogo}
          balanceLabel={balanceLabel}
          amount={depositAmount}
          onAmount={handleDepositAmountInput}
          onPercent={handleDepositPercentageClick}
          percentEnabled={isConnected}
          isOver={!!depositAmountError}
          amountError={depositAmountError}
          note=""
          ctaLabel={ctaLabel}
          ctaEnabled={ctaEnabled}
          onSubmit={isConnected ? handleDeposit : onConnect}
        />
      </>
    );
  };

  const renderWithdrawTab = () => {
    const isConnected = !!userAccount;
    const accountStatus = checkAccountStatus();
    const hasSelection = !!(
      selectedChainId &&
      selectedTokenId &&
      tokenData &&
      isValidTokenAddress(tokenData.address)
    );

    // Mirrors the old amount-input onChange's validation exactly, just
    // parameterized on a raw value instead of an event.
    const handleWithdrawAmountInput = (value) => {
      if (value === "" || value === "-") {
        setWithdrawAmount(value);
        setWithdrawAmountError("");
        return;
      }
      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < 0) {
        setWithdrawAmountError("Amount cannot be negative");
        setWithdrawAmount(value);
        return;
      }
      const decimalCheck = value.split(".");
      if (decimalCheck.length > 1 && decimalCheck[1].length > 2) {
        setWithdrawAmountError("Maximum 2 decimal places allowed");
        setWithdrawAmount(value);
        return;
      }
      const U32_MAX = 4294967295; // 2^32 - 1
      const rawAmount = numValue * 100;
      const shouldShowMaxError =
        selectedChainId &&
        confidentialBalance !== null &&
        confidentialBalance > 0;
      if (rawAmount > U32_MAX && shouldShowMaxError) {
        const maxAmount = U32_MAX / 100;
        setWithdrawAmountError(
          `Amount exceeds maximum (${Math.floor(maxAmount)} ${tokenData?.symbol || "tokens"})`,
        );
        setWithdrawAmount(value);
      } else if (
        confidentialBalance !== null &&
        rawAmount > confidentialBalance
      ) {
        setWithdrawAmountError("Amount exceeds your balance");
        setWithdrawAmount(value);
      } else {
        setWithdrawAmount(value);
        setWithdrawAmountError("");
      }
    };

    const netLabel =
      selectedChainId && tokenData
        ? `${tokenData.chainName} · ${toConfidentialSymbol(tokenData.symbol, tokenData.name)}`
        : "Select network";

    const balanceLabel = getAmountCardBalanceLabel();

    const isFormValid =
      hasSelection && !!withdrawAmount && !withdrawAmountError;

    // A contract op is already mid-flight — say which one on the CTA.
    const opInProgressLabel =
      !isPendingKind(pendingActionKind, PENDING_KIND_CREATE) &&
      getPendingProgressMessage(pendingActionKind);
    const noConfidentialBalance =
      keysDerived &&
      !accountCreationFailed &&
      accountStatus.hasAccount &&
      !accountStatus.hasBalance &&
      confidentialBalance !== null;

    const setupBlocked = accountSetupIssue?.severity === "error";

    const baseCtaLabel = !isConnected
      ? "Connect"
      : !hasSelection
        ? "Select a network"
        : setupBlocked
          ? accountSetupIssue.cta || "Add funds, then retry"
          : isProcessing || withdrawStatus === "pending"
            ? "Processing..."
            : opInProgressLabel
              ? opInProgressLabel
              : isAccountCreationPending
                ? ACCOUNT_CREATION_CTA_LABEL
                : noConfidentialBalance
                  ? "No balance to withdraw"
                  : !withdrawAmount
                    ? "Enter an amount"
                    : withdrawAmountError
                      ? withdrawAmountError
                      : "Withdraw Tokens";

    const baseCtaEnabled = !isConnected
      ? true
      : setupBlocked
        ? true
        : !(
            !hasSelection ||
            !withdrawAmount ||
            isProcessing ||
            withdrawStatus === "pending" ||
            !!opInProgressLabel ||
            isCreatingAccount ||
            isPendingKind(pendingActionKind, PENDING_KIND_CREATE) ||
            !!withdrawAmountError ||
            (keysDerived &&
              !accountCreationFailed &&
              (!accountStatus.hasAccount || !accountStatus.hasBalance))
          );

    // Gas gates the transaction — see the Transfer tab above for the reasoning.
    const withdrawLocked =
      isAccountCreationPending || isProcessing || isPendingKind(pendingActionKind, PENDING_KIND_CREATE);
    const withdrawGasBlocks = gasBlocksSubmit(withdrawAmount, withdrawLocked);
    const ctaLabel = needsOnboardingSetup
      ? SETUP_CTA_LABEL
      : withdrawGasBlocks
        ? NO_GAS_CTA_LABEL()
        : baseCtaLabel;
    const ctaEnabled = needsOnboardingSetup
      ? true
      : withdrawGasBlocks
        ? false
        : baseCtaEnabled;

    if (isMobileViewport) {
      return (
        <>
          <MobileShell
            activeTab={activeTab}
            onTab={setActiveTab}
            connected={isConnected}
            address={truncatedAddress}
            onWallet={onConnect}
            fullAddress={userAccount}
            onDisconnect={onDisconnect}
            email={privyIdentityLabel}
            onSwitchAccount={onSwitchAccount}
            switchingAccount={switchingAccount}
            networkLabel={
              tokenData
                ? `${tokenData.chainName} · ${toConfidentialSymbol(tokenData.symbol, tokenData.name)}`
                : "Select network"
            }
            networkSelected={!!selectedChainId}
            tokenIcon={tokenData?.icon}
            chainIcon={tokenData?.chainLogo}
            onOpenNetwork={() => setShowTokenSelector(true)}
            amount={withdrawAmount}
            onAmountChange={handleWithdrawAmountInput}
            balanceLabel={
              !isConnected
                ? "Connect to see your balance"
                : balanceLabel
            }
            isOver={!!withdrawAmountError}
            overMessage={withdrawAmountError || undefined}
            detailRows={[
              ["Network", tokenData?.chainName || "—"],
              ["Protocol fee", "$0.00"],
            ]}
            percentEnabled={isConnected}
            onPercent={handleWithdrawPercentageClick}
            ctaLabel={ctaLabel}
            ctaEnabled={ctaEnabled}
            claimBanner={renderMobileClaimBanner()}
            onSubmit={isConnected ? handleWithdraw : onConnect}
          />
        </>
      );
    }

    return (
      <>
        <EditorialForm
          activeTab={activeTab}
          onTab={setActiveTab}
          netLabel={netLabel}
          onOpenNetwork={() => setShowTokenSelector(true)}
          tokenIcon={tokenData?.icon}
          chainIcon={tokenData?.chainLogo}
          balanceLabel={balanceLabel}
          amount={withdrawAmount}
          onAmount={handleWithdrawAmountInput}
          onPercent={handleWithdrawPercentageClick}
          percentEnabled={isConnected}
          isOver={!!withdrawAmountError}
          amountError={withdrawAmountError}
          note=""
          ctaLabel={ctaLabel}
          ctaEnabled={ctaEnabled}
          onSubmit={isConnected ? handleWithdraw : onConnect}
        />
      </>
    );
  };

  const BalanceCard = ({ label, amount }) => (
    <div className="balance-card">
      <div className="balance-label">{label}</div>
      <div className="balance-amount">
        {amount !== null
          ? `${formatTokenAmount(amount / 100, 2)} ${toConfidentialSymbol(tokenData.symbol, tokenData.name)}`
          : keyLoading
            ? "Deriving key..."
            : "Loading..."}
      </div>
    </div>
  );

  // Apply Pending tab removed; replaced by overlay flow

  // Key derivation is now handled by the WASM hook

  // Mobile Secure Claim — desktop header + right-banner are hidden on phone,
  // and the old .pending-notification-container is display:none globally.
  const renderMobileClaimBanner = () => {
    if (!tokenData) return null;
    const hasPending = pendingBalance != null && pendingBalance > 0;
    if (!hasPending) return null;
    if (pendingNotificationDismissed && !isApplyingPending) return null;
    if (
      isPendingKind(pendingActionKind, PENDING_KIND_APPLY) &&
      !isApplyingPending
    ) {
      return null;
    }
    return (
      <MobileClaimBanner
        amountLabel={formatTokenAmount(pendingBalance / 100, 2)}
        symbol={tokenData.symbol}
        busy={isApplyingPending}
        onClaim={handleApplyPending}
        onDismiss={
          isApplyingPending
            ? undefined
            : () => {
                setShowPendingNotification(false);
                setPendingNotificationDismissed(true);
              }
        }
      />
    );
  };

  // Post-submit status card (StableTrust Pay - Editorial design). Replaces
  // a tab's form entirely — the call sites below swap in either this or
  // the tab's normal render*Tab() output based on that tab's *Status state.
  // config: { showReceipt, actionLabel, onAction } — the three tabs each want
  // a different success-screen CTA setup (Transfer keeps "View receipt" +
  // "Make another transfer"; Deposit/Withdraw get a single button routing
  // straight into the next natural action), not just a different label on
  // the same two-button layout.
  const renderStatusView = (status, opLabel, receiptData, config) => {
    const currentToken = getTokenData();
    const chainName =
      receiptData?.chainName || currentToken?.chainName || "the network";
    const tokenSymbol = receiptData?.tokenSymbol || currentToken?.symbol || "USDC";
    const txHashShort = receiptData?.txHash
      ? `${receiptData.txHash.slice(0, 6)}…${receiptData.txHash.slice(-4)}`
      : "";
    const opKind = opLabel.toLowerCase();

    const pendingCopyByOp = {
      deposit: {
        title: "Submitting deposit…",
        meta: `Approving ${tokenSymbol} if needed and submitting to ${chainName}.`,
      },
      withdraw: {
        title: "Generating proof…",
        meta: `Encrypting the amount and submitting to ${chainName}.`,
      },
      transfer: {
        title: "Generating proof…",
        meta: `Encrypting the amount and submitting to ${chainName}.`,
      },
    };
    const pendingCopy = pendingCopyByOp[opKind] || pendingCopyByOp.transfer;

    if (isMobileViewport) {
      const doneTitleByOp = {
        transfer: "Transfer complete",
        deposit: "Deposit submitted",
        withdraw: "Withdraw submitted",
      };
      return (
        <div style={{ position: "relative", height: "100dvh" }}>
          {status === "pending" ? (
            <MobilePending
              title={pendingCopy.title.replace(/…$/, "")}
              network={chainName}
              meta={pendingCopy.meta + " Keep the app open."}
            />
          ) : (
            <MobileComplete
              title={doneTitleByOp[opKind] || `${opLabel} submitted`}
              token={tokenSymbol}
              network={chainName}
              hash={txHashShort}
              showReceipt={!!config.showReceipt}
              showHistory={opKind === "transfer"}
              onReceipt={
                config.showReceipt
                  ? () => setReceiptTab(opLabel.toLowerCase())
                  : undefined
              }
              onHistory={
                opKind === "transfer"
                  ? () => {
                      config.onAction?.();
                      setActiveTab("history");
                    }
                  : undefined
              }
              onReset={config.onAction}
              resetLabel={config.actionLabel}
            />
          )}
        </div>
      );
    }

    return (
      <StatusPanel
        pending={status === "pending"}
        title={
          status === "pending" ? pendingCopy.title : `${opLabel} submitted`
        }
        meta={
          status === "pending"
            ? pendingCopy.meta
            : `Encrypted · ${tokenSymbol} · ${chainName} · ${txHashShort}`
        }
        note={
          status === "pending"
            ? null
            : "It might take a few moments for the balance to be updated."
        }
        onReceipt={config.showReceipt ? () => setReceiptTab(opLabel.toLowerCase()) : null}
        onReset={config.onAction}
        resetLabel={config.actionLabel}
      />
    );
  };

  const resetTransferStatus = () => {
    setTransferStatus("idle");
    setTransferReceiptData(null);
  };
  const resetDepositStatus = () => {
    setDepositStatus("idle");
    setDepositReceiptData(null);
  };
  const resetWithdrawStatus = () => {
    setWithdrawStatus("idle");
    setWithdrawReceiptData(null);
  };

  // Printed-receipt-style modal opened from a status card's "View receipt".
  const renderReceiptModal = () => {
    if (!receiptTab) return null;
    const dataByTab = {
      transfer: transferReceiptData,
      deposit: depositReceiptData,
      withdraw: withdrawReceiptData,
    };
    const data = dataByTab[receiptTab];
    if (!data) return null;

    const opLabelByTab = {
      transfer: "Transfer",
      deposit: "Deposit",
      withdraw: "Withdraw",
    };
    const opLabel = opLabelByTab[receiptTab];
    const chainName =
      data.chainName || getChainDisplayName(selectedChainId) || "Unknown network";
    const tokenSymbol = data.tokenSymbol || "USDC";
    const time = data.time || new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const rcTime = `${pad(time.getDate())}.${pad(time.getMonth() + 1)}.${time.getFullYear()}  ${pad(time.getHours())}:${pad(time.getMinutes())}:${pad(time.getSeconds())}`;
    const rcStars = "*".repeat(46);
    const hashDigits = data.txHash ? data.txHash.slice(2, 18).toUpperCase() : "";
    const rcHash = hashDigits.match(/.{1,4}/g)?.join(" ") || "";
    const rcRows = [
      ["Network", chainName],
      ["Type", opLabel],
      ["Status", "Confirmed"],
      ["Protocol fee", "$0.00"],
    ];
    const rcBars = Array.from({ length: 46 }, (_, i) => {
      const seed = (i * 37 + 11) % 7;
      const w = seed < 2 ? 4 : seed < 5 ? 2 : 6;
      const h = 38 + ((i * 53) % 3) * 8;
      return { w, h };
    });

    const txHashShort = data.txHash
      ? `${data.txHash.slice(0, 6)}…${data.txHash.slice(-4)}`
      : null;
    const explorerUrl = data.txHash
      ? getExplorerTxUrl(selectedChainId, data.txHash)
      : null;

    const shareToX = () => {
      const tweetText = `Just completed a private ${opLabel.toLowerCase()} on Stabletrust Pay 🔒\n\nAmount encrypted onchain. Homomorphic encryption, proof verified.`;
      const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
      window.open(url, "_blank", "noopener,noreferrer");
      setReceiptShared(true);
      setTimeout(() => setReceiptShared(false), 1800);
    };

    // Redraw the receipt onto a canvas and save it as a PNG (no external deps).
    const downloadReceipt = async () => {
      try {
        await document.fonts?.ready;
      } catch {
        /* fonts already loaded (receipt is on screen) */
      }
      const scale = 2;
      const W = 460;
      const H = 680;
      const MARGIN = 50;
      const CW = W + MARGIN * 2;
      const CH = H + MARGIN * 2;
      const canvas = document.createElement("canvas");
      canvas.width = CW * scale;
      canvas.height = CH * scale;
      const ctx = canvas.getContext("2d");
      ctx.scale(scale, scale);

      const ink = "#1c1c1c";
      const muted = "#8a8578";
      const cx = W / 2;
      const padX = 44;
      const mono = (px, weight = "400") =>
        `${weight} ${px}px "JetBrains Mono", ui-monospace, monospace`;
      const serif = (px) => `${px}px Georgia, "Times New Roman", serif`;

      ctx.fillStyle = "#d9d7d1";
      ctx.fillRect(0, 0, CW, CH);

      ctx.save();
      ctx.shadowColor = "rgba(28,26,20,0.16)";
      ctx.shadowBlur = 18;
      ctx.shadowOffsetY = 6;
      ctx.fillStyle = "#f4f2ea";
      ctx.fillRect(MARGIN, MARGIN, W, H);
      ctx.restore();
      ctx.strokeStyle = "rgba(0,0,0,0.06)";
      ctx.lineWidth = 1;
      ctx.strokeRect(MARGIN + 0.5, MARGIN + 0.5, W - 1, H - 1);

      // Everything below draws in the card's own 0..W / 0..H coordinate space.
      ctx.translate(MARGIN, MARGIN);
      ctx.textBaseline = "alphabetic";

      const centered = (text, y, font, color) => {
        ctx.textAlign = "center";
        ctx.font = font;
        ctx.fillStyle = color;
        ctx.fillText(text, cx, y);
      };
      const stars = (y) => centered(rcStars, y, mono(12), muted);

      centered("Stabletrust Pay", 56, mono(15, "700"), ink);
      centered(`Confidential ${opLabel}`, 80, mono(13), muted);
      centered(rcTime, 100, mono(12), muted);
      stars(132);
      centered("Encrypted", 188, serif(44), ink);
      centered(`${tokenSymbol} · Confidential`, 212, mono(12), muted);
      stars(244);

      let ry = 288;
      for (const [k, v] of rcRows) {
        ctx.font = mono(12);
        ctx.textAlign = "left";
        ctx.fillStyle = muted;
        ctx.fillText(k, padX, ry);
        const kw = ctx.measureText(k).width;
        ctx.textAlign = "right";
        ctx.fillStyle = ink;
        ctx.fillText(v, W - padX, ry);
        const vw = ctx.measureText(v).width;
        ctx.strokeStyle = "rgba(0,0,0,0.18)";
        ctx.setLineDash([1, 3]);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(padX + kw + 8, ry - 4);
        ctx.lineTo(W - padX - vw - 8, ry - 4);
        ctx.stroke();
        ctx.setLineDash([]);
        ry += 30;
      }

      // To.
      ctx.textAlign = "left";
      ctx.font = mono(12);
      ctx.fillStyle = muted;
      ctx.fillText("To", padX, ry + 12);
      ctx.fillStyle = ink;
      ctx.fillText("Encrypted", padX, ry + 34);
      ry += 66;

      stars(ry);
      ry += 34;
      centered("Amount encrypted onchain", ry, mono(13), ink);
      centered("Homomorphic encryption · Proof verified", ry + 20, mono(11), muted);
      ry += 50;

      // Barcode.
      const gap = 2;
      const totalW = rcBars.reduce((s, b) => s + b.w + gap, -gap);
      let bx = cx - totalW / 2;
      const barMaxH = 50;
      ctx.fillStyle = ink;
      for (const b of rcBars) {
        const bh = Math.min(b.h, barMaxH);
        ctx.fillRect(bx, ry + (barMaxH - bh) / 2, b.w, bh);
        bx += b.w + gap;
      }
      ry += barMaxH + 26;
      centered(rcHash, ry, mono(13), muted);

      // Save as PNG.
      const fname = `stabletrust-${opLabel.toLowerCase()}-receipt${data.txHash ? "-" + data.txHash.slice(2, 10) : ""}.png`;
      const trigger = (url) => {
        const a = document.createElement("a");
        a.href = url;
        a.download = fname;
        document.body.appendChild(a);
        a.click();
        a.remove();
      };
      if (canvas.toBlob) {
        canvas.toBlob((blob) => {
          if (!blob) return;
          const url = URL.createObjectURL(blob);
          trigger(url);
          setTimeout(() => URL.revokeObjectURL(url), 1000);
        }, "image/png");
      } else {
        trigger(canvas.toDataURL("image/png"));
      }
    };

    return (
      <div className="tx-receipt-overlay" onClick={() => setReceiptTab(null)}>
        <div
          className="tx-receipt-stack"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="tx-receipt-card">
            <button
              type="button"
              className="tx-receipt-close-icon"
              onClick={() => setReceiptTab(null)}
              aria-label="Close"
            >
              ✕
            </button>
            <div className="tx-receipt-perf" />
            <div className="tx-receipt-body">
              <div className="tx-receipt-brand">Stabletrust Pay</div>
              <div className="tx-receipt-kind">Confidential {opLabel}</div>
              <div className="tx-receipt-time">{rcTime}</div>

              <div className="tx-receipt-stars">{rcStars}</div>

              <div className="tx-receipt-amount">Encrypted</div>
              <div className="tx-receipt-token">
                {tokenSymbol} · Confidential
              </div>

              <div className="tx-receipt-stars">{rcStars}</div>

              <div className="tx-receipt-rows">
                {rcRows.map(([k, v]) => (
                  <div className="tx-receipt-row" key={k}>
                    <div className="tx-receipt-row-k">{k}</div>
                    <div className="tx-receipt-row-leader" />
                    <div className="tx-receipt-row-v">{v}</div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 16 }}>
                <div className="tx-receipt-to-label">To</div>
                <div className="tx-receipt-to-value">Encrypted</div>
              </div>

              <div className="tx-receipt-stars">{rcStars}</div>

              <div className="tx-receipt-verify-1">
                Amount encrypted onchain
              </div>
              <div className="tx-receipt-verify-2">
                Homomorphic encryption · Proof verified
              </div>

              <div className="tx-receipt-barcode">
                {rcBars.map((b, i) => (
                  <div
                    key={i}
                    className="tx-receipt-bar"
                    style={{ width: b.w, height: b.h }}
                  />
                ))}
              </div>
              <div className="tx-receipt-hash">{rcHash}</div>
            </div>
            <div className="tx-receipt-perf bottom" />
          </div>

          <div className="tx-receipt-actions">
            {txHashShort &&
              (explorerUrl ? (
                <a
                  href={explorerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tx-receipt-txhash"
                >
                  {txHashShort}
                </a>
              ) : (
                <span className="tx-receipt-txhash">{txHashShort}</span>
              ))}
            <button
              type="button"
              className="tx-receipt-share"
              onClick={shareToX}
            >
              {receiptShared ? "Opened" : "Share on X"}
            </button>
            <button
              type="button"
              className="tx-receipt-share tx-receipt-download"
              onClick={downloadReceipt}
            >
              Download
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Key Derivation Consent Modal */}
      <KeyDerivationConsentModal
        isOpen={showConsentModal}
        onConsent={handleKeyDerivationConsent}
        onCancel={handleKeyDerivationCancel}
        chainName={getChainDisplayName(selectedChainId)}
        accountExists={accountExists}
        isFirstTime={isFirstTimeConnection}
      />

      {/* Request-a-transfer link builder (shareable pre-filled transfer link) */}
      <TransferRequestModal
        open={showRequestModal}
        onClose={() => setShowRequestModal(false)}
        defaults={{
          to: userAccount, // request = "pay ME" → default to the creator's own address
          chainKey: selectedChainId,
          tokenId: selectedTokenId,
          amount: transferAmount,
        }}
      />

      {/* Pending Balance Notification */}
      {showPendingNotification && tokenData && (
        <div
          className="pending-notification-container"
          ref={pendingNotificationRef}
        >
          <div className="pending-notification">
            <div className="pending-notification-header">
              <div className="pending-notification-content">
                <div className="pending-notification-title">
                  <i className="fas fa-info-circle"></i>
                  <h3> Pending balance available</h3>
                </div>
                <div className="pending-notification-message">
                  You have{" "}
                  {pendingBalance !== null
                    ? `${formatTokenAmount(pendingBalance / 100, 2)}`
                    : "0"}{" "}
                  C{tokenData.symbol} ready to claim. Secure it to your
                  available balance now.
                </div>
              </div>
              <button
                className="pending-notification-close"
                onClick={() => {
                  setShowPendingNotification(false);
                  setPendingNotificationDismissed(true);
                }}
                aria-label="Dismiss notification"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <button
              className="pending-notification-button"
              onClick={handleApplyPending}
              disabled={isApplyingPending}
            >
              {isApplyingPending
                ? "Processing..."
                : !keysDerived || accountCreationFailed
                  ? "Sign"
                  : "Secure Claim"}
            </button>
          </div>
        </div>
      )}

      <div
        className="tabbed-operations"
        // Sheet (MobileShell.js) positions itself with position:absolute;
        // inset:0, expecting a positioned ancestor sized to the mobile
        // app's own viewport-height box to anchor to — MobileShell's own
        // root div provides that, but Sheet/SheetRow are rendered here as
        // a sibling of MobileShell (see the Token Selector block below),
        // not nested inside it, since MobileShell has no slot to inject
        // overlay content into. Without this, Sheet falls back to the
        // page's own initial containing block. App.js now hides its own
        // header/footer below the same 767px breakpoint (they used to
        // stack above/below MobileShell's 100dvh box, forcing scroll and
        // making this anchor's initial containing block taller than one
        // screen), but this stays regardless — it's what gives Sheet a
        // positioned ancestor sized to the visible screen. Inline, not a
        // stylesheet rule — doesn't touch stabletrust-editorial.css, and
        // is a no-op for desktop (position:relative with no offsets moves
        // nothing).
        style={{ position: "relative" }}
      >
        {/* The old .tabs/.tab-pill bar was a second, duplicate tab row —
            EditorialForm renders its own Tabs internally (used by every
            branch below, including status views and History via
            children), so this is the only tab bar now. Its CSS
            (.tabs/.tab-pill in both stylesheets) is dead weight; see the
            removal note further down. */}
        {/* mobile-shell-active (App.css) zeroes out .tab-content's legacy
            mobile padding — that padding was sized for the old
            desktop-tree-squeezed-onto-a-phone layout. MobileShell sizes
            itself to exactly 100dvh, so any padding on its direct parent
            adds on top of a box that's already one full screen tall,
            forcing a scroll to reach the bottom nav. */}
        <div className={`tab-content${isMobileViewport ? " mobile-shell-active" : ""}`}>
          {activeTab === "transfer" &&
            (transferStatus !== "idle" ? (
              isMobileViewport ? (
                renderStatusView(transferStatus, "Transfer", transferReceiptData, {
                  showReceipt: true,
                  actionLabel: "Make another transfer",
                  onAction: resetTransferStatus,
                })
              ) : (
                <EditorialForm activeTab={activeTab} onTab={setActiveTab}>
                  {renderStatusView(transferStatus, "Transfer", transferReceiptData, {
                    showReceipt: true,
                    actionLabel: "Make another transfer",
                    onAction: resetTransferStatus,
                  })}
                </EditorialForm>
              )
            ) : (
              renderTransferTab()
            ))}
          {activeTab === "deposit" &&
            (depositStatus !== "idle" ? (
              isMobileViewport ? (
                renderStatusView(depositStatus, "Deposit", depositReceiptData, {
                  showReceipt: false,
                  actionLabel: "Make transfer",
                  onAction: () => {
                    resetDepositStatus();
                    setActiveTab("transfer");
                  },
                })
              ) : (
                <EditorialForm activeTab={activeTab} onTab={setActiveTab}>
                  {renderStatusView(depositStatus, "Deposit", depositReceiptData, {
                    showReceipt: false,
                    actionLabel: "Make transfer",
                    onAction: () => {
                      resetDepositStatus();
                      setActiveTab("transfer");
                    },
                  })}
                </EditorialForm>
              )
            ) : (
              renderDepositTab()
            ))}
          {activeTab === "withdraw" &&
            (withdrawStatus !== "idle" ? (
              isMobileViewport ? (
                renderStatusView(withdrawStatus, "Withdraw", withdrawReceiptData, {
                  showReceipt: false,
                  actionLabel: "Deposit Tokens",
                  onAction: () => {
                    resetWithdrawStatus();
                    setActiveTab("deposit");
                  },
                })
              ) : (
                <EditorialForm activeTab={activeTab} onTab={setActiveTab}>
                  {renderStatusView(withdrawStatus, "Withdraw", withdrawReceiptData, {
                    showReceipt: false,
                    actionLabel: "Deposit Tokens",
                    onAction: () => {
                      resetWithdrawStatus();
                      setActiveTab("deposit");
                    },
                  })}
                </EditorialForm>
              )
            ) : (
              renderWithdrawTab()
            ))}
          {activeTab === "history" &&
            (isMobileViewport ? (
              // History's own mobile treatment (day grouping, 72px rows,
              // "Encrypted" amounts) is a later step — for now just keep
              // the header/bottom-nav shell consistent across all four
              // tabs instead of dropping to the unstyled desktop tree the
              // moment someone taps History.
              <MobileShell
                activeTab={activeTab}
                onTab={setActiveTab}
                connected={!!userAccount}
                address={truncatedAddress}
                onWallet={onConnect}
            fullAddress={userAccount}
            onDisconnect={onDisconnect}
            email={privyIdentityLabel}
            onSwitchAccount={onSwitchAccount}
            switchingAccount={switchingAccount}
              >
                <History
                  contract={contract}
                  userAccount={userAccount}
                  provider={provider}
                  keypair={keypair}
                  chainId={selectedChainId}
                />
              </MobileShell>
            ) : (
              <EditorialForm activeTab={activeTab} onTab={setActiveTab}>
                <History
                  contract={contract}
                  userAccount={userAccount}
                  provider={provider}
                  keypair={keypair}
                  chainId={selectedChainId}
                />
              </EditorialForm>
            ))}
        </div>

        {/* Key Input Modal */}
        {/* No key input modal needed: key is derived automatically */}

        {onboardingPhase === "open" && userAccount && (
          <Onboarding
            isMobile={isMobileViewport}
            address={userAccount}
            chainKey={selectedChainId}
            token={
              tokenData && isValidTokenAddress(tokenData.address)
                ? tokenData
                : null
            }
            onChangeNetwork={() => {
              setMobilePickerChainId(null);
              setShowTokenSelector(true);
            }}
            onSkip={skipOnboarding}
            accountPending={onboardingAccountState === "pending"}
            setupStage={onboardingSetupStage}
            setupError={onboardingSetupError}
            keyReady={!!keypair}
            onCreateAccount={startOnboardingSetup}
            onComplete={finishOnboarding}
          />
        )}

        {/* Token Selector — desktop shows chains + tokens side by side in
            one modal; mobile drills through two Sheets instead (chain
            list, then that chain's token list), since there's no room for
            two columns on a phone. Same handlers either way. */}
        {isMobileViewport ? (
          showTokenSelector &&
          (mobilePickerChainId ? (
            <Sheet
              title={CHAINS[mobilePickerChainId]?.name || "Select token"}
              actionLabel="Cancel"
              onAction={closeMobilePicker}
            >
              {Object.entries(CHAINS[mobilePickerChainId]?.tokens || {})
                .filter(
                  ([, token]) =>
                    isValidTokenAddress(token.address) || token.active === false,
                )
                .map(([tokenId, token]) => {
                  const enabled = isMobileTokenEnabled(
                    mobilePickerChainId,
                    token.symbol,
                  );
                  const showConfidential = tokenSelectorShowConfidential;
                  return (
                    <SheetRow
                      key={tokenId}
                      iconUrl={token.icon}
                      round
                      name={
                        showConfidential
                          ? toConfidentialSymbol(token.symbol, token.name)
                          : token.symbol
                      }
                      sub={
                        enabled
                          ? showConfidential
                            ? toConfidentialName(token.symbol, token.name)
                            : token.name
                          : "Coming soon"
                      }
                      selected={
                        selectedChainId === mobilePickerChainId &&
                        selectedTokenId === tokenId
                      }
                      disabled={!enabled}
                      onClick={() => {
                        const chainConfig = CHAINS[mobilePickerChainId];
                        handleTokenSelect({
                          ...token,
                          tokenId,
                          chainId: mobilePickerChainId,
                          chainName: chainConfig.name,
                          id: `${mobilePickerChainId}_${tokenId}`,
                          chainLogo: chainConfig.icon,
                          chainColor: chainConfig.color,
                        });
                        closeMobilePicker();
                      }}
                    />
                  );
                })}
            </Sheet>
          ) : (
            <Sheet
              title={tokenSelectorTitle}
              actionLabel="Cancel"
              onAction={closeMobilePicker}
            >
              {/* Testnet/Mainnet toggle — content I'm placing inside
                  Sheet's own children slot, not a change to MobileShell.js
                  itself (Sheet doesn't own or restyle what's put inside
                  it). A compact segmented control (one bordered track)
                  rather than two buttons stretched edge-to-edge — it's a
                  binary switch, not two equally-weighted actions. Same
                  control, same inline styling as the desktop
                  TokenSelector's own toggle, just with each segment tall
                  enough to clear the 44px tap-target minimum. */}
              <div style={{ padding: "18px 20px 16px" }}>
                <div
                  style={{
                    display: "inline-flex",
                    border: "1px solid #E6E6E1",
                    borderRadius: 999,
                    padding: 3,
                    gap: 2,
                  }}
                >
                  {["mainnet", "testnet"].map((mode) => {
                    const active = networkMode === mode;
                    return (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => handleNetworkModeChange(mode)}
                        style={{
                          minHeight: 44,
                          padding: "0 18px",
                          border: 0,
                          borderRadius: 999,
                          background: active ? "#16181A" : "transparent",
                          color: active ? "#FFFFFF" : "#8E8E88",
                          fontFamily:
                            "'JetBrains Mono', ui-monospace, Menlo, monospace",
                          fontSize: 11,
                          letterSpacing: "0.1em",
                          cursor: "pointer",
                        }}
                      >
                        {mode === "testnet" ? "Testnet" : "Mainnet"}
                      </button>
                    );
                  })}
                </div>
              </div>
              {getPopularChains(networkMode).map((chain) => {
                const chainEnabled = isChainEnabledForNetwork(chain.id, networkMode);
                return (
                <SheetRow
                  key={chain.id}
                  iconUrl={chain.icon}
                  round
                  name={chain.name}
                  sub={chainEnabled ? "" : "Coming soon"}
                  selected={selectedChainId === chain.id}
                  disabled={!chainEnabled}
                  onClick={() => {
                    handleChainSelect(chain.id);
                    setMobilePickerChainId(chain.id);
                  }}
                />
                );
              })}
            </Sheet>
          ))
        ) : (
          <TokenSelector
            isOpen={showTokenSelector}
            onClose={() => setShowTokenSelector(false)}
            onTokenSelect={handleTokenSelect}
            onChainSelect={handleChainSelect}
            selectedChainId={selectedChainId}
            selectedTokenId={selectedTokenId}
            title={tokenSelectorTitle}
            showConfidential={tokenSelectorShowConfidential}
            networkMode={networkMode}
            onNetworkModeChange={handleNetworkModeChange}
          />
        )}

        {/* Mobile transfer confirm — CTA sets showTransferConfirm; without
            this sheet the button appeared to do nothing (no Privy prompt). */}
        {isMobileViewport &&
          showTransferConfirm &&
          activeTab === "transfer" &&
          transferStatus === "idle" && (
            <ConfirmSheet
              amount={transferAmount || "0"}
              token={
                tokenData
                  ? `${toConfidentialSymbol(tokenData.symbol, tokenData.name)}`
                  : "CONFIDENTIAL"
              }
              rows={[
                ["To", recipient.trim() || "—"],
                ["Network", tokenData?.chainName || "—"],
                ["Rate", "1 : 1"],
                ["Protocol fee", "$0.00"],
              ]}
              onCancel={() => setShowTransferConfirm(false)}
              onConfirm={() => {
                setShowTransferConfirm(false);
                handleTransfer();
              }}
            />
          )}
      </div>
      {renderReceiptModal()}
    </>
  );
};

export default TabbedOperations;
